import { Icon } from '@iconify/react';
import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Spinner,
  Card,
  CardBody,
  Input,
  Tabs,
  Tab,
  Chip,
  Textarea,
  Autocomplete,
  AutocompleteItem,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  BoxFormatMapping,
  ExporterReceiver,
  Market,
  ShipTrip,
} from 'common/src/utils/types';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import {
  getExporterDivisions,
  type ExporterDivision,
} from 'common/src/api/exporter-divisions';
import {
  getFruticBoxToFormatMapping,
  saveFruticBoxToFormatMapping,
} from 'common/src/api/frutic';
import { getPrimaryMarkets } from 'common/src/api/markets';
import {
  findOrCreateAirShipTrip,
  getShipTrips,
} from 'common/src/api/ship_trips';
import { SEASONS, VALID_FORMATS } from 'common/src/utils/definitions';
import { supabase } from 'common/src/supabase';
import { SettlementDetail } from 'common/src/api/excel-process';
import {
  GroupReviewStatus,
  PdfGroupCostLine,
  PdfGroupSalesLine,
  PdfSettlementGroup,
  processGroupedPdfFile,
  SettlementCostCategory,
} from 'common/src/api/pdf-process';
import {
  getReceiverCostMappings,
  normalizeReceiverCostConcept,
  ReceiverCostMapping,
  supersedeReceiverCostMapping,
} from 'common/src/api/receiver-costs';
import {
  ExporterCostCategory,
  getCustomCostCategoriesEnabled,
  getDefaultExporterCostCategory,
  getExporterCostCategories,
  getReceiverCostExporterMappings,
  setReceiverCostExporterCategories,
  UNCLASSIFIED_EXPORTER_COST_LABEL,
} from 'common/src/api/exporter-cost-categories';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useDataStore from '../../../../common/src/stores/useDataStore';
import useFilterStore from '../../../../common/src/stores/useFiltersStore';
import useShipmentsStore, {
  ShipmentRecord,
} from '../../store/useShipmentsStore';
import CustomAutocomplete from '../common/CustomAutocomplete';
import TransportTypeSelector from '../common/TransportTypeSelector';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { Alert, useConfirm } from '../ui';
import { uploadOriginalSettlementDocument } from '../../services/settlementDocumentService';
import { type InboxClaim, inboxRequest } from 'common/src/api/settlement-inbox';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type SelectionMode = 'ship_group' | 'manual';
type ModalStep = 'setup' | 'groups' | 'review' | 'settlements';
type PdfGroupProcessStatus = 'pending' | 'processing' | 'success' | 'error';
type TransportType = 'A' | 'M' | 'T';

type GroupMapping = {
  shipmentKeys: string[];
  varietyFilter: string;
  caliberFilter: string;
  colorFilter: string;
  notes: string;
  scope: ShipmentBoxScope;
  detailScopes: Record<string, DetailScope>;
  qualitySplits: Record<string, QualitySplit[]>;
  confirmedLineAssignments: Record<string, boolean>;
  shipmentBoxQuantitiesByLine: Record<string, Record<string, number>>;
  manuallyIncludedBoxIds: string[];
  manuallyExcludedBoxIds: string[];
  manualBoxQuantities: Record<string, number>;
};

type GroupAssignmentLock = {
  shipmentBoxIdsByLine: Record<string, string[]>;
  shipmentBoxQuantitiesByLine: Record<string, Record<string, number>>;
  assignedAt: string;
};

type BoxTransfer = {
  id: string;
  shipmentBoxId: string;
  fromGroupKey: string;
  toGroupKey: string;
  quantity: number;
  sourceSalesLineId?: string;
};

type ManualBoxAdjustmentSnapshot = {
  hadManualQuantity: boolean;
  manualQuantity: number;
  wasIncluded: boolean;
  wasExcluded: boolean;
};

type ShipmentBoxScope = {
  speciesIds: string[];
  shipNames: string[];
  referenceNumbers: string[];
  containerNumbers: string[];
  varieties: string[];
  labeledVarieties: string[];
  calibers: string[];
  colors: string[];
  categories: string[];
  labels: string[];
  formats: string[];
  lots: string[];
  pallets: string[];
};

type DetailScope = {
  scope: ShipmentBoxScope;
  activeFields: Array<keyof ShipmentBoxScope>;
};

type QualitySplit = {
  id: string;
  variety: string;
  caliber: string;
  color: string;
  category: string;
  label: string;
  format: string;
  quantity: number;
  kilos: number;
  amount: number;
  references: string[];
};

type DerivedSettlementDetail = {
  id: string;
  referenceNumber: string;
  containerNumber: string;
  shipName: string;
  shipEtd: string;
  shipEta: string;
  variety: string;
  caliber: string;
  color: string;
  category: string;
  label: string;
  format: string;
  unitWeight: number;
  quantity: number;
  kilos: number;
  sales: number;
  tax: number;
  commission: number;
  freight: number;
  repacking: number;
  otherCosts: number;
  sourceGroupIndex: number;
  sourceLineId: string;
  shipmentBoxes: ShipmentRecord[];
};

type DerivedSettlement = {
  referenceNumber: string;
  containerNumber: string;
  shipName: string;
  shipEtd: string;
  shipEta: string;
  details: DerivedSettlementDetail[];
};

type SettlementHeaderConfig = {
  transportType: TransportType | '';
  shipTripId: string;
  shipEtd: string;
  shipEta: string;
  destinationMarketId: string;
  marketId: string;
  comparableMarketId: string;
  dateSold: string;
  region: string;
  exchangeRate: string;
  currency: string;
  businessType: string;
  saleOrder: string;
  originalIncoterm: string;
};

type ScopeSuggestion = {
  field: keyof ShipmentBoxScope;
  label: string;
  values: string[];
  boxes: number;
  difference: number;
};

type PdfDocumentGroup = {
  id: string;
  name: string;
  pages: number[];
};

type PdfPageThumbnail = {
  pageNumber: number;
  dataUrl: string;
  width: number;
  height: number;
};

type PdfGroupProcessState = {
  status: PdfGroupProcessStatus;
  error?: string;
};

const EMPTY_GROUP_MAPPING: GroupMapping = {
  shipmentKeys: [],
  varietyFilter: '',
  caliberFilter: '',
  colorFilter: '',
  notes: '',
  scope: {
    speciesIds: [],
    shipNames: [],
    referenceNumbers: [],
    containerNumbers: [],
    varieties: [],
    labeledVarieties: [],
    calibers: [],
    colors: [],
    categories: [],
    labels: [],
    formats: [],
    lots: [],
    pallets: [],
  },
  detailScopes: {},
  qualitySplits: {},
  confirmedLineAssignments: {},
  shipmentBoxQuantitiesByLine: {},
  manuallyIncludedBoxIds: [],
  manuallyExcludedBoxIds: [],
  manualBoxQuantities: {},
};

const COST_CATEGORY_OPTIONS: SettlementCostCategory[] = [
  'commission',
  'tax',
  'freight',
  'repacking',
  'other_costs',
];

const COST_CATEGORY_LABELS: Record<SettlementCostCategory, string> = {
  commission: 'Comisión',
  tax: 'Impuestos',
  freight: 'Flete',
  repacking: 'Repacking',
  other_costs: 'Otros costos',
};

const REVIEW_STATUS_OPTIONS: GroupReviewStatus[] = [
  'extracted',
  'mapped',
  'reviewed',
  'ready_to_apply',
];

const REVIEW_STATUS_LABELS: Record<GroupReviewStatus, string> = {
  extracted: 'Extraído',
  mapped: 'Mapeado',
  reviewed: 'Revisado',
  ready_to_apply: 'Listo para aplicar',
};

type ShipGroup = {
  key: string;
  ship_name: string;
  ship_etd: string;
  ship_eta: string;
  ship_destination_port: string;
  boxes: ShipmentRecord[];
  references: string[];
  containers: string[];
  kilos: number;
};

const normalizeText = (value?: string | number | null) =>
  String(value || '').trim();

const normalizeLookup = (value?: string | number | null) =>
  normalizeText(value)
    .replace(/[-/\s]+/g, '')
    .toUpperCase();

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message.trim()) return error.message;
  if (typeof error === 'string' && error.trim()) return error;
  if (error && typeof error === 'object') {
    const apiError = error as { message?: unknown; error?: unknown };
    if (typeof apiError.message === 'string' && apiError.message.trim()) {
      return apiError.message;
    }
    if (typeof apiError.error === 'string' && apiError.error.trim()) {
      return apiError.error;
    }
  }
  return fallback;
};

const getMappedShipmentPackageType = (
  box: ShipmentRecord,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const boxType = normalizeText(box.box_type);
  if (!boxType) return '';

  const mappedFormat = mappedFormats.find(
    (mapping) =>
      normalizeLookup(mapping.frutic_code) === normalizeLookup(boxType)
  )?.cherry_box_format;
  const normalizedFormat = normalizeText(mappedFormat).toUpperCase();

  return normalizedFormat && normalizedFormat !== 'NA' ? normalizedFormat : '';
};

const getShipmentPackageType = (
  box: ShipmentRecord,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const mappedFormat = getMappedShipmentPackageType(box, mappedFormats);
  if (mappedFormat) return mappedFormat;

  // A present box_type must be mapped explicitly. Only use the stored format
  // as a fallback for legacy shipment boxes that do not have a box_type.
  if (normalizeText(box.box_type)) return '';

  return normalizeText(box.box_format)
    .replace(/[-/]\s*\d+(?:[.,]\d+)?\s*KG$/i, '')
    .trim();
};

const getShipmentFormat = (
  box: ShipmentRecord,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const boxType = normalizeText(box.box_type);
  const mappedBoxFormat = getMappedShipmentPackageType(box, mappedFormats);
  const storedFormat = normalizeText(box.complete_format)
    .replace(/^(null|undefined|n\/a)\s*[-/]\s*/i, '')
    .trim();
  const packageType = normalizeText(
    mappedBoxFormat || box.box_format || boxType
  )
    .replace(/^(null|undefined|n\/a)$/i, '')
    .trim();
  const weight = Number(box.box_weight) || 0;
  const weightLabel = weight ? `${String(weight).replace(/\.0+$/, '')}KG` : '';

  if (packageType && storedFormat.startsWith(`${packageType}-`)) {
    return storedFormat;
  }
  if (packageType && weightLabel) return `${packageType}-${weightLabel}`;
  if (packageType && storedFormat) return `${packageType}-${storedFormat}`;
  return storedFormat || weightLabel;
};

const getSettlementDetailVariety = (box: ShipmentRecord) =>
  normalizeText(box.labeled_variety) || normalizeText(box.variety);

const getShipmentBoxAdjustmentLabel = (
  box: ShipmentRecord,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const quality = [
    getSettlementDetailVariety(box),
    box.caliber,
    box.color,
    box.category,
    box.box_label,
    getShipmentFormat(box, mappedFormats),
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(' · ');
  const traceability = [
    box.reference_number,
    box.container_number,
    box.lot ? `Lote ${box.lot}` : '',
    box.pallet ? `Pallet ${box.pallet}` : '',
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(' · ');

  return `${formatNumber(Number(box.quantity) || 0)} bultos — ${traceability}${quality ? ` — ${quality}` : ''}`;
};

const sortShipmentBoxesByAdjustmentLabel = (
  boxes: ShipmentRecord[],
  mappedFormats: BoxFormatMapping[]
) =>
  boxes
    .map((box) => ({
      box,
      label: getShipmentBoxAdjustmentLabel(box, mappedFormats),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'))
    .map(({ box }) => box);

const getShipmentScopeValue = (
  box: ShipmentRecord,
  field: keyof ShipmentBoxScope,
  mappedFormats: BoxFormatMapping[]
) => {
  const values: Record<
    keyof ShipmentBoxScope,
    string | number | null | undefined
  > = {
    speciesIds: box.species_id,
    shipNames: box.ship_name,
    referenceNumbers: box.reference_number,
    containerNumbers: box.container_number,
    varieties: box.variety,
    labeledVarieties: box.labeled_variety,
    calibers: box.caliber,
    colors: box.color,
    categories: box.category,
    labels: box.box_label,
    formats: getShipmentFormat(box, mappedFormats),
    lots: box.lot,
    pallets: box.pallet,
  };
  return normalizeText(values[field]);
};

const findClosestScopeSubset = (
  totals: Array<{ value: string; boxes: number }>,
  target: number
) => {
  const roundedTarget = Math.max(0, Math.round(target));
  const combinations = new Map<number, string[]>([[0, []]]);

  totals.forEach(({ value, boxes }) => {
    const quantity = Math.max(0, Math.round(boxes));
    if (!quantity) return;
    Array.from(combinations.entries())
      .sort(([a], [b]) => b - a)
      .forEach(([sum, values]) => {
        const nextSum = sum + quantity;
        if (nextSum > roundedTarget || combinations.has(nextSum)) return;
        combinations.set(nextSum, [...values, value]);
      });
  });

  const bestSum = Math.max(...combinations.keys());
  return { values: combinations.get(bestSum) || [], boxes: bestSum };
};

const getScopeSuggestions = (
  boxes: ShipmentRecord[],
  scope: ShipmentBoxScope,
  target: number,
  mappedFormats: BoxFormatMapping[],
  fields: Array<{ field: keyof ShipmentBoxScope; label: string }>
): ScopeSuggestion[] => {
  const suggestions = fields.flatMap(({ field, label }) => {
    const scopeWithoutField = { ...scope, [field]: [] } as ShipmentBoxScope;
    const totals = new Map<string, number>();
    boxes
      .filter((box) =>
        matchesShipmentBoxScope(box, scopeWithoutField, mappedFormats)
      )
      .forEach((box) => {
        const value = getShipmentScopeValue(box, field, mappedFormats);
        if (!value) return;
        totals.set(
          value,
          (totals.get(value) || 0) + (Number(box.quantity) || 0)
        );
      });

    const options = Array.from(totals, ([value, boxTotal]) => ({
      value,
      boxes: boxTotal,
    })).sort((a, b) => b.boxes - a.boxes);
    if (!options.length) return [];

    const closest = findClosestScopeSubset(options, target);
    const candidates = closest.values.length
      ? [closest]
      : options
          .slice()
          .sort(
            (a, b) => Math.abs(a.boxes - target) - Math.abs(b.boxes - target)
          )
          .slice(0, 1)
          .map((option) => ({
            values: [option.value],
            boxes: option.boxes,
          }));

    return candidates
      .filter(({ values }) => {
        const current = scope[field].slice().sort();
        const proposed = values.slice().sort();
        return current.join('|') !== proposed.join('|');
      })
      .map(({ values, boxes: suggestedBoxes }) => ({
        field,
        label,
        values,
        boxes: suggestedBoxes,
        difference: suggestedBoxes - target,
      }));
  });

  return suggestions
    .sort(
      (a, b) =>
        Math.abs(a.difference) - Math.abs(b.difference) ||
        a.values.length - b.values.length ||
        a.label.localeCompare(b.label, 'es')
    )
    .slice(0, 6);
};

const uniqueValues = (values: Array<string | number | null | undefined>) =>
  Array.from(
    new Set(values.map((value) => normalizeText(value)).filter(Boolean))
  );

const emptyShipmentBoxScope = (): ShipmentBoxScope => ({
  speciesIds: [],
  shipNames: [],
  referenceNumbers: [],
  containerNumbers: [],
  varieties: [],
  labeledVarieties: [],
  calibers: [],
  colors: [],
  categories: [],
  labels: [],
  formats: [],
  lots: [],
  pallets: [],
});

const scopeHasValues = (scope: ShipmentBoxScope) =>
  Object.values(scope).some((values) => values.length > 0);

const matchesScopeValues = (
  candidate: string | number | null | undefined,
  values: string[]
) => {
  if (values.length === 0) return true;
  const normalizedCandidate = normalizeLookup(candidate);
  return values.some((value) => normalizeLookup(value) === normalizedCandidate);
};

const matchesShipmentBoxScope = (
  box: ShipmentRecord,
  scope: ShipmentBoxScope,
  mappedFormats: BoxFormatMapping[] = []
) =>
  matchesScopeValues(box.species_id, scope.speciesIds) &&
  matchesScopeValues(box.ship_name, scope.shipNames) &&
  matchesScopeValues(box.reference_number, scope.referenceNumbers) &&
  matchesScopeValues(box.container_number, scope.containerNumbers) &&
  matchesScopeValues(box.variety, scope.varieties) &&
  matchesScopeValues(box.labeled_variety, scope.labeledVarieties) &&
  matchesScopeValues(box.caliber, scope.calibers) &&
  matchesScopeValues(box.color, scope.colors) &&
  matchesScopeValues(box.category, scope.categories) &&
  matchesScopeValues(box.box_label, scope.labels) &&
  (scope.formats.length === 0 ||
    matchesScopeValues(getShipmentFormat(box, mappedFormats), scope.formats)) &&
  matchesScopeValues(box.lot, scope.lots) &&
  matchesScopeValues(box.pallet, scope.pallets);

const getManuallyAdjustedGroupBoxes = (
  boxes: ShipmentRecord[],
  mapping: GroupMapping,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const includedIds = new Set(mapping.manuallyIncludedBoxIds || []);
  const excludedIds = new Set(mapping.manuallyExcludedBoxIds || []);
  const manualQuantities = mapping.manualBoxQuantities || {};
  const selectedBoxes = new Map<string, ShipmentRecord>();

  boxes.forEach((box) => {
    const boxId = String(box.id);
    if (Object.prototype.hasOwnProperty.call(manualQuantities, boxId)) {
      const quantity = Math.min(
        Math.max(Number(manualQuantities[boxId]) || 0, 0),
        Number(box.quantity) || 0
      );
      if (quantity > 0) {
        selectedBoxes.set(boxId, { ...box, quantity });
      }
      return;
    }
    if (
      !excludedIds.has(boxId) &&
      (includedIds.has(boxId) ||
        matchesShipmentBoxScope(box, mapping.scope, mappedFormats))
    ) {
      selectedBoxes.set(boxId, box);
    }
  });

  return Array.from(selectedBoxes.values());
};

type GroupLineAllocation = {
  boxesByLine: Record<string, ShipmentRecord[]>;
  matchedLineIds: Set<string>;
  availableBoxesByLine: Record<string, ShipmentRecord[]>;
  scopedBoxes: ShipmentRecord[];
};

type LineScopeOptions = Pick<
  ShipmentBoxScope,
  | 'referenceNumbers'
  | 'varieties'
  | 'labeledVarieties'
  | 'calibers'
  | 'colors'
  | 'categories'
  | 'labels'
  | 'formats'
>;

const buildLineScopeOptions = (
  boxes: ShipmentRecord[],
  mappedFormats: BoxFormatMapping[]
): LineScopeOptions => {
  const values = {
    referenceNumbers: new Set<string>(),
    varieties: new Set<string>(),
    labeledVarieties: new Set<string>(),
    calibers: new Set<string>(),
    colors: new Set<string>(),
    categories: new Set<string>(),
    labels: new Set<string>(),
    formats: new Set<string>(),
  };
  const add = (target: Set<string>, value?: string | number | null) => {
    const normalized = normalizeText(value);
    if (normalized) target.add(normalized);
  };

  boxes.forEach((box) => {
    add(values.referenceNumbers, box.reference_number);
    add(values.varieties, box.variety);
    add(values.labeledVarieties, box.labeled_variety);
    add(values.calibers, box.caliber);
    add(values.colors, box.color);
    add(values.categories, box.category);
    add(values.labels, box.box_label);
    add(values.formats, getShipmentFormat(box, mappedFormats));
  });

  return Object.fromEntries(
    Object.entries(values).map(([field, fieldValues]) => [
      field,
      Array.from(fieldValues).sort((a, b) => a.localeCompare(b, 'es')),
    ])
  ) as LineScopeOptions;
};

const EMPTY_LINE_SCOPE_OPTIONS: LineScopeOptions = {
  referenceNumbers: [],
  varieties: [],
  labeledVarieties: [],
  calibers: [],
  colors: [],
  categories: [],
  labels: [],
  formats: [],
};

const getGroupLineAllocation = (
  boxes: ShipmentRecord[],
  group: PdfSettlementGroup,
  mapping: GroupMapping,
  mappedFormats: BoxFormatMapping[] = [],
  precomputedScopedBoxes?: ShipmentRecord[]
): GroupLineAllocation => {
  const groupBoxes =
    precomputedScopedBoxes ||
    getManuallyAdjustedGroupBoxes(boxes, mapping, mappedFormats);
  const remainingQuantities = new Map(
    groupBoxes.map((box) => [String(box.id), Number(box.quantity) || 0])
  );
  const matchedBoxesByLine = new Map<string, ShipmentRecord[]>();
  const originalLineIndex = new Map(
    group.salesLines.map((line, index) => [line.id, index])
  );
  const filterSpecificity = (line: PdfGroupSalesLine) => {
    const detailScope = mapping.detailScopes[line.id];
    if (!detailScope) return 0;
    return Object.values(detailScope.scope).reduce(
      (total, values) => total + (values.length > 0 ? 1 : 0),
      0
    );
  };
  const orderedLines = [...group.salesLines].sort(
    (a, b) =>
      filterSpecificity(b) - filterSpecificity(a) ||
      (originalLineIndex.get(a.id) || 0) - (originalLineIndex.get(b.id) || 0)
  );
  const getLineDetailScope = (lineId: string) =>
    mapping.detailScopes[lineId] || {
      scope: emptyShipmentBoxScope(),
      activeFields: [],
    };
  const eligibleBoxesByLine = new Map<string, ShipmentRecord[]>();
  const getEligibleLineBoxes = (line: PdfGroupSalesLine) => {
    const cachedBoxes = eligibleBoxesByLine.get(line.id);
    if (cachedBoxes) return cachedBoxes;
    const detailScope = getLineDetailScope(line.id);
    const eligibleBoxes = groupBoxes.filter((box) =>
      matchesShipmentBoxScope(box, detailScope.scope, mappedFormats)
    );
    eligibleBoxesByLine.set(line.id, eligibleBoxes);
    return eligibleBoxes;
  };
  const getRemainingLineBoxes = (line: PdfGroupSalesLine) => {
    return getEligibleLineBoxes(line).flatMap((box) => {
      const remainingQuantity = remainingQuantities.get(String(box.id)) || 0;
      if (remainingQuantity <= 0) return [];
      return [
        remainingQuantity === (Number(box.quantity) || 0)
          ? box
          : { ...box, quantity: remainingQuantity },
      ];
    });
  };

  // Apply explicit row reservations before exact automatic matches. This lets
  // one sales row consume only the quantity it needs while leaving the
  // remainder available to other rows in the same group.
  const reservedLineIds = new Set(
    Object.keys(mapping.shipmentBoxQuantitiesByLine || {})
  );
  const groupBoxesById =
    reservedLineIds.size > 0
      ? new Map(groupBoxes.map((box) => [String(box.id), box]))
      : null;
  orderedLines.forEach((line) => {
    if (!groupBoxesById || !reservedLineIds.has(line.id)) return;
    const reservedQuantities = mapping.shipmentBoxQuantitiesByLine[line.id];

    const eligibleBoxIds = new Set(
      getEligibleLineBoxes(line).map((box) => String(box.id))
    );
    const reservedBoxes = Object.entries(reservedQuantities).flatMap(
      ([boxId, reservedQuantity]) => {
        const box = groupBoxesById.get(boxId);
        if (!box) return [];
        const requestedQuantity = Number(reservedQuantity) || 0;
        const remainingQuantity = remainingQuantities.get(boxId) || 0;
        if (
          requestedQuantity <= 0 ||
          remainingQuantity <= 0 ||
          !eligibleBoxIds.has(boxId)
        ) {
          return [];
        }

        const quantity = Math.min(requestedQuantity, remainingQuantity);
        return [{ ...box, quantity }];
      }
    );
    const reservedQuantity = reservedBoxes.reduce(
      (total, box) => total + (Number(box.quantity) || 0),
      0
    );
    if (reservedQuantity !== Number(line.quantity || 0)) return;

    matchedBoxesByLine.set(line.id, reservedBoxes);
    reservedBoxes.forEach((box) => {
      const boxId = String(box.id);
      remainingQuantities.set(
        boxId,
        Math.max(
          0,
          (remainingQuantities.get(boxId) || 0) - (Number(box.quantity) || 0)
        )
      );
    });
  });

  let addedMatch = true;
  while (addedMatch) {
    addedMatch = false;
    orderedLines.forEach((line) => {
      if (matchedBoxesByLine.has(line.id)) return;
      const lineBoxes = getRemainingLineBoxes(line);
      const quantity = lineBoxes.reduce(
        (total, box) => total + (Number(box.quantity) || 0),
        0
      );
      if (quantity <= 0 || quantity !== Number(line.quantity || 0)) return;

      matchedBoxesByLine.set(line.id, lineBoxes);
      lineBoxes.forEach((box) => {
        const boxId = String(box.id);
        remainingQuantities.set(
          boxId,
          Math.max(
            0,
            (remainingQuantities.get(boxId) || 0) - (Number(box.quantity) || 0)
          )
        );
      });
      addedMatch = true;
    });
  }

  const remainingBoxes = groupBoxes.flatMap((box) => {
    const quantity = remainingQuantities.get(String(box.id)) || 0;
    if (quantity <= 0) return [];
    return [
      quantity === (Number(box.quantity) || 0) ? box : { ...box, quantity },
    ];
  });
  const boxesByLine: Record<string, ShipmentRecord[]> = {};
  const availableBoxesByLine: Record<string, ShipmentRecord[]> = {};
  group.salesLines.forEach((line) => {
    const matchedBoxes = matchedBoxesByLine.get(line.id);
    if (matchedBoxes) {
      boxesByLine[line.id] = matchedBoxes;
      availableBoxesByLine[line.id] = [...matchedBoxes, ...remainingBoxes];
      return;
    }
    boxesByLine[line.id] = getRemainingLineBoxes(line);
    availableBoxesByLine[line.id] = remainingBoxes;
  });

  return {
    boxesByLine,
    matchedLineIds: new Set(matchedBoxesByLine.keys()),
    availableBoxesByLine,
    scopedBoxes: groupBoxes,
  };
};

const getShipmentScopeFieldValues = (
  boxes: ShipmentRecord[],
  scope: ShipmentBoxScope,
  field: keyof ShipmentBoxScope,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const scopeWithoutField = { ...scope, [field]: [] } as ShipmentBoxScope;
  const matchingBoxes = boxes.filter((box) =>
    matchesShipmentBoxScope(box, scopeWithoutField, mappedFormats)
  );
  const valuesByField: Record<
    keyof ShipmentBoxScope,
    Array<string | number | null | undefined>
  > = {
    speciesIds: matchingBoxes.map((box) => box.species_id),
    shipNames: matchingBoxes.map((box) => box.ship_name),
    referenceNumbers: matchingBoxes.map((box) => box.reference_number),
    containerNumbers: matchingBoxes.map((box) => box.container_number),
    varieties: matchingBoxes.map((box) => box.variety),
    labeledVarieties: matchingBoxes.map((box) => box.labeled_variety),
    calibers: matchingBoxes.map((box) => box.caliber),
    colors: matchingBoxes.map((box) => box.color),
    categories: matchingBoxes.map((box) => box.category),
    labels: matchingBoxes.map((box) => box.box_label),
    formats: matchingBoxes.map((box) => getShipmentFormat(box, mappedFormats)),
    lots: matchingBoxes.map((box) => box.lot),
    pallets: matchingBoxes.map((box) => box.pallet),
  };

  return uniqueValues(valuesByField[field]);
};

const PRIMARY_SCOPE_FIELDS: Array<keyof ShipmentBoxScope> = [
  'speciesIds',
  'shipNames',
  'referenceNumbers',
  'containerNumbers',
];

const getCascadingScopeFieldValues = (
  boxes: ShipmentRecord[],
  scope: ShipmentBoxScope,
  field: keyof ShipmentBoxScope,
  mappedFormats: BoxFormatMapping[] = []
) => {
  const primaryScope = emptyShipmentBoxScope();
  PRIMARY_SCOPE_FIELDS.forEach((primaryField) => {
    primaryScope[primaryField] = scope[primaryField];
  });

  return getShipmentScopeFieldValues(boxes, primaryScope, field, mappedFormats);
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-CL');
};

const getShipTripLabel = (trip: ShipTrip) =>
  `${trip.ship?.name || '-'} · ${formatDate(trip.etd)} → ${formatDate(
    trip.eta
  )} · ${trip.market?.name || '-'}`;

const getMarketLabel = (market: Market) => `${market.name}, ${market.country}`;

const toSettlementDate = (value?: string | null) => {
  const parsed = value ? new Date(value) : new Date(NaN);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();

  const parts = normalizeText(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (parts) {
    const [, month, day, rawYear] = parts;
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return new Date(
      `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T12:00:00Z`
    ).toISOString();
  }
  return new Date().toISOString();
};

const toDateInputValue = (value?: string | null) => {
  const normalized = normalizeText(value);
  if (!normalized) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(normalized)) {
    return normalized.slice(0, 10);
  }
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime())
    ? ''
    : parsed.toISOString().slice(0, 10);
};

const fileTypeLabel = (file: File | null) => {
  if (!file) return '';
  const name = file.name.toLowerCase();
  if (name.endsWith('.pdf')) return 'PDF';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'Excel';
  return file.type || 'Archivo';
};

const formatNumber = (value: number, decimals = 0) =>
  value.toLocaleString('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatMoney = (value: number, currency?: string, decimals = 0) =>
  `${currency || ''} ${formatNumber(value, decimals)}`.trim();

const normalizeCostCurrency = (value?: string | null) => {
  const normalized = normalizeText(value).toUpperCase();
  return normalized === 'CNY' ? 'RMB' : normalized;
};

const convertDerivedDetailsToLocalCurrency = (
  details: DerivedSettlementDetail[],
  groups: PdfSettlementGroup[],
  localCurrency: string,
  exchangeRate: number
) =>
  details.map((detail) => {
    const sourceGroup = groups[detail.sourceGroupIndex];
    const toLocalFactor = (category: SettlementCostCategory) => {
      const categoryLines =
        sourceGroup?.costLines.filter(
          (line) => line.mappedCategory === category
        ) || [];
      const rawTotal = categoryLines.reduce(
        (sum, line) => sum + (Number(line.amount) || 0),
        0
      );
      if (!rawTotal) return 1;
      const localTotal = categoryLines.reduce((sum, line) => {
        const amount = Number(line.amount) || 0;
        return (
          sum +
          (normalizeCostCurrency(line.currency) === 'USD' &&
          localCurrency !== 'USD'
            ? amount * exchangeRate
            : amount)
        );
      }, 0);
      return localTotal / rawTotal;
    };

    return {
      ...detail,
      tax: detail.tax * toLocalFactor('tax'),
      commission: detail.commission * toLocalFactor('commission'),
      otherCosts: detail.otherCosts * toLocalFactor('other_costs'),
      repacking: detail.repacking * toLocalFactor('repacking'),
      // The multi-settlement RPC accepts freight in local currency, then
      // stores it canonically as USD after dividing by the exchange rate.
      freight: detail.freight * toLocalFactor('freight'),
    };
  });

const getDerivedSettlementFinancials = (details: DerivedSettlementDetail[]) => {
  const totals = details.reduce(
    (sum, detail) => ({
      income: sum.income + detail.sales,
      costs:
        sum.costs +
        detail.tax +
        detail.commission +
        detail.freight +
        detail.repacking +
        detail.otherCosts,
    }),
    { income: 0, costs: 0 }
  );

  return { ...totals, net: totals.income - totals.costs };
};

const createLocalId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatPageRange = (group: PdfDocumentGroup) =>
  group.pages.length === 1
    ? `P${group.pages[0]}`
    : `P${group.pages.join(', ')}`;

const wait = (milliseconds: number) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
};

const getGroupKey = (group: PdfSettlementGroup, index: number) =>
  `${group.groupId || group.accountNumber || 'group'}-${index}`;

const getBoxAdjustmentSnapshot = (
  mapping: GroupMapping,
  boxId: string
): ManualBoxAdjustmentSnapshot => ({
  hadManualQuantity: Object.prototype.hasOwnProperty.call(
    mapping.manualBoxQuantities,
    boxId
  ),
  manualQuantity: Number(mapping.manualBoxQuantities[boxId]) || 0,
  wasIncluded: mapping.manuallyIncludedBoxIds.includes(boxId),
  wasExcluded: mapping.manuallyExcludedBoxIds.includes(boxId),
});

const getBoxTransferBaselineKey = (groupKey: string, boxId: string) =>
  `${groupKey}:${boxId}`;

const getStatusColor = (status: GroupReviewStatus) => {
  if (status === 'ready_to_apply') return 'success';
  if (status === 'reviewed') return 'primary';
  if (status === 'mapped') return 'secondary';
  return 'default';
};

const getProcessStatusColor = (
  status: PdfGroupProcessStatus | undefined,
  reviewStatus?: GroupReviewStatus
) => {
  if (reviewStatus === 'reviewed') return 'primary';
  if (status === 'error') return 'danger';
  if (status === 'processing') return 'warning';
  if (status === 'success') return 'success';
  return 'default';
};

const getProcessStatusLabel = (
  status: PdfGroupProcessStatus | undefined,
  reviewStatus?: GroupReviewStatus
) => {
  if (reviewStatus === 'reviewed') return 'Revisado';
  if (status === 'error') return 'Error';
  if (status === 'processing') return 'Leyendo';
  if (status === 'success') return 'Leído';
  return 'En cola';
};

const createPendingSettlementGroup = (
  group: PdfDocumentGroup
): PdfSettlementGroup => ({
  groupId: group.id,
  accountNumber: '',
  title: group.name,
  pages: group.pages,
  status: 'extracted',
  header: {
    shipName: '',
    voyage: '',
    dateRangeStart: '',
    dateRangeEnd: '',
    clientLot: '',
    brand: '',
    productDescription: '',
    species: '',
    variety: '',
    receiver: '',
    market: '',
    currency: 'USD',
    cases: 0,
    kilos: 0,
  },
  salesLines: [],
  rawSalesLines: [],
  costLines: [],
  totals: {
    cases: 0,
    kilos: 0,
    grossSales: 0,
    totalExpenses: 0,
    netProceeds: 0,
  },
  extractedReferences: [],
  extractedContainers: [],
  warnings: [],
});

const combineExtractedGroups = (
  sourceGroup: PdfDocumentGroup,
  extractedGroups: PdfSettlementGroup[]
): PdfSettlementGroup => {
  if (extractedGroups.length === 0) {
    return {
      ...createPendingSettlementGroup(sourceGroup),
      warnings: ['No se detectaron datos para este grupo.'],
    };
  }

  const [firstGroup] = extractedGroups;
  const currency = firstGroup.header.currency || 'USD';

  return {
    ...firstGroup,
    groupId: sourceGroup.id,
    accountNumber: uniqueValues(
      extractedGroups.map((group) => group.accountNumber)
    ).join(' / '),
    title: sourceGroup.name,
    pages: Array.from(
      new Set(
        extractedGroups.flatMap((group) =>
          group.pages.length > 0
            ? group.pages.map((page) => sourceGroup.pages[page - 1] || page)
            : sourceGroup.pages
        )
      )
    ).sort((a, b) => a - b),
    status: 'extracted',
    header: {
      ...firstGroup.header,
      currency,
    },
    salesLines: extractedGroups.flatMap((group) => group.salesLines),
    rawSalesLines: Array.from(
      new Set(extractedGroups.flatMap((group) => group.rawSalesLines))
    ),
    costLines: extractedGroups.flatMap((group) => group.costLines),
    totals: extractedGroups.reduce(
      (totals, group) => ({
        cases: totals.cases + (Number(group.totals.cases) || 0),
        kilos: totals.kilos + (Number(group.totals.kilos) || 0),
        grossSales: totals.grossSales + (Number(group.totals.grossSales) || 0),
        totalExpenses:
          totals.totalExpenses + (Number(group.totals.totalExpenses) || 0),
        netProceeds:
          totals.netProceeds + (Number(group.totals.netProceeds) || 0),
      }),
      {
        cases: 0,
        kilos: 0,
        grossSales: 0,
        totalExpenses: 0,
        netProceeds: 0,
      }
    ),
    extractedReferences: Array.from(
      new Set(extractedGroups.flatMap((group) => group.extractedReferences))
    ),
    extractedContainers: Array.from(
      new Set(extractedGroups.flatMap((group) => group.extractedContainers))
    ),
    warnings: Array.from(
      new Set(extractedGroups.flatMap((group) => group.warnings))
    ),
  };
};

interface MultiShipmentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  inboxClaim?: InboxClaim;
}

const MultiShipmentUploadModal: React.FC<MultiShipmentUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  inboxClaim,
}) => {
  const { t } = useTranslation('settlements');
  const { userProfile } = useUserStore();
  const { selectedSpecies, species, setSelectedSpecies } = useDataStore();
  const { selectedSeason, setSelectedSeason } = useFilterStore();
  const { shipments, loading: shipmentsLoading } = useShipmentsStore();
  const confirm = useConfirm();

  const [receivers, setReceivers] = useState<ExporterReceiver[]>([]);
  const [mappedFormats, setMappedFormats] = useState<BoxFormatMapping[]>([]);
  const [receiversLoading, setReceiversLoading] = useState(false);
  const [selectedReceiverId, setSelectedReceiverId] = useState<string>('');
  // Multi-division exporters may pin the division; by default each settlement
  // follows the division of the shipment with the same reference.
  const [divisions, setDivisions] = useState<ExporterDivision[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) return;
    getExporterDivisions(userProfile.exporter_id)
      .then(setDivisions)
      .catch((error) => console.error('Error loading divisions:', error));
  }, [isOpen, userProfile?.exporter_id]);
  const [selectionMode, setSelectionMode] =
    useState<SelectionMode>('ship_group');
  const [selectedShipGroupKeys, setSelectedShipGroupKeys] = useState<
    Set<string>
  >(new Set());
  const [selectedManualKeys, setSelectedManualKeys] = useState<Set<string>>(
    new Set()
  );
  const [pastedShipmentText, setPastedShipmentText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(
    inboxClaim?.file || null
  );
  const [activeStep, setActiveStep] = useState<ModalStep>('setup');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [pdfThumbnails, setPdfThumbnails] = useState<PdfPageThumbnail[]>([]);
  const [pdfRenderDocument, setPdfRenderDocument] =
    useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [currentPdfPageNumber, setCurrentPdfPageNumber] = useState(1);
  const [pdfGroups, setPdfGroups] = useState<PdfDocumentGroup[]>([]);
  const [selectedPdfGroupId, setSelectedPdfGroupId] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processedData, setProcessedData] = useState<SettlementDetail[]>([]);
  const [groupedData, setGroupedData] = useState<PdfSettlementGroup[]>([]);
  const [pdfGroupProcessing, setPdfGroupProcessing] = useState<
    Record<string, PdfGroupProcessState>
  >({});
  const [isPdfSidebarOpen, setIsPdfSidebarOpen] = useState(false);
  const [pdfSidebarPage, setPdfSidebarPage] = useState(1);
  const [isApplyingSettlements, setIsApplyingSettlements] = useState(false);
  const [retrySourceDocumentId, setRetrySourceDocumentId] = useState('');
  const [reconciliationAssistantGroupKey, setReconciliationAssistantGroupKey] =
    useState('');
  const [activeLineAssistantKey, setActiveLineAssistantKey] = useState('');
  const [editingSalesGroupKey, setEditingSalesGroupKey] = useState('');
  const [editingCostGroupKey, setEditingCostGroupKey] = useState('');
  const [reviewSectionVisibility, setReviewSectionVisibility] = useState<
    Record<string, boolean>
  >({});
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [settlementHeaders, setSettlementHeaders] = useState<
    Record<string, SettlementHeaderConfig>
  >({});
  const [settlementActionErrors, setSettlementActionErrors] = useState<
    string[]
  >([]);
  const [activeGroupKey, setActiveGroupKey] = useState('');
  const [groupMappings, setGroupMappings] = useState<
    Record<string, GroupMapping>
  >({});
  const [processWarnings, setProcessWarnings] = useState<string[]>([]);
  const [receiverCostMappings, setReceiverCostMappings] = useState<
    ReceiverCostMapping[]
  >([]);
  const [receiverCostMappingsLoaded, setReceiverCostMappingsLoaded] =
    useState(false);
  const [savingReceiverCostMappingId, setSavingReceiverCostMappingId] =
    useState('');
  const [exporterCostCategories, setExporterCostCategories] = useState<
    ExporterCostCategory[]
  >([]);
  const [exporterCostMappings, setExporterCostMappings] = useState<
    Record<string, number>
  >({});
  const [savingExporterCostConcept, setSavingExporterCostConcept] =
    useState('');
  const showExporterCostCategories = exporterCostCategories.length > 0;
  const [savingBoxTypeMapping, setSavingBoxTypeMapping] = useState('');
  const [groupAssignmentLocks, setGroupAssignmentLocks] = useState<
    Record<string, GroupAssignmentLock>
  >({});
  const [boxTransfers, setBoxTransfers] = useState<BoxTransfer[]>([]);
  const [boxTransferBaselines, setBoxTransferBaselines] = useState<
    Record<string, ManualBoxAdjustmentSnapshot>
  >({});
  const [boxTransferDrafts, setBoxTransferDrafts] = useState<
    Record<
      string,
      { shipmentBoxId: string; toGroupKey: string; quantity: string }
    >
  >({});
  const [salesLineTransferDrafts, setSalesLineTransferDrafts] = useState<
    Record<string, { toGroupKey: string; quantity: string }>
  >({});

  const handleReviewSectionToggle = (key: string, isOpen: boolean) => {
    setReviewSectionVisibility((current) =>
      current[key] === isOpen ? current : { ...current, [key]: isOpen }
    );
  };

  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) return;

    let active = true;
    getFruticBoxToFormatMapping(userProfile).then((mappings) => {
      if (active)
        setMappedFormats((mappings as BoxFormatMapping[] | null) || []);
    });

    return () => {
      active = false;
    };
  }, [isOpen, userProfile]);

  const handleSaveBoxTypeMapping = async (
    boxType: string,
    format: (typeof VALID_FORMATS)[number]
  ) => {
    if (!userProfile?.exporter_id || !boxType || !format) return;

    setSavingBoxTypeMapping(boxType);
    try {
      const savedMapping = await saveFruticBoxToFormatMapping(
        userProfile,
        boxType,
        format
      );
      if (!savedMapping) {
        throw new Error(t('No se pudo guardar el mapeo de formato'));
      }

      const mapping = savedMapping as unknown as BoxFormatMapping;
      setMappedFormats((current) => [
        ...current.filter(
          (item) =>
            normalizeLookup(item.frutic_code) !== normalizeLookup(boxType)
        ),
        mapping,
      ]);
      toast.success(`${boxType} → ${format}`);
    } catch (error) {
      console.error('Error saving shipment box type mapping:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('No se pudo guardar el mapeo de formato')
      );
    } finally {
      setSavingBoxTypeMapping('');
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    void Promise.all([getShipTrips(), getPrimaryMarkets()]).then(
      ([trips, primaryMarkets]) => {
        setShipTrips(trips || []);
        setMarkets(primaryMarkets || []);
      }
    );
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !userProfile?.exporter_id) return;

    let active = true;
    const fetchReceivers = async () => {
      setReceiversLoading(true);
      try {
        const data = await getReceiversByExporter(userProfile.exporter_id);
        if (active) {
          setReceivers(data.sort((a, b) => a.name.localeCompare(b.name)));
        }
      } catch (error) {
        console.error('Error loading receivers:', error);
        toast.error(t('No se pudieron cargar los clientes'));
      } finally {
        if (active) setReceiversLoading(false);
      }
    };

    fetchReceivers();

    return () => {
      active = false;
    };
  }, [isOpen, t, userProfile?.exporter_id]);

  useEffect(() => {
    if (!selectedFile) {
      setActiveStep('setup');
      setPdfPreviewUrl('');
      setPdfPageCount(0);
      setPdfThumbnails([]);
      setPdfRenderDocument(null);
      setPdfGroups([]);
      setCurrentPdfPageNumber(1);
      return;
    }

    const lowerName = selectedFile.name.toLowerCase();
    const isPdf =
      selectedFile.type === 'application/pdf' || lowerName.endsWith('.pdf');
    if (!isPdf) {
      setPdfPreviewUrl('');
      setPdfPageCount(0);
      setPdfThumbnails([]);
      setPdfRenderDocument(null);
      setPdfGroups([]);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    let active = true;
    let loadingTask: ReturnType<typeof pdfjsLib.getDocument> | null = null;
    setPdfPreviewUrl(objectUrl);
    setPdfGroups([]);
    setPdfThumbnails([]);
    setPdfRenderDocument(null);
    setCurrentPdfPageNumber(1);

    const loadPdfPreview = async () => {
      try {
        const bytes = await selectedFile.arrayBuffer();
        loadingTask = pdfjsLib.getDocument({
          data: new Uint8Array(bytes),
        });
        const pdfDocument = await loadingTask.promise;
        if (!active) return;
        setPdfPageCount(pdfDocument.numPages);
        setPdfRenderDocument(pdfDocument);
      } catch (error) {
        console.error('Error loading PDF preview metadata:', error);
        if (active) {
          setPdfPageCount(0);
          setPdfThumbnails([]);
          setPdfRenderDocument(null);
          toast.error(t('No se pudo leer la cantidad de páginas del PDF'));
        }
      }
    };

    loadPdfPreview();

    return () => {
      active = false;
      setPdfRenderDocument(null);
      void loadingTask?.destroy();
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedFile, t]);

  const pdfPageToRender =
    activeStep === 'review' && isPdfSidebarOpen
      ? pdfSidebarPage
      : currentPdfPageNumber;

  useEffect(() => {
    if (
      !pdfRenderDocument ||
      !pdfPageToRender ||
      pdfThumbnails.some(
        (thumbnail) => thumbnail.pageNumber === pdfPageToRender
      )
    ) {
      return;
    }

    let active = true;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null =
      null;

    const renderCurrentPage = async () => {
      try {
        const page = await pdfRenderDocument.getPage(pdfPageToRender);
        if (!active) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(
          1100 / baseViewport.width,
          1500 / baseViewport.height,
          2
        );
        const viewport = page.getViewport({ scale });
        const canvas = window.document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        renderTask = page.render({ canvasContext: context, viewport });
        await renderTask.promise;
        if (!active) return;

        const thumbnail: PdfPageThumbnail = {
          pageNumber: pdfPageToRender,
          dataUrl: canvas.toDataURL('image/jpeg', 0.88),
          width: canvas.width,
          height: canvas.height,
        };
        setPdfThumbnails((current) => [
          ...current
            .filter((item) => item.pageNumber !== pdfPageToRender)
            .slice(-5),
          thumbnail,
        ]);
      } catch (error) {
        if (active) {
          console.error('Error rendering PDF preview page:', error);
        }
      }
    };

    void renderCurrentPage();
    return () => {
      active = false;
      renderTask?.cancel();
    };
  }, [pdfPageToRender, pdfRenderDocument, pdfThumbnails]);

  const selectedReceiver = useMemo(
    () =>
      receivers.find((receiver) => String(receiver.id) === selectedReceiverId),
    [receivers, selectedReceiverId]
  );

  useEffect(() => {
    let cancelled = false;
    setReceiverCostMappings([]);
    setReceiverCostMappingsLoaded(false);

    if (!userProfile?.exporter_id || !selectedReceiver?.id) return;

    getReceiverCostMappings(userProfile.exporter_id, selectedReceiver.id)
      .then((mappings) => {
        if (!cancelled) {
          setReceiverCostMappings(mappings);
          setReceiverCostMappingsLoaded(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Error loading receiver cost mappings:', error);
          toast.error(t('No se pudieron cargar los mapeos de costos'));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedReceiver?.id, t, userProfile?.exporter_id]);

  useEffect(() => {
    let cancelled = false;
    setExporterCostCategories([]);
    setExporterCostMappings({});
    const exporterId = userProfile?.exporter_id;
    const receiverId = selectedReceiver?.id;
    if (!exporterId || !receiverId) return;

    (async () => {
      if (!(await getCustomCostCategoriesEnabled(exporterId))) return;
      const [categories, mappingsByConcept] = await Promise.all([
        getExporterCostCategories(exporterId, true),
        getReceiverCostExporterMappings(exporterId, receiverId),
      ]);
      if (!cancelled) {
        setExporterCostCategories(categories);
        setExporterCostMappings(mappingsByConcept);
      }
    })().catch((error) => {
      console.error('Error loading exporter cost categories:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedReceiver?.id, userProfile?.exporter_id]);

  const handleExporterCostCategoryChange = async (
    description: string,
    categoryId: number | null
  ) => {
    if (!selectedReceiver?.id) return;
    const concept = normalizeReceiverCostConcept(description);
    if ((exporterCostMappings[concept] ?? null) === categoryId) return;

    setSavingExporterCostConcept(concept);
    try {
      await setReceiverCostExporterCategories(selectedReceiver.id, [
        { name: description, categoryId },
      ]);
      setExporterCostMappings((current) => {
        const next = { ...current };
        if (categoryId === null) delete next[concept];
        else next[concept] = categoryId;
        return next;
      });
      toast.success(t('Categoría exportadora guardada para este concepto'));
    } catch (error) {
      console.error('Error saving exporter cost category:', error);
      toast.error(t('No se pudo guardar la categoría exportadora'));
    } finally {
      setSavingExporterCostConcept('');
    }
  };

  const groupCostConceptSignature = useMemo(
    () =>
      groupedData
        .flatMap((group) =>
          group.costLines.map(
            (line) =>
              `${line.id}:${normalizeReceiverCostConcept(line.description)}`
          )
        )
        .join('|'),
    [groupedData]
  );

  useEffect(() => {
    if (!receiverCostMappingsLoaded) return;

    const mappingsByConcept = new Map(
      receiverCostMappings.map((mapping) => [
        mapping.normalizedConcept,
        mapping,
      ])
    );

    setGroupedData((current) => {
      let changed = false;
      const next = current.map((group) => ({
        ...group,
        costLines: group.costLines.map((line) => {
          const mapping = mappingsByConcept.get(
            normalizeReceiverCostConcept(line.description)
          );
          const mappingStatus = mapping ? 'saved' : 'new';
          const mappedCategory =
            mapping?.officialCategory || line.mappedCategory;
          const suggestedCategory =
            line.suggestedCategory || line.mappedCategory;

          if (
            line.mappingStatus === mappingStatus &&
            line.mappingId === mapping?.id &&
            line.mappedCategory === mappedCategory &&
            line.suggestedCategory === suggestedCategory
          ) {
            return line;
          }

          changed = true;
          return {
            ...line,
            mappedCategory,
            suggestedCategory,
            mappingStatus,
            mappingId: mapping?.id,
          };
        }),
      }));
      return changed ? next : current;
    });
  }, [
    groupCostConceptSignature,
    receiverCostMappings,
    receiverCostMappingsLoaded,
  ]);

  const sortedShipTrips = useMemo(
    () =>
      [...shipTrips].sort((a, b) =>
        `${a.ship?.name || ''} ${a.eta || ''}`.localeCompare(
          `${b.ship?.name || ''} ${b.eta || ''}`,
          'es'
        )
      ),
    [shipTrips]
  );

  const sortedMarkets = useMemo(
    () => [...markets].sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [markets]
  );

  const clientShipments = useMemo(() => {
    if (!selectedReceiverId) return [];
    return shipments.filter((box) => {
      if (Number(box.exporter_receiver_id) !== Number(selectedReceiverId)) {
        return false;
      }

      if (
        selectedSpecies?.id &&
        box.species_id &&
        Number(box.species_id) !== selectedSpecies.id
      ) {
        return false;
      }

      if (
        selectedSeason &&
        selectedSeason !== 'all' &&
        box.season &&
        box.season !== selectedSeason
      ) {
        return false;
      }

      return true;
    });
  }, [selectedReceiverId, selectedSeason, selectedSpecies?.id, shipments]);

  const shipGroups = useMemo<ShipGroup[]>(() => {
    const groups = new Map<string, ShipGroup>();

    clientShipments.forEach((box) => {
      const shipName = normalizeText(box.ship_name) || t('Sin nave');
      const eta = normalizeText(box.ship_eta) || t('Sin ETA');
      const destination =
        normalizeText(box.ship_destination_port) || t('Sin puerto');
      const key = `${shipName}__${eta}__${destination}`;
      const current =
        groups.get(key) ||
        ({
          key,
          ship_name: shipName,
          ship_etd: normalizeText(box.ship_etd),
          ship_eta: eta,
          ship_destination_port: destination,
          boxes: [],
          references: [],
          containers: [],
          kilos: 0,
        } satisfies ShipGroup);

      current.boxes.push(box);
      current.kilos += (box.quantity || 0) * (box.box_weight || 0);
      groups.set(key, current);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        references: uniqueValues(
          group.boxes.map((box) => box.reference_number)
        ),
        containers: uniqueValues(
          group.boxes.map((box) => box.container_number)
        ),
      }))
      .sort((a, b) => {
        const etaCompare = a.ship_eta.localeCompare(b.ship_eta);
        return etaCompare || a.ship_name.localeCompare(b.ship_name);
      });
  }, [clientShipments, t]);

  const manualOptions = useMemo(() => {
    const grouped = new Map<string, ShipmentRecord[]>();

    clientShipments.forEach((box) => {
      const reference = normalizeText(box.reference_number);
      const container = normalizeText(box.container_number);
      if (!reference && !container) return;
      const key = reference
        ? `ref:${reference.toUpperCase()}`
        : `container:${normalizeLookup(container)}`;
      grouped.set(key, [...(grouped.get(key) || []), box]);
    });

    return Array.from(grouped.entries())
      .map(([key, boxes]) => {
        const first = boxes[0];
        const reference = normalizeText(first.reference_number);
        const container = normalizeText(first.container_number);
        const ship = normalizeText(first.ship_name);
        const eta = formatDate(first.ship_eta);
        return {
          key,
          label: reference
            ? `Ref ${reference}${container ? ` · ${container}` : ''}`
            : `Contenedor ${container}`,
          description: [ship, eta, `${boxes.length} ${t('líneas')}`]
            .filter(Boolean)
            .join(' · '),
          boxes,
          reference,
          container,
          lookupTokens: [
            normalizeLookup(reference),
            normalizeLookup(container),
          ].filter(Boolean),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clientShipments, t]);

  const pastedMatches = useMemo(() => {
    const tokens = pastedShipmentText
      .split(/[\n,;\t ]+/)
      .map((token) => normalizeLookup(token))
      .filter(Boolean);

    if (!tokens.length) {
      return {
        matchedKeys: new Set<string>(),
        unmatchedTokens: [] as string[],
      };
    }

    const optionByToken = new Map<string, string>();
    manualOptions.forEach((option) => {
      option.lookupTokens.forEach((token) =>
        optionByToken.set(token, option.key)
      );
    });

    const matchedKeys = new Set<string>();
    const unmatchedTokens: string[] = [];
    tokens.forEach((token) => {
      const optionKey = optionByToken.get(token);
      if (optionKey) {
        matchedKeys.add(optionKey);
      } else if (!unmatchedTokens.includes(token)) {
        unmatchedTokens.push(token);
      }
    });

    return { matchedKeys, unmatchedTokens };
  }, [manualOptions, pastedShipmentText]);

  const selectedManualOptionKeys = useMemo(
    () =>
      new Set([
        ...Array.from(selectedManualKeys),
        ...Array.from(pastedMatches.matchedKeys),
      ]),
    [pastedMatches.matchedKeys, selectedManualKeys]
  );

  const selectedBoxes = useMemo(() => {
    let boxes: ShipmentRecord[] = [];

    if (selectionMode === 'ship_group') {
      boxes = shipGroups
        .filter((group) => selectedShipGroupKeys.has(group.key))
        .flatMap((group) => group.boxes);
    }

    if (selectionMode === 'manual') {
      boxes = manualOptions
        .filter((option) => selectedManualOptionKeys.has(option.key))
        .flatMap((option) => option.boxes);
    }

    return Array.from(
      new Map(boxes.map((box) => [String(box.id), box])).values()
    );
  }, [
    manualOptions,
    selectedManualOptionKeys,
    selectedShipGroupKeys,
    selectionMode,
    shipGroups,
  ]);

  const hasShipmentPrefilter =
    selectionMode === 'ship_group'
      ? selectedShipGroupKeys.size > 0
      : selectionMode === 'manual'
        ? selectedManualOptionKeys.size > 0
        : false;

  const effectiveShipments = useMemo(
    () => (hasShipmentPrefilter ? selectedBoxes : clientShipments),
    [clientShipments, hasShipmentPrefilter, selectedBoxes]
  );
  const effectiveShipmentsById = useMemo(
    () =>
      new Map(effectiveShipments.map((box) => [String(box.id), box] as const)),
    [effectiveShipments]
  );

  const reservedBoxQuantities = useMemo(() => {
    const reservations = new Map<string, Map<string, number>>();
    Object.entries(groupAssignmentLocks).forEach(([groupKey, assignment]) => {
      Object.values(assignment.shipmentBoxQuantitiesByLine || {}).forEach(
        (lineQuantities) => {
          Object.entries(lineQuantities).forEach(([boxId, quantity]) => {
            const byGroup =
              reservations.get(boxId) || new Map<string, number>();
            byGroup.set(
              groupKey,
              (byGroup.get(groupKey) || 0) + (Number(quantity) || 0)
            );
            reservations.set(boxId, byGroup);
          });
        }
      );
    });
    return reservations;
  }, [groupAssignmentLocks]);

  const shipmentAvailabilityCache = useMemo(
    () => ({
      effectiveShipments,
      reservedBoxQuantities,
      values: new Map<string, ShipmentRecord[]>(),
    }),
    [effectiveShipments, reservedBoxQuantities]
  );

  const getAvailableShipmentsForGroup = useCallback(
    (groupKey: string) => {
      const cached = shipmentAvailabilityCache.values.get(groupKey);
      if (cached) return cached;

      const availableShipments = effectiveShipments.flatMap((box) => {
        const reservations = reservedBoxQuantities.get(String(box.id));
        const reservedByOthers = Array.from(reservations || []).reduce(
          (total, [reservedGroupKey, quantity]) =>
            reservedGroupKey === groupKey ? total : total + quantity,
          0
        );
        const availableQuantity = Math.max(
          0,
          (Number(box.quantity) || 0) - reservedByOthers
        );
        if (availableQuantity <= 0) return [];
        return availableQuantity === (Number(box.quantity) || 0)
          ? [box]
          : [{ ...box, quantity: availableQuantity }];
      });

      shipmentAvailabilityCache.values.set(groupKey, availableShipments);
      return availableShipments;
    },
    [effectiveShipments, reservedBoxQuantities, shipmentAvailabilityCache]
  );

  const selectedReferences = useMemo(
    () => uniqueValues(effectiveShipments.map((box) => box.reference_number)),
    [effectiveShipments]
  );

  const selectedContainers = useMemo(
    () => uniqueValues(effectiveShipments.map((box) => box.container_number)),
    [effectiveShipments]
  );

  const selectedKilos = useMemo(
    () =>
      effectiveShipments.reduce(
        (sum, box) => sum + (box.quantity || 0) * (box.box_weight || 0),
        0
      ),
    [effectiveShipments]
  );

  const previewTotals = useMemo(
    () =>
      processedData.reduce(
        (totals, detail) => ({
          kilos: totals.kilos + (Number(detail.kilos) || 0),
          boxes: totals.boxes + (Number(detail.bultos_cargados) || 0),
          revenue: totals.revenue + (Number(detail.ingreso_moneda_origen) || 0),
          commission:
            totals.commission + (Number(detail.comision_moneda_origen) || 0),
          tax: totals.tax + (Number(detail.tax_moneda_origen) || 0),
          logistics:
            totals.logistics +
            (Number(detail.costos_logisticos_moneda_origen) || 0),
          repacking:
            totals.repacking +
            (Number(detail.repacking_costs_moneda_origen) || 0),
          freight: totals.freight + (Number(detail.flete_usd) || 0),
        }),
        {
          kilos: 0,
          boxes: 0,
          revenue: 0,
          commission: 0,
          tax: 0,
          logistics: 0,
          repacking: 0,
          freight: 0,
        }
      ),
    [processedData]
  );

  const previewReferences = useMemo(
    () => uniqueValues(processedData.map((detail) => detail.referencia)),
    [processedData]
  );

  const previewContainers = useMemo(
    () => uniqueValues(processedData.map((detail) => detail.nro_contenedor)),
    [processedData]
  );

  const previewCurrencies = useMemo(
    () => uniqueValues(processedData.map((detail) => detail.moneda)),
    [processedData]
  );

  const groupedTotals = useMemo(
    () =>
      groupedData.reduce(
        (totals, group) => ({
          groups: totals.groups + 1,
          salesLines: totals.salesLines + group.salesLines.length,
          costLines: totals.costLines + group.costLines.length,
          cases: totals.cases + (Number(group.totals.cases) || 0),
          grossSales:
            totals.grossSales + (Number(group.totals.grossSales) || 0),
          totalExpenses:
            totals.totalExpenses + (Number(group.totals.totalExpenses) || 0),
          netProceeds:
            totals.netProceeds + (Number(group.totals.netProceeds) || 0),
        }),
        {
          groups: 0,
          salesLines: 0,
          costLines: 0,
          cases: 0,
          grossSales: 0,
          totalExpenses: 0,
          netProceeds: 0,
        }
      ),
    [groupedData]
  );

  const groupedCurrency = useMemo(
    () => groupedData.find((group) => group.header.currency)?.header.currency,
    [groupedData]
  );

  const groupedStatusCounts = useMemo(
    () =>
      groupedData.reduce(
        (counts, group) => ({
          ...counts,
          [group.status]: counts[group.status] + 1,
        }),
        {
          extracted: 0,
          mapped: 0,
          reviewed: 0,
          ready_to_apply: 0,
        } as Record<GroupReviewStatus, number>
      ),
    [groupedData]
  );

  const deferredGroupMappings = useDeferredValue(groupMappings);

  const derivedSettlementResult = useMemo(() => {
    const errors: string[] = [];
    const details: DerivedSettlementDetail[] = [];
    const unmappedBoxTypes = new Map<string, string>();

    groupedData.forEach((group, groupIndex) => {
      const groupKey = getGroupKey(group, groupIndex);
      const mapping = deferredGroupMappings[groupKey] || EMPTY_GROUP_MAPPING;
      const assignment = groupAssignmentLocks[groupKey];
      const groupShipments = getAvailableShipmentsForGroup(groupKey);
      const groupDetails: DerivedSettlementDetail[] = [];

      if (!assignment) {
        errors.push(`${group.title}: ${t('grupo sin asignar')}`);
      }

      group.salesLines.forEach((line) => {
        if (!assignment && !mapping.confirmedLineAssignments[line.id]) {
          errors.push(`${group.title}: ${t('hay ventas sin confirmar')}`);
        }

        const detailScope = mapping.detailScopes[line.id] || {
          scope: emptyShipmentBoxScope(),
          activeFields: [],
        };
        const lineScope = {
          ...mapping.scope,
          ...Object.fromEntries(
            Object.entries(detailScope.scope).map(([field, values]) => [
              field,
              values.length > 0
                ? values
                : mapping.scope[field as keyof ShipmentBoxScope],
            ])
          ),
        } as ShipmentBoxScope;
        const candidateLineBoxes = groupShipments.filter((box) =>
          matchesShipmentBoxScope(box, lineScope, mappedFormats)
        );
        const assignedBoxIds = new Set(
          assignment?.shipmentBoxIdsByLine[line.id] || []
        );
        const assignedBoxQuantities =
          assignment?.shipmentBoxQuantitiesByLine[line.id] || {};
        const lineBoxes = assignment
          ? groupShipments
              .filter((box) => assignedBoxIds.has(String(box.id)))
              .map((box) => ({
                ...box,
                quantity: Math.min(
                  Number(box.quantity) || 0,
                  Number(assignedBoxQuantities[String(box.id)]) || 0
                ),
              }))
              .filter((box) => box.quantity > 0)
          : candidateLineBoxes;
        lineBoxes.forEach((box) => {
          const boxType = normalizeText(box.box_type);
          if (boxType && !getMappedShipmentPackageType(box, mappedFormats)) {
            unmappedBoxTypes.set(normalizeLookup(boxType), boxType);
          }
        });
        const assignedQuantity = lineBoxes.reduce(
          (total, box) => total + (Number(box.quantity) || 0),
          0
        );

        if (!assignment) return;

        if (assignedQuantity !== Number(line.quantity || 0)) {
          errors.push(`${group.title}: ${t('bultos no cuadran en una venta')}`);
          return;
        }

        const byQuality = new Map<string, ShipmentRecord[]>();
        lineBoxes.forEach((box) => {
          const key = [
            box.reference_number,
            getSettlementDetailVariety(box),
            box.caliber || '',
            box.color || '',
            box.category || '',
            box.box_label || '',
            getShipmentFormat(box, mappedFormats),
          ].join('|');
          byQuality.set(key, [...(byQuality.get(key) || []), box]);
        });

        let allocatedLineSales = 0;
        const qualityGroups = Array.from(byQuality.values());
        qualityGroups.forEach((boxes, qualityIndex) => {
          const first = boxes[0];
          const quantity = boxes.reduce(
            (total, box) => total + (Number(box.quantity) || 0),
            0
          );
          const kilos = boxes.reduce(
            (total, box) =>
              total +
              (Number(box.quantity) || 0) * (Number(box.box_weight) || 0),
            0
          );
          const lineAmount = Number(line.amount) || 0;
          const sales =
            qualityIndex === qualityGroups.length - 1
              ? lineAmount - allocatedLineSales
              : lineAmount * (quantity / assignedQuantity);
          allocatedLineSales += sales;
          groupDetails.push({
            id: createLocalId(),
            referenceNumber: first.reference_number,
            containerNumber: first.container_number,
            shipName: first.ship_name,
            shipEtd: first.ship_etd,
            shipEta: first.ship_eta,
            variety: getSettlementDetailVariety(first),
            caliber: first.caliber || '',
            color: first.color || '',
            category: first.category || '',
            label: first.box_label || '',
            format: getShipmentPackageType(first, mappedFormats),
            unitWeight: Number(first.box_weight) || 0,
            quantity,
            kilos,
            sales,
            tax: 0,
            commission: 0,
            freight: 0,
            repacking: 0,
            otherCosts: 0,
            sourceGroupIndex: groupIndex,
            sourceLineId: line.id,
            shipmentBoxes: boxes,
          });
        });
      });

      const rawLineGross = group.salesLines.reduce(
        (total, line) => total + (Number(line.amount) || 0),
        0
      );
      const targetGross = rawLineGross;
      const currentGross = groupDetails.reduce(
        (total, detail) => total + detail.sales,
        0
      );
      const salesDenominator =
        currentGross ||
        groupDetails.reduce(
          (total, detail) => total + (Number(detail.quantity) || 0),
          0
        );
      let allocatedGross = 0;
      groupDetails.forEach((detail, detailIndex) => {
        const weight = currentGross ? detail.sales : detail.quantity;
        const value =
          detailIndex === groupDetails.length - 1
            ? targetGross - allocatedGross
            : salesDenominator > 0
              ? targetGross * (weight / salesDenominator)
              : 0;
        detail.sales = value;
        allocatedGross += value;
      });

      const rawCostsByCategory = Object.fromEntries(
        COST_CATEGORY_OPTIONS.map((category) => [
          category,
          group.costLines
            .filter((line) => line.mappedCategory === category)
            .reduce((sum, line) => sum + (Number(line.amount) || 0), 0),
        ])
      ) as Record<SettlementCostCategory, number>;
      const allocate = (
        key: 'tax' | 'commission' | 'freight' | 'repacking' | 'otherCosts',
        category: SettlementCostCategory,
        by: 'sales' | 'kilos'
      ) => {
        const total = rawCostsByCategory[category];
        const denominator = groupDetails.reduce(
          (sum, detail) => sum + (by === 'sales' ? detail.sales : detail.kilos),
          0
        );
        if (!total || !denominator) return;
        let allocated = 0;
        groupDetails.forEach((detail, index) => {
          const value =
            index === groupDetails.length - 1
              ? total - allocated
              : total *
                ((by === 'sales' ? detail.sales : detail.kilos) / denominator);
          detail[key] = value;
          allocated += value;
        });
      };

      allocate('commission', 'commission', 'sales');
      allocate('tax', 'tax', 'sales');
      allocate('freight', 'freight', 'kilos');
      allocate('repacking', 'repacking', 'kilos');
      allocate('otherCosts', 'other_costs', 'kilos');

      details.push(...groupDetails);
    });

    const missingBoxTypeMappings = Array.from(unmappedBoxTypes.values()).sort(
      (a, b) => a.localeCompare(b, 'es')
    );
    if (missingBoxTypeMappings.length > 0) {
      errors.push(
        `${t('Mapea los tipos de caja antes de crear las liquidaciones')}: ${missingBoxTypeMappings.join(', ')}`
      );
    }

    const settlements = Array.from(
      details
        .reduce((byReference, detail) => {
          const current = byReference.get(detail.referenceNumber) || {
            referenceNumber: detail.referenceNumber,
            containerNumber: detail.containerNumber,
            shipName: detail.shipName,
            shipEtd: detail.shipEtd,
            shipEta: detail.shipEta,
            details: [],
          };
          current.details.push(detail);
          byReference.set(detail.referenceNumber, current);
          return byReference;
        }, new Map<string, DerivedSettlement>())
        .values()
    );

    return {
      settlements,
      errors: Array.from(new Set(errors)),
      unmappedBoxTypes: missingBoxTypeMappings,
    };
  }, [
    getAvailableShipmentsForGroup,
    groupAssignmentLocks,
    groupedData,
    deferredGroupMappings,
    mappedFormats,
    t,
  ]);

  useEffect(() => {
    if (derivedSettlementResult.settlements.length === 0) return;
    setSettlementHeaders((current) => {
      const next = { ...current };
      derivedSettlementResult.settlements.forEach((settlement) => {
        if (next[settlement.referenceNumber]) return;
        const matchingTrips = shipTrips.filter(
          (trip) =>
            trip.ship?.name === settlement.shipName &&
            trip.etd === settlement.shipEtd &&
            trip.eta === settlement.shipEta
        );
        const shipmentTransportType = settlement.details
          .flatMap((detail) => detail.shipmentBoxes)
          .find((box) => box.ship_type)?.ship_type;
        const destinationPort = settlement.details
          .flatMap((detail) => detail.shipmentBoxes)
          .map((box) => normalizeText(box.ship_destination_port))
          .find(Boolean);
        const destinationMarketId = destinationPort
          ? String(
              markets.find(
                (market) =>
                  normalizeLookup(market.name) ===
                  normalizeLookup(destinationPort)
              )?.id || ''
            )
          : '';
        next[settlement.referenceNumber] = {
          transportType:
            matchingTrips.length === 1
              ? matchingTrips[0].ship.type
              : shipmentTransportType || '',
          shipTripId:
            matchingTrips.length === 1 ? String(matchingTrips[0].id) : '',
          shipEtd: toDateInputValue(settlement.shipEtd),
          shipEta: toDateInputValue(settlement.shipEta),
          destinationMarketId,
          marketId: '',
          comparableMarketId: '',
          dateSold: toSettlementDate(groupedData[0]?.header.dateRangeEnd).slice(
            0,
            10
          ),
          region: '',
          exchangeRate: '1',
          currency: groupedCurrency || 'USD',
          businessType: '',
          saleOrder: '',
          originalIncoterm: '',
        };
      });
      return next;
    });
  }, [
    derivedSettlementResult.settlements,
    groupedCurrency,
    groupedData,
    markets,
    shipTrips,
  ]);

  const updateSettlementHeader = (
    referenceNumber: string,
    updates: Partial<SettlementHeaderConfig>
  ) => {
    setSettlementActionErrors([]);
    setSettlementHeaders((current) => ({
      ...current,
      [referenceNumber]: {
        ...current[referenceNumber],
        ...updates,
      },
    }));
  };

  const hasIncompleteSettlementHeaders =
    derivedSettlementResult.settlements.some((settlement) => {
      const header = settlementHeaders[settlement.referenceNumber];
      const hasIncompleteTransport =
        !header?.transportType ||
        (header.transportType === 'M'
          ? !header.shipTripId
          : !header.shipEtd || !header.shipEta || !header.destinationMarketId);
      return hasIncompleteTransport || !header.marketId || !header.dateSold;
    });

  const shipmentLookup = useMemo(() => {
    const lookup = new Map<
      string,
      { label: string; reference: string; container: string }
    >();

    effectiveShipments.forEach((box) => {
      const reference = normalizeText(box.reference_number);
      const container = normalizeText(box.container_number);
      const label = reference
        ? `Ref ${reference}${container ? ` · ${container}` : ''}`
        : `Contenedor ${container}`;

      if (reference) {
        lookup.set(`ref:${normalizeLookup(reference)}`, {
          label,
          reference,
          container,
        });
      }
      if (container) {
        lookup.set(`container:${normalizeLookup(container)}`, {
          label,
          reference,
          container,
        });
      }
    });

    return lookup;
  }, [effectiveShipments]);

  const fullShipmentLookup = useMemo(() => {
    const lookup = new Map<string, string>();
    clientShipments.forEach((box) => {
      const reference = normalizeText(box.reference_number);
      const container = normalizeText(box.container_number);
      if (reference) {
        lookup.set(`ref:${normalizeLookup(reference)}`, reference);
      }
      if (container) {
        lookup.set(`container:${normalizeLookup(container)}`, container);
      }
    });
    return lookup;
  }, [clientShipments]);

  const outsideScopeShipmentOptions = useMemo(() => {
    if (!hasShipmentPrefilter || groupedData.length === 0) return [];

    const extractedTokens = new Set<string>();
    groupedData.forEach((group) => {
      uniqueValues([...group.extractedReferences, group.groupId]).forEach(
        (reference) => extractedTokens.add(`ref:${normalizeLookup(reference)}`)
      );
      uniqueValues(group.extractedContainers).forEach((container) =>
        extractedTokens.add(`container:${normalizeLookup(container)}`)
      );
    });

    const outsideTokens = new Set(
      Array.from(extractedTokens).filter(
        (token) =>
          token.split(':')[1] &&
          fullShipmentLookup.has(token) &&
          !shipmentLookup.has(token)
      )
    );

    return manualOptions.filter((option) =>
      option.lookupTokens.some(
        (token) =>
          outsideTokens.has(`ref:${token}`) ||
          outsideTokens.has(`container:${token}`)
      )
    );
  }, [
    fullShipmentLookup,
    groupedData,
    hasShipmentPrefilter,
    manualOptions,
    shipmentLookup,
  ]);

  const outsideScopeShipGroups = useMemo(() => {
    const outsideBoxIds = new Set(
      outsideScopeShipmentOptions
        .flatMap((option) => option.boxes)
        .map((box) => String(box.id))
    );
    return shipGroups.filter((group) =>
      group.boxes.some((box) => outsideBoxIds.has(String(box.id)))
    );
  }, [outsideScopeShipmentOptions, shipGroups]);

  const previewShipmentMatches = useMemo(() => {
    const extractedKeys = [
      ...previewReferences.map((reference) => ({
        key: `ref:${normalizeLookup(reference)}`,
        label: `Ref ${reference}`,
      })),
      ...previewContainers.map((container) => ({
        key: `container:${normalizeLookup(container)}`,
        label: `Contenedor ${container}`,
      })),
    ].filter((item) => item.key.split(':')[1]);

    const matched = new Map<
      string,
      { label: string; reference: string; container: string }
    >();
    const unmatched: string[] = [];

    extractedKeys.forEach((item) => {
      const match = shipmentLookup.get(item.key);
      if (match) {
        matched.set(`${match.reference || ''}-${match.container || ''}`, match);
      } else if (!unmatched.includes(item.label)) {
        unmatched.push(item.label);
      }
    });

    return {
      matched: Array.from(matched.values()),
      unmatched,
    };
  }, [previewContainers, previewReferences, shipmentLookup]);

  const getGroupShipmentMatches = (group: PdfSettlementGroup) => {
    const referenceCandidates = uniqueValues([
      ...group.extractedReferences,
      group.groupId,
    ]);
    const containerCandidates = uniqueValues(group.extractedContainers);
    const extractedKeys = [
      ...referenceCandidates.map((reference) => ({
        key: `ref:${normalizeLookup(reference)}`,
        label: `Ref ${reference}`,
      })),
      ...containerCandidates.map((container) => ({
        key: `container:${normalizeLookup(container)}`,
        label: `Contenedor ${container}`,
      })),
    ].filter((item) => item.key.split(':')[1]);

    const matched = new Map<
      string,
      { label: string; reference: string; container: string }
    >();
    const unmatched: string[] = [];

    extractedKeys.forEach((item) => {
      const match = shipmentLookup.get(item.key);
      if (match) {
        matched.set(`${match.reference || ''}-${match.container || ''}`, match);
      } else if (!unmatched.includes(item.label)) {
        unmatched.push(item.label);
      }
    });

    return {
      matched: Array.from(matched.values()),
      unmatched,
    };
  };

  useEffect(() => {
    setActiveStep('setup');
    setSelectedShipGroupKeys(new Set());
    setSelectedManualKeys(new Set());
    setPastedShipmentText('');
    setProcessingMessage('');
    setProcessedData([]);
    setGroupedData([]);
    setPdfGroupProcessing({});
    setIsPdfSidebarOpen(false);
    setPdfSidebarPage(1);
    setActiveGroupKey('');
    setGroupMappings({});
    setGroupAssignmentLocks({});
    setBoxTransfers([]);
    setBoxTransferBaselines({});
    setBoxTransferDrafts({});
    setSalesLineTransferDrafts({});
    setActiveLineAssistantKey('');
    setSettlementHeaders({});
    setProcessWarnings([]);
    setRetrySourceDocumentId('');
  }, [selectedReceiverId, selectedSeason, selectedSpecies?.id]);

  const handleFileSelect = (file: File) => {
    if (inboxClaim && file !== inboxClaim.file) return;
    const lowerName = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf');

    if (!isPdf) {
      toast.error(t('Selecciona un archivo PDF válido'));
      return;
    }

    if (file.size > 25 * 1024 * 1024) {
      toast.error(t('El archivo es demasiado grande. Máximo 25MB.'));
      return;
    }

    setSelectedFile(file);
    setActiveStep('setup');
    setProcessingMessage('');
    setProcessedData([]);
    setGroupedData([]);
    setPdfGroupProcessing({});
    setActiveGroupKey('');
    setGroupMappings({});
    setGroupAssignmentLocks({});
    setBoxTransfers([]);
    setBoxTransferBaselines({});
    setBoxTransferDrafts({});
    setSalesLineTransferDrafts({});
    setActiveLineAssistantKey('');
    setProcessWarnings([]);
    setRetrySourceDocumentId('');
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleProcessDocument = async () => {
    if (!selectedReceiver || !selectedFile) {
      toast.error(t('Completa cliente y archivo antes de continuar'));
      return;
    }

    const lowerName = selectedFile.name.toLowerCase();
    const isPdf =
      selectedFile.type === 'application/pdf' || lowerName.endsWith('.pdf');

    if (!isPdf) {
      toast.error(t('Selecciona un archivo PDF válido'));
      return;
    }

    if (pdfGroups.length === 0) {
      toast.error(t('Crea al menos un grupo de páginas'));
      return;
    }

    const pendingGroups = pdfGroups.map(createPendingSettlementGroup);
    const initialProcessState = Object.fromEntries(
      pendingGroups.map((group, index) => [
        getGroupKey(group, index),
        { status: 'pending' as PdfGroupProcessStatus },
      ])
    );

    setIsProcessing(true);
    setProcessedData([]);
    setGroupedData(pendingGroups);
    setPdfGroupProcessing(initialProcessState);
    setActiveGroupKey(getGroupKey(pendingGroups[0], 0));
    setActiveLineAssistantKey('');
    setPdfSidebarPage(pendingGroups[0]?.pages[0] || 1);
    setIsPdfSidebarOpen(true);
    setReviewSectionVisibility({});
    setActiveStep('review');
    setGroupMappings({});
    setGroupAssignmentLocks({});
    setBoxTransfers([]);
    setBoxTransferBaselines({});
    setBoxTransferDrafts({});
    setSalesLineTransferDrafts({});
    setActiveLineAssistantKey('');
    setProcessWarnings([]);
    setRetrySourceDocumentId('');

    try {
      setProcessingMessage(t('Preparando grupos del PDF'));
      const sourceBytes = await selectedFile.arrayBuffer();
      const sourceDocument = await PDFDocument.load(sourceBytes);
      const groupedPayloads: Array<{
        group: PdfDocumentGroup;
        pdfData: string;
      }> = [];

      for (const group of pdfGroups) {
        groupedPayloads.push({
          group,
          pdfData: await buildGroupedPdfPayload(sourceDocument, group),
        });
      }

      const warnings: string[] = [];
      let completedGroups = 0;
      let successfulGroups = 0;
      setProcessingMessage(`0/${groupedPayloads.length} ${t('grupos leídos')}`);

      for (const { group, pdfData } of groupedPayloads) {
        const groupIndex = pdfGroups.findIndex((item) => item.id === group.id);
        const pendingGroup = pendingGroups[groupIndex];
        const groupKey = getGroupKey(pendingGroup, groupIndex);

        setPdfGroupProcessing((current) => ({
          ...current,
          [groupKey]: { status: 'processing' },
        }));
        setProcessingMessage(
          `${completedGroups}/${groupedPayloads.length} ${t('grupos leídos')} · ${group.name}`
        );

        let result: Awaited<ReturnType<typeof processGroupedPdfFile>> | null =
          null;
        let lastError: unknown = null;

        for (let attempt = 1; attempt <= 2; attempt += 1) {
          try {
            result = await processGroupedPdfFile({
              pdfData,
              filename: `${group.name}.pdf`,
              groupId: group.id,
              groupName: group.name,
            });
            break;
          } catch (error) {
            lastError = error;
            if (attempt === 1) {
              setProcessingMessage(
                `${t('Reintentando grupo')} · ${group.name}`
              );
              await wait(1000);
            }
          }
        }

        completedGroups += 1;

        if (!result || !result.success) {
          const message =
            result?.error ||
            (lastError instanceof Error
              ? lastError.message
              : t('No se pudo leer el documento'));

          setGroupedData((current) =>
            current.map((currentGroup, index) =>
              index === groupIndex
                ? {
                    ...currentGroup,
                    warnings: Array.from(
                      new Set([...currentGroup.warnings, message])
                    ),
                  }
                : currentGroup
            )
          );
          setPdfGroupProcessing((current) => ({
            ...current,
            [groupKey]: { status: 'error', error: message },
          }));
          setProcessingMessage(
            `${completedGroups}/${groupedPayloads.length} ${t('grupos leídos')}`
          );
          continue;
        }

        result.warnings?.forEach((warning) => {
          if (!warnings.includes(warning)) warnings.push(warning);
        });

        const extractedGroup = combineExtractedGroups(
          group,
          result.groups || []
        );

        setGroupedData((current) =>
          current.map((currentGroup, index) =>
            index === groupIndex ? extractedGroup : currentGroup
          )
        );
        setPdfGroupProcessing((current) => ({
          ...current,
          [groupKey]: { status: 'success' },
        }));
        successfulGroups += 1;
        setProcessingMessage(
          `${completedGroups}/${groupedPayloads.length} ${t('grupos leídos')}`
        );
      }

      setProcessWarnings(warnings);
      warnings.forEach((warning) => toast.warning(warning));

      if (successfulGroups === 0) {
        toast.error(t('No se pudo leer el documento'));
        return;
      }

      toast.success(t('Documento leído correctamente'));
    } catch (error) {
      console.error('Error processing multi-shipment document:', error);
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('No se pudo leer el documento')
      );
    } finally {
      setIsProcessing(false);
      setProcessingMessage('');
    }
  };

  const assignedPdfPages = useMemo(() => {
    const pages = new Set<number>();
    pdfGroups.forEach((group) => {
      group.pages.forEach((pageNumber) => pages.add(pageNumber));
    });
    return pages;
  }, [pdfGroups]);

  useEffect(() => {
    setSelectedPdfGroupId((current) => {
      if (current && pdfGroups.some((group) => group.id === current)) {
        return current;
      }
      return pdfGroups[pdfGroups.length - 1]?.id || '';
    });
  }, [pdfGroups]);

  const assignedPdfPageCount = assignedPdfPages.size;
  const unassignedPdfPageCount = Math.max(
    pdfPageCount - assignedPdfPageCount,
    0
  );

  const currentPdfThumbnail = pdfThumbnails.find(
    (thumbnail) => thumbnail.pageNumber === currentPdfPageNumber
  );
  const pdfSidebarThumbnail = pdfThumbnails.find(
    (thumbnail) => thumbnail.pageNumber === pdfSidebarPage
  );
  const currentPdfPageAssigned = assignedPdfPages.has(currentPdfPageNumber);

  const moveToNextUnassignedPdfPage = (fromPage: number) => {
    const nextPage = Array.from(
      { length: pdfPageCount },
      (_, index) => index + 1
    ).find(
      (pageNumber) => pageNumber > fromPage && !assignedPdfPages.has(pageNumber)
    );
    setCurrentPdfPageNumber(
      nextPage || Math.min(fromPage + 1, pdfPageCount || 1)
    );
  };

  const assignCurrentPageToNewGroup = () => {
    if (!currentPdfPageNumber || currentPdfPageAssigned) return;
    const pageNumber = currentPdfPageNumber;
    const groupId = createLocalId();
    setPdfGroups((current) => {
      const newGroup = {
        id: groupId,
        name: `${t('Grupo')} ${current.length + 1}`,
        pages: [pageNumber],
      };
      return [...current, newGroup];
    });
    setSelectedPdfGroupId(groupId);
    moveToNextUnassignedPdfPage(pageNumber);
  };

  const assignCurrentPageToGroup = (groupId: string) => {
    if (!currentPdfPageNumber || currentPdfPageAssigned) return;
    const pageNumber = currentPdfPageNumber;
    setPdfGroups((current) =>
      current.map((group) =>
        group.id === groupId
          ? {
              ...group,
              pages: [...group.pages, pageNumber].sort((a, b) => a - b),
            }
          : group
      )
    );
    moveToNextUnassignedPdfPage(pageNumber);
  };

  const handleManualAdd = (key: string | number | null) => {
    if (!key) return;
    setSelectedManualKeys((current) => {
      const next = new Set(current);
      next.add(String(key));
      return next;
    });
  };

  const handleManualRemove = async (key: string) => {
    if (
      Object.keys(groupAssignmentLocks).length > 0 &&
      !(await confirm({
        title: t('¿Reducir el alcance?'),
        message: t('Se liberarán las asignaciones de grupos existentes.'),
        confirmLabel: t('Continuar'),
      }))
    ) {
      return;
    }
    setGroupAssignmentLocks({});
    setSelectedManualKeys((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const handleShipGroupAdd = (key: string | number | null) => {
    if (!key) return;
    setSelectedShipGroupKeys((current) => {
      const next = new Set(current);
      next.add(String(key));
      return next;
    });
  };

  const handleShipGroupRemove = async (key: string) => {
    if (
      Object.keys(groupAssignmentLocks).length > 0 &&
      !(await confirm({
        title: t('¿Reducir el alcance?'),
        message: t('Se liberarán las asignaciones de grupos existentes.'),
        confirmLabel: t('Continuar'),
      }))
    ) {
      return;
    }
    setGroupAssignmentLocks({});
    setSelectedShipGroupKeys((current) => {
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  };

  const handlePastedShipmentTextChange = async (value: string) => {
    if (
      Object.keys(groupAssignmentLocks).length > 0 &&
      value !== pastedShipmentText &&
      !(await confirm({
        title: t('¿Cambiar el alcance?'),
        message: t('Se liberarán las asignaciones de grupos existentes.'),
        confirmLabel: t('Continuar'),
      }))
    ) {
      return;
    }
    if (value !== pastedShipmentText) setGroupAssignmentLocks({});
    setPastedShipmentText(value);
  };

  const handleSelectionModeChange = async (nextMode: SelectionMode) => {
    if (nextMode === selectionMode) return;
    if (
      hasShipmentPrefilter &&
      !(await confirm({
        title: t('¿Cambiar el modo?'),
        message: t('Se limpiará el alcance seleccionado.'),
        confirmLabel: t('Continuar'),
      }))
    ) {
      return;
    }
    setSelectedShipGroupKeys(new Set());
    setSelectedManualKeys(new Set());
    setGroupAssignmentLocks({});
    setPastedShipmentText('');
    setSelectionMode(nextMode);
  };

  const handleExpandShipmentScope = (keys?: string[]) => {
    if (selectionMode === 'ship_group') {
      const shipKeys = keys || outsideScopeShipGroups.map((group) => group.key);
      setSelectedShipGroupKeys((current) => new Set([...current, ...shipKeys]));
      return;
    }

    const shipmentKeys =
      keys || outsideScopeShipmentOptions.map((option) => option.key);
    setSelectedManualKeys((current) => new Set([...current, ...shipmentKeys]));
  };

  const handleRemovePdfGroup = (groupId: string) => {
    setPdfGroups((current) => current.filter((group) => group.id !== groupId));
  };

  const buildGroupedPdfPayload = async (
    sourceDocument: PDFDocument,
    group: PdfDocumentGroup
  ) => {
    const targetDocument = await PDFDocument.create();
    const pageIndexes = group.pages.map((pageNumber) => pageNumber - 1);
    const copiedPages = await targetDocument.copyPages(
      sourceDocument,
      pageIndexes
    );
    copiedPages.forEach((page) => targetDocument.addPage(page));
    const bytes = await targetDocument.save();
    return bytesToBase64(bytes);
  };

  const handleGroupStatusChange = (
    groupIndex: number,
    status: GroupReviewStatus
  ) => {
    setGroupedData((current) =>
      current.map((group, index) =>
        index === groupIndex ? { ...group, status } : group
      )
    );
  };

  const handleCostCategoryChange = async (
    groupIndex: number,
    costIndex: number,
    mappedCategory: SettlementCostCategory
  ) => {
    const selectedLine = groupedData[groupIndex]?.costLines[costIndex];
    if (!selectedLine || selectedLine.mappedCategory === mappedCategory) return;
    const normalizedConcept = normalizeReceiverCostConcept(
      selectedLine.description
    );

    if (selectedLine.mappingStatus === 'saved' && selectedLine.mappingId) {
      setSavingReceiverCostMappingId(selectedLine.mappingId);
      try {
        const nextMapping = await supersedeReceiverCostMapping(
          selectedLine.mappingId,
          mappedCategory
        );
        setReceiverCostMappings((current) => [
          ...current.filter(
            (mapping) =>
              mapping.normalizedConcept !== nextMapping.normalizedConcept
          ),
          nextMapping,
        ]);
        setGroupedData((current) =>
          current.map((group) => ({
            ...group,
            costLines: group.costLines.map((line) =>
              normalizeReceiverCostConcept(line.description) ===
              normalizedConcept
                ? {
                    ...line,
                    mappedCategory,
                    mappingStatus: 'saved',
                    mappingId: nextMapping.id,
                  }
                : line
            ),
          }))
        );
        toast.success(t('Mapeo actualizado para esta y futuras liquidaciones'));
      } catch (mappingError) {
        console.error('Error updating receiver cost mapping:', mappingError);
        toast.error(t('No se pudo actualizar el mapeo de costos'));
      } finally {
        setSavingReceiverCostMappingId('');
      }
      return;
    }

    setGroupedData((current) =>
      current.map((group) => {
        let changed = false;
        const nextCostLines = group.costLines.map((line) => {
          if (
            line.mappingStatus === 'saved' ||
            normalizeReceiverCostConcept(line.description) !== normalizedConcept
          ) {
            return line;
          }
          changed = true;
          return { ...line, mappedCategory };
        });
        if (!changed) return group;
        return {
          ...group,
          status: group.status === 'extracted' ? 'mapped' : group.status,
          costLines: nextCostLines,
        };
      })
    );
  };

  const handleSalesLineChange = (
    groupIndex: number,
    lineId: string,
    updates: Partial<PdfGroupSalesLine>
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    setGroupedData((current) =>
      current.map((item, index) =>
        index === groupIndex
          ? {
              ...item,
              status: item.status === 'extracted' ? 'mapped' : item.status,
              salesLines: item.salesLines.map((line) => {
                if (line.id !== lineId) return line;
                const next = { ...line, ...updates };
                return {
                  ...next,
                  kilos:
                    updates.quantity !== undefined ||
                    updates.boxWeight !== undefined
                      ? (Number(next.quantity) || 0) *
                        (Number(next.boxWeight) || 0)
                      : next.kilos,
                };
              }),
            }
          : item
      )
    );

    const mappingKey = getGroupKey(group, groupIndex);
    setGroupMappings((current) => {
      const mapping = current[mappingKey] || EMPTY_GROUP_MAPPING;
      const qualitySplits = { ...mapping.qualitySplits };
      const shipmentBoxQuantitiesByLine = {
        ...mapping.shipmentBoxQuantitiesByLine,
      };
      delete qualitySplits[lineId];
      if (updates.quantity !== undefined) {
        delete shipmentBoxQuantitiesByLine[lineId];
      }
      return {
        ...current,
        [mappingKey]: {
          ...mapping,
          qualitySplits,
          shipmentBoxQuantitiesByLine,
          confirmedLineAssignments: {
            ...mapping.confirmedLineAssignments,
            [lineId]: false,
          },
        },
      };
    });
  };

  const handleAddSalesLine = (groupIndex: number) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const id = createLocalId();
    setGroupedData((current) =>
      current.map((item, index) =>
        index === groupIndex
          ? {
              ...item,
              status: item.status === 'extracted' ? 'mapped' : item.status,
              salesLines: [
                ...item.salesLines,
                {
                  id,
                  rawText: 'Fila agregada manualmente',
                  variety: '',
                  caliber: '',
                  color: '',
                  label: '',
                  format: '',
                  quantity: 0,
                  boxWeight: 0,
                  kilos: 0,
                  unitPrice: 0,
                  amount: 0,
                  currency: item.header.currency || 'USD',
                },
              ],
            }
          : item
      )
    );
  };

  const handleDeleteSalesLine = (groupIndex: number, lineId: string) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    setGroupedData((current) =>
      current.map((item, index) =>
        index === groupIndex
          ? {
              ...item,
              status: item.status === 'extracted' ? 'mapped' : item.status,
              salesLines: item.salesLines.filter((line) => line.id !== lineId),
            }
          : item
      )
    );
    const mappingKey = getGroupKey(group, groupIndex);
    setGroupMappings((current) => {
      const mapping = current[mappingKey] || EMPTY_GROUP_MAPPING;
      const detailScopes = { ...mapping.detailScopes };
      const qualitySplits = { ...mapping.qualitySplits };
      const confirmedLineAssignments = {
        ...mapping.confirmedLineAssignments,
      };
      const shipmentBoxQuantitiesByLine = {
        ...mapping.shipmentBoxQuantitiesByLine,
      };
      delete detailScopes[lineId];
      delete qualitySplits[lineId];
      delete confirmedLineAssignments[lineId];
      delete shipmentBoxQuantitiesByLine[lineId];
      return {
        ...current,
        [mappingKey]: {
          ...mapping,
          detailScopes,
          qualitySplits,
          confirmedLineAssignments,
          shipmentBoxQuantitiesByLine,
        },
      };
    });
  };

  const handleCostLineChange = (
    groupIndex: number,
    lineId: string,
    updates: Partial<PdfGroupCostLine>
  ) => {
    setGroupedData((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              status: group.status === 'extracted' ? 'mapped' : group.status,
              costLines: group.costLines.map((line) => {
                if (line.id !== lineId) return line;
                const next = { ...line, ...updates };
                if (
                  updates.amount !== undefined &&
                  line.traceabilityLevel !== 'manual'
                ) {
                  const extractedAmount =
                    typeof line.sourceLocation?.extractedAmount === 'number'
                      ? line.sourceLocation.extractedAmount
                      : line.amount;
                  next.sourceLocation = {
                    ...(line.sourceLocation || {}),
                    extractedAmount,
                    amountEdited:
                      Math.abs(updates.amount - extractedAmount) > 0.000001,
                  };
                }
                if (updates.currency !== undefined) {
                  const reviewedCurrency = normalizeCostCurrency(
                    updates.currency
                  );
                  next.currency = reviewedCurrency;
                  if (line.traceabilityLevel !== 'manual') {
                    const extractedCurrency =
                      typeof line.sourceLocation?.extractedCurrency === 'string'
                        ? normalizeCostCurrency(
                            line.sourceLocation.extractedCurrency
                          )
                        : normalizeCostCurrency(line.currency);
                    next.sourceLocation = {
                      ...(next.sourceLocation || {}),
                      extractedCurrency,
                      currencyEdited: reviewedCurrency !== extractedCurrency,
                    };
                  }
                }
                if (updates.description !== undefined) {
                  if (line.traceabilityLevel !== 'manual') {
                    const sourceLocation = next.sourceLocation || {};
                    const extractedOriginalName =
                      typeof sourceLocation.extractedOriginalName === 'string'
                        ? sourceLocation.extractedOriginalName
                        : line.description;
                    next.sourceLocation = {
                      ...sourceLocation,
                      extractedOriginalName,
                      originalNameEdited:
                        updates.description.trim() !==
                        extractedOriginalName.trim(),
                    };
                  }
                  next.mappingStatus = 'new';
                  next.mappingId = undefined;
                }
                return next;
              }),
            }
          : group
      )
    );
  };

  const handleAddCostLine = (groupIndex: number) => {
    setGroupedData((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              status: group.status === 'extracted' ? 'mapped' : group.status,
              costLines: [
                ...group.costLines,
                {
                  id: createLocalId(),
                  description: 'Costo agregado manualmente',
                  amount: 0,
                  currency: group.header.currency || 'USD',
                  mappedCategory: 'other_costs',
                  suggestedCategory: 'other_costs',
                  mappingStatus: 'new',
                  confidence: 1,
                  sourceLocation: { manuallyAdded: true },
                  traceabilityLevel: 'manual',
                },
              ],
            }
          : group
      )
    );
  };

  const handleDeleteCostLine = (groupIndex: number, lineId: string) => {
    setGroupedData((current) =>
      current.map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              status: group.status === 'extracted' ? 'mapped' : group.status,
              costLines: group.costLines.filter((line) => line.id !== lineId),
            }
          : group
      )
    );
  };

  const handleGroupMappingChange = (
    groupIndex: number,
    updates: Partial<GroupMapping>
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);

    setGroupMappings((current) => ({
      ...current,
      [mappingKey]: {
        ...EMPTY_GROUP_MAPPING,
        ...(current[mappingKey] || {}),
        ...updates,
      },
    }));

    const hasMappingValue = Object.entries(updates).some(([, value]) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    );
    if (hasMappingValue && group.status === 'extracted') {
      handleGroupStatusChange(groupIndex, 'mapped');
    }
  };

  const getConfirmedAssignmentsForMapping = (
    group: PdfSettlementGroup,
    mapping: GroupMapping,
    groupKey: string
  ) => {
    const allocation = getGroupLineAllocation(
      getAvailableShipmentsForGroup(groupKey),
      group,
      mapping,
      mappedFormats
    );
    return Object.fromEntries(
      group.salesLines.map((line) => [
        line.id,
        allocation.matchedLineIds.has(line.id),
      ])
    );
  };

  const handleManualBoxAdjustment = (
    groupIndex: number,
    boxId: string,
    adjustment: 'include' | 'exclude' | 'clear'
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    if (groupAssignmentLocks[mappingKey]) {
      toast.warning(t('Libera la asignación antes de ajustar sus cajas'));
      return;
    }
    if (
      boxTransfers.some(
        (transfer) =>
          transfer.shipmentBoxId === boxId &&
          (transfer.fromGroupKey === mappingKey ||
            transfer.toGroupKey === mappingKey)
      )
    ) {
      toast.warning(
        t('Deshaz la transferencia antes de cambiar esta fila completa')
      );
      return;
    }

    const current = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[mappingKey] || {}),
    };
    const includedIds = new Set(current.manuallyIncludedBoxIds);
    const excludedIds = new Set(current.manuallyExcludedBoxIds);

    includedIds.delete(boxId);
    excludedIds.delete(boxId);
    if (adjustment === 'include') includedIds.add(boxId);
    if (adjustment === 'exclude') excludedIds.add(boxId);

    const nextMapping = {
      ...current,
      manuallyIncludedBoxIds: Array.from(includedIds),
      manuallyExcludedBoxIds: Array.from(excludedIds),
    };
    const confirmedLineAssignments = getConfirmedAssignmentsForMapping(
      group,
      nextMapping,
      mappingKey
    );

    handleGroupMappingChange(groupIndex, {
      manuallyIncludedBoxIds: nextMapping.manuallyIncludedBoxIds,
      manuallyExcludedBoxIds: nextMapping.manuallyExcludedBoxIds,
      confirmedLineAssignments,
    });
  };

  const handleTransferBoxes = (fromGroupIndex: number) => {
    const fromGroup = groupedData[fromGroupIndex];
    if (!fromGroup) return;
    const fromGroupKey = getGroupKey(fromGroup, fromGroupIndex);
    const draft = boxTransferDrafts[fromGroupKey];
    const toGroupIndex = groupedData.findIndex(
      (group, groupIndex) =>
        getGroupKey(group, groupIndex) === draft?.toGroupKey
    );
    const toGroup = groupedData[toGroupIndex];
    const quantity = Math.floor(Number(draft?.quantity) || 0);
    if (!draft?.shipmentBoxId || !toGroup || quantity <= 0) {
      toast.warning(t('Selecciona cajas, cantidad y grupo destino'));
      return;
    }
    if (
      groupAssignmentLocks[fromGroupKey] ||
      groupAssignmentLocks[draft.toGroupKey]
    ) {
      toast.warning(
        t('Libera ambos grupos antes de transferir cajas entre ellos')
      );
      return;
    }
    if (
      boxTransfers.some(
        (transfer) =>
          transfer.shipmentBoxId === draft.shipmentBoxId &&
          transfer.toGroupKey === fromGroupKey
      )
    ) {
      toast.warning(
        t(
          'Esta cantidad fue recibida desde otro grupo. Deshaz esa transferencia antes de volver a enviarla.'
        )
      );
      return;
    }

    const fromMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[fromGroupKey] || {}),
    };
    const toMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[draft.toGroupKey] || {}),
    };
    const sourceBox = getManuallyAdjustedGroupBoxes(
      getAvailableShipmentsForGroup(fromGroupKey),
      fromMapping,
      mappedFormats
    ).find((box) => String(box.id) === draft.shipmentBoxId);
    if (!sourceBox || quantity > (Number(sourceBox.quantity) || 0)) {
      toast.error(t('La cantidad supera los bultos disponibles en el grupo'));
      return;
    }

    const currentTargetQuantity = Object.prototype.hasOwnProperty.call(
      toMapping.manualBoxQuantities,
      draft.shipmentBoxId
    )
      ? Number(toMapping.manualBoxQuantities[draft.shipmentBoxId]) || 0
      : 0;
    const sourceQuantity = (Number(sourceBox.quantity) || 0) - quantity;
    const targetQuantity = currentTargetQuantity + quantity;
    const originalBox = effectiveShipments.find(
      (box) => String(box.id) === draft.shipmentBoxId
    );
    if (!originalBox || targetQuantity > (Number(originalBox.quantity) || 0)) {
      toast.error(t('La transferencia supera la cantidad original de la fila'));
      return;
    }

    const fromIncludedIds = fromMapping.manuallyIncludedBoxIds.filter(
      (boxId) => boxId !== draft.shipmentBoxId
    );
    const fromExcludedIds = fromMapping.manuallyExcludedBoxIds.filter(
      (boxId) => boxId !== draft.shipmentBoxId
    );
    const toIncludedIds = toMapping.manuallyIncludedBoxIds.filter(
      (boxId) => boxId !== draft.shipmentBoxId
    );
    const toExcludedIds = toMapping.manuallyExcludedBoxIds.filter(
      (boxId) => boxId !== draft.shipmentBoxId
    );
    const nextFromMapping = {
      ...fromMapping,
      manuallyIncludedBoxIds: fromIncludedIds,
      manuallyExcludedBoxIds: fromExcludedIds,
      manualBoxQuantities: {
        ...fromMapping.manualBoxQuantities,
        [draft.shipmentBoxId]: sourceQuantity,
      },
    };
    const nextToMapping = {
      ...toMapping,
      manuallyIncludedBoxIds: toIncludedIds,
      manuallyExcludedBoxIds: toExcludedIds,
      manualBoxQuantities: {
        ...toMapping.manualBoxQuantities,
        [draft.shipmentBoxId]: targetQuantity,
      },
    };
    nextFromMapping.confirmedLineAssignments =
      getConfirmedAssignmentsForMapping(
        fromGroup,
        nextFromMapping,
        fromGroupKey
      );
    nextToMapping.confirmedLineAssignments = getConfirmedAssignmentsForMapping(
      toGroup,
      nextToMapping,
      draft.toGroupKey
    );

    setBoxTransferBaselines((current) => {
      const next = { ...current };
      const fromBaselineKey = getBoxTransferBaselineKey(
        fromGroupKey,
        draft.shipmentBoxId
      );
      const toBaselineKey = getBoxTransferBaselineKey(
        draft.toGroupKey,
        draft.shipmentBoxId
      );
      if (!next[fromBaselineKey]) {
        next[fromBaselineKey] = getBoxAdjustmentSnapshot(
          fromMapping,
          draft.shipmentBoxId
        );
      }
      if (!next[toBaselineKey]) {
        next[toBaselineKey] = getBoxAdjustmentSnapshot(
          toMapping,
          draft.shipmentBoxId
        );
      }
      return next;
    });
    setGroupMappings((current) => ({
      ...current,
      [fromGroupKey]: nextFromMapping,
      [draft.toGroupKey]: nextToMapping,
    }));
    setBoxTransfers((current) => [
      ...current,
      {
        id: createLocalId(),
        shipmentBoxId: draft.shipmentBoxId,
        fromGroupKey,
        toGroupKey: draft.toGroupKey,
        quantity,
      },
    ]);
    setBoxTransferDrafts((current) => ({
      ...current,
      [fromGroupKey]: {
        shipmentBoxId: '',
        toGroupKey: draft.toGroupKey,
        quantity: '',
      },
    }));
    toast.success(
      `${formatNumber(quantity)} ${t('bultos enviados a')} ${t('Grupo')} ${toGroupIndex + 1}`
    );
  };

  const handleTransferSalesLineSurplus = (
    fromGroupIndex: number,
    sourceSalesLineId: string
  ) => {
    const fromGroup = groupedData[fromGroupIndex];
    if (!fromGroup) return;
    const fromGroupKey = getGroupKey(fromGroup, fromGroupIndex);
    const draftKey = `${fromGroupKey}:${sourceSalesLineId}`;
    const draft = salesLineTransferDrafts[draftKey];
    const toGroupIndex = groupedData.findIndex(
      (group, groupIndex) =>
        getGroupKey(group, groupIndex) === draft?.toGroupKey
    );
    const toGroup = groupedData[toGroupIndex];
    const quantity = Math.floor(Number(draft?.quantity) || 0);
    const sourceLine = fromGroup.salesLines.find(
      (line) => line.id === sourceSalesLineId
    );
    if (!sourceLine || !toGroup || quantity <= 0) {
      toast.warning(t('Indica cantidad y grupo destino'));
      return;
    }
    if (
      groupAssignmentLocks[fromGroupKey] ||
      groupAssignmentLocks[draft.toGroupKey]
    ) {
      toast.warning(
        t('Libera ambos grupos antes de transferir cajas entre ellos')
      );
      return;
    }

    const fromMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[fromGroupKey] || {}),
    };
    const toMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[draft.toGroupKey] || {}),
    };
    const matchedBoxes =
      getGroupLineAllocation(
        getAvailableShipmentsForGroup(fromGroupKey),
        fromGroup,
        fromMapping,
        mappedFormats
      ).boxesByLine[sourceSalesLineId] || [];
    const matchedQuantity = matchedBoxes.reduce(
      (total, box) => total + (Number(box.quantity) || 0),
      0
    );
    const surplus = Math.max(
      0,
      matchedQuantity - (Number(sourceLine.quantity) || 0)
    );
    if (quantity > surplus) {
      toast.error(
        `${t('Solo puedes transferir el excedente de la fila')}: ${formatNumber(surplus)} ${t('bultos')}`
      );
      return;
    }

    const transferableBoxes = matchedBoxes
      .filter(
        (box) =>
          !boxTransfers.some(
            (transfer) =>
              transfer.shipmentBoxId === String(box.id) &&
              transfer.toGroupKey === fromGroupKey
          )
      )
      .sort(
        (a, b) =>
          (Number(a.quantity) || 0) - (Number(b.quantity) || 0) ||
          getShipmentBoxAdjustmentLabel(a, mappedFormats).localeCompare(
            getShipmentBoxAdjustmentLabel(b, mappedFormats),
            'es'
          )
      );
    const nextFromQuantities = { ...fromMapping.manualBoxQuantities };
    const nextToQuantities = { ...toMapping.manualBoxQuantities };
    const adjustedFromBoxIds = new Set<string>();
    const adjustedToBoxIds = new Set<string>();
    const transfers: BoxTransfer[] = [];
    let remaining = quantity;

    for (const box of transferableBoxes) {
      if (remaining <= 0) break;
      const boxId = String(box.id);
      const originalBox = effectiveShipments.find(
        (candidate) => String(candidate.id) === boxId
      );
      if (!originalBox) continue;
      const sourceQuantity = Number(box.quantity) || 0;
      const targetQuantity = Object.prototype.hasOwnProperty.call(
        nextToQuantities,
        boxId
      )
        ? Number(nextToQuantities[boxId]) || 0
        : 0;
      const targetCapacity = Math.max(
        0,
        (Number(originalBox.quantity) || 0) - targetQuantity
      );
      const transferredQuantity = Math.min(
        remaining,
        sourceQuantity,
        targetCapacity
      );
      if (transferredQuantity <= 0) continue;

      nextFromQuantities[boxId] = sourceQuantity - transferredQuantity;
      nextToQuantities[boxId] = targetQuantity + transferredQuantity;
      adjustedFromBoxIds.add(boxId);
      adjustedToBoxIds.add(boxId);
      transfers.push({
        id: createLocalId(),
        shipmentBoxId: boxId,
        fromGroupKey,
        toGroupKey: draft.toGroupKey,
        quantity: transferredQuantity,
        sourceSalesLineId,
      });
      remaining -= transferredQuantity;
    }

    if (remaining > 0) {
      toast.error(
        t('No hay suficientes cajas transferibles dentro de esta fila')
      );
      return;
    }

    const nextFromMapping = {
      ...fromMapping,
      manuallyIncludedBoxIds: fromMapping.manuallyIncludedBoxIds.filter(
        (boxId) => !adjustedFromBoxIds.has(boxId)
      ),
      manuallyExcludedBoxIds: fromMapping.manuallyExcludedBoxIds.filter(
        (boxId) => !adjustedFromBoxIds.has(boxId)
      ),
      manualBoxQuantities: nextFromQuantities,
    };
    const nextToMapping = {
      ...toMapping,
      manuallyIncludedBoxIds: toMapping.manuallyIncludedBoxIds.filter(
        (boxId) => !adjustedToBoxIds.has(boxId)
      ),
      manuallyExcludedBoxIds: toMapping.manuallyExcludedBoxIds.filter(
        (boxId) => !adjustedToBoxIds.has(boxId)
      ),
      manualBoxQuantities: nextToQuantities,
    };
    nextFromMapping.confirmedLineAssignments =
      getConfirmedAssignmentsForMapping(
        fromGroup,
        nextFromMapping,
        fromGroupKey
      );
    nextToMapping.confirmedLineAssignments = getConfirmedAssignmentsForMapping(
      toGroup,
      nextToMapping,
      draft.toGroupKey
    );

    setBoxTransferBaselines((current) => {
      const next = { ...current };
      adjustedFromBoxIds.forEach((boxId) => {
        const baselineKey = getBoxTransferBaselineKey(fromGroupKey, boxId);
        if (!next[baselineKey]) {
          next[baselineKey] = getBoxAdjustmentSnapshot(fromMapping, boxId);
        }
      });
      adjustedToBoxIds.forEach((boxId) => {
        const baselineKey = getBoxTransferBaselineKey(draft.toGroupKey, boxId);
        if (!next[baselineKey]) {
          next[baselineKey] = getBoxAdjustmentSnapshot(toMapping, boxId);
        }
      });
      return next;
    });
    setGroupMappings((current) => ({
      ...current,
      [fromGroupKey]: nextFromMapping,
      [draft.toGroupKey]: nextToMapping,
    }));
    setBoxTransfers((current) => [...current, ...transfers]);
    setSalesLineTransferDrafts((current) => ({
      ...current,
      [draftKey]: {
        toGroupKey: draft.toGroupKey,
        quantity: '',
      },
    }));
    toast.success(
      `${formatNumber(quantity)} ${t('bultos enviados a')} ${t('Grupo')} ${toGroupIndex + 1}`
    );
  };

  const handleRemoveBoxTransfer = (transferId: string) => {
    const transfer = boxTransfers.find((item) => item.id === transferId);
    if (!transfer) return;
    if (
      groupAssignmentLocks[transfer.fromGroupKey] ||
      groupAssignmentLocks[transfer.toGroupKey]
    ) {
      toast.warning(
        t('Libera ambos grupos antes de deshacer la transferencia')
      );
      return;
    }
    const fromGroupIndex = groupedData.findIndex(
      (group, groupIndex) =>
        getGroupKey(group, groupIndex) === transfer.fromGroupKey
    );
    const toGroupIndex = groupedData.findIndex(
      (group, groupIndex) =>
        getGroupKey(group, groupIndex) === transfer.toGroupKey
    );
    const fromGroup = groupedData[fromGroupIndex];
    const toGroup = groupedData[toGroupIndex];
    if (!fromGroup || !toGroup) return;

    const fromMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[transfer.fromGroupKey] || {}),
    };
    const toMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...(groupMappings[transfer.toGroupKey] || {}),
    };
    const remainingTransfers = boxTransfers.filter(
      (item) => item.id !== transfer.id
    );
    const nextFromQuantities = { ...fromMapping.manualBoxQuantities };
    const nextToQuantities = { ...toMapping.manualBoxQuantities };
    const fromStillAdjusted = remainingTransfers.some(
      (item) =>
        item.shipmentBoxId === transfer.shipmentBoxId &&
        (item.fromGroupKey === transfer.fromGroupKey ||
          item.toGroupKey === transfer.fromGroupKey)
    );
    const toStillAdjusted = remainingTransfers.some(
      (item) =>
        item.shipmentBoxId === transfer.shipmentBoxId &&
        (item.fromGroupKey === transfer.toGroupKey ||
          item.toGroupKey === transfer.toGroupKey)
    );
    const fromBaselineKey = getBoxTransferBaselineKey(
      transfer.fromGroupKey,
      transfer.shipmentBoxId
    );
    const toBaselineKey = getBoxTransferBaselineKey(
      transfer.toGroupKey,
      transfer.shipmentBoxId
    );
    const fromBaseline = boxTransferBaselines[fromBaselineKey];
    const toBaseline = boxTransferBaselines[toBaselineKey];

    if (fromStillAdjusted) {
      nextFromQuantities[transfer.shipmentBoxId] =
        (Number(nextFromQuantities[transfer.shipmentBoxId]) || 0) +
        transfer.quantity;
    } else if (fromBaseline?.hadManualQuantity) {
      nextFromQuantities[transfer.shipmentBoxId] =
        fromBaseline.manualQuantity || 0;
    } else {
      delete nextFromQuantities[transfer.shipmentBoxId];
    }
    if (toStillAdjusted) {
      nextToQuantities[transfer.shipmentBoxId] = Math.max(
        0,
        (Number(nextToQuantities[transfer.shipmentBoxId]) || 0) -
          transfer.quantity
      );
    } else if (toBaseline?.hadManualQuantity) {
      nextToQuantities[transfer.shipmentBoxId] = toBaseline.manualQuantity || 0;
    } else {
      delete nextToQuantities[transfer.shipmentBoxId];
    }

    const restoreMembership = (
      boxIds: string[],
      shouldInclude: boolean,
      shouldRestore: boolean
    ) => {
      if (!shouldRestore) return boxIds;
      const withoutBox = boxIds.filter(
        (boxId) => boxId !== transfer.shipmentBoxId
      );
      return shouldInclude
        ? [...withoutBox, transfer.shipmentBoxId]
        : withoutBox;
    };
    const nextFromMapping = {
      ...fromMapping,
      manuallyIncludedBoxIds: restoreMembership(
        fromMapping.manuallyIncludedBoxIds,
        fromBaseline?.wasIncluded || false,
        !fromStillAdjusted && Boolean(fromBaseline)
      ),
      manuallyExcludedBoxIds: restoreMembership(
        fromMapping.manuallyExcludedBoxIds,
        fromBaseline?.wasExcluded || false,
        !fromStillAdjusted && Boolean(fromBaseline)
      ),
      manualBoxQuantities: nextFromQuantities,
    };
    const nextToMapping = {
      ...toMapping,
      manuallyIncludedBoxIds: restoreMembership(
        toMapping.manuallyIncludedBoxIds,
        toBaseline?.wasIncluded || false,
        !toStillAdjusted && Boolean(toBaseline)
      ),
      manuallyExcludedBoxIds: restoreMembership(
        toMapping.manuallyExcludedBoxIds,
        toBaseline?.wasExcluded || false,
        !toStillAdjusted && Boolean(toBaseline)
      ),
      manualBoxQuantities: nextToQuantities,
    };
    nextFromMapping.confirmedLineAssignments =
      getConfirmedAssignmentsForMapping(
        fromGroup,
        nextFromMapping,
        transfer.fromGroupKey
      );
    nextToMapping.confirmedLineAssignments = getConfirmedAssignmentsForMapping(
      toGroup,
      nextToMapping,
      transfer.toGroupKey
    );

    setGroupMappings((current) => ({
      ...current,
      [transfer.fromGroupKey]: nextFromMapping,
      [transfer.toGroupKey]: nextToMapping,
    }));
    setBoxTransfers(remainingTransfers);
    setBoxTransferBaselines((current) => {
      const next = { ...current };
      if (!fromStillAdjusted) delete next[fromBaselineKey];
      if (!toStillAdjusted) delete next[toBaselineKey];
      return next;
    });
  };

  const handleGroupScopeChange = (
    groupIndex: number,
    updates: Partial<ShipmentBoxScope>
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const current = groupMappings[mappingKey] || EMPTY_GROUP_MAPPING;
    const nextScope = {
      ...current.scope,
      ...updates,
    };
    handleGroupMappingChange(groupIndex, {
      scope: nextScope,
      shipmentBoxQuantitiesByLine: {},
      confirmedLineAssignments: {},
    });
  };

  const handleDetailScopeChange = (
    groupIndex: number,
    lineId: string,
    updates: Partial<DetailScope>
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const current = groupMappings[mappingKey] || EMPTY_GROUP_MAPPING;
    const currentDetailScope = current.detailScopes[lineId] || {
      scope: emptyShipmentBoxScope(),
      activeFields: [],
    };

    const nextDetailScope = {
      ...currentDetailScope,
      ...updates,
      scope: {
        ...currentDetailScope.scope,
        ...(updates.scope || {}),
      },
    };
    const line = group.salesLines.find((salesLine) => salesLine.id === lineId);
    const shipmentBoxQuantitiesByLine = {
      ...current.shipmentBoxQuantitiesByLine,
    };
    delete shipmentBoxQuantitiesByLine[lineId];
    const nextMapping = {
      ...EMPTY_GROUP_MAPPING,
      ...current,
      detailScopes: {
        ...current.detailScopes,
        [lineId]: nextDetailScope,
      },
      shipmentBoxQuantitiesByLine,
    };

    handleGroupMappingChange(groupIndex, {
      detailScopes: nextMapping.detailScopes,
      shipmentBoxQuantitiesByLine,
      confirmedLineAssignments: line
        ? {
            ...current.confirmedLineAssignments,
            [lineId]: false,
          }
        : current.confirmedLineAssignments,
    });
  };

  const handleAddDetailFilter = (
    groupIndex: number,
    field: keyof ShipmentBoxScope
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const current = groupMappings[mappingKey] || EMPTY_GROUP_MAPPING;
    const detailScopes = { ...current.detailScopes };

    group.salesLines.forEach((salesLine) => {
      const detailScope = detailScopes[salesLine.id] || {
        scope: emptyShipmentBoxScope(),
        activeFields: [],
      };
      detailScopes[salesLine.id] = {
        ...detailScope,
        activeFields: detailScope.activeFields.includes(field)
          ? detailScope.activeFields
          : [...detailScope.activeFields, field],
        scope: {
          ...detailScope.scope,
          [field]: detailScope.scope[field] || [],
        },
      };
    });

    handleGroupMappingChange(groupIndex, {
      detailScopes,
      confirmedLineAssignments: current.confirmedLineAssignments,
    });
  };

  const handleRemoveDetailFilter = (
    groupIndex: number,
    lineId: string,
    field: keyof ShipmentBoxScope
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const detailScope = groupMappings[mappingKey]?.detailScopes[lineId] || {
      scope: emptyShipmentBoxScope(),
      activeFields: [],
    };
    handleDetailScopeChange(groupIndex, lineId, {
      activeFields: detailScope.activeFields.filter((item) => item !== field),
      scope: { ...detailScope.scope, [field]: [] },
    });
  };

  const handleQualitySplit = (
    groupIndex: number,
    line: PdfGroupSalesLine,
    matchedBoxes: ShipmentRecord[]
  ) => {
    const group = groupedData[groupIndex];
    if (!group || matchedBoxes.length === 0) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const qualityGroups = new Map<
      string,
      {
        variety: string;
        caliber: string;
        color: string;
        category: string;
        label: string;
        format: string;
        quantity: number;
        kilos: number;
        references: Set<string>;
      }
    >();

    matchedBoxes.forEach((box) => {
      const variety = getSettlementDetailVariety(box);
      const caliber = box.caliber || '';
      const color = box.color || '';
      const category = box.category || '';
      const label = box.box_label || '';
      const format = getShipmentFormat(box, mappedFormats);
      const key = [variety, caliber, color, category, label, format].join('|');
      const current = qualityGroups.get(key) || {
        variety,
        caliber,
        color,
        category,
        label,
        format,
        quantity: 0,
        kilos: 0,
        references: new Set<string>(),
      };
      current.quantity += Number(box.quantity) || 0;
      current.kilos +=
        (Number(box.quantity) || 0) * (Number(box.box_weight) || 0);
      if (box.reference_number) current.references.add(box.reference_number);
      qualityGroups.set(key, current);
    });

    const totalKilos = Array.from(qualityGroups.values()).reduce(
      (total, split) => total + split.kilos,
      0
    );
    const splits = Array.from(qualityGroups.values()).map((split) => ({
      id: createLocalId(),
      variety: split.variety,
      caliber: split.caliber,
      color: split.color,
      category: split.category,
      label: split.label,
      format: split.format,
      quantity: split.quantity,
      kilos: split.kilos,
      amount: totalKilos > 0 ? line.amount * (split.kilos / totalKilos) : 0,
      references: Array.from(split.references).sort(),
    }));

    handleGroupMappingChange(groupIndex, {
      qualitySplits: {
        ...(groupMappings[mappingKey]?.qualitySplits || {}),
        [line.id]: splits,
      },
      confirmedLineAssignments: {
        ...(groupMappings[mappingKey]?.confirmedLineAssignments || {}),
        [line.id]: false,
      },
    });
  };

  const handleClearQualitySplit = (groupIndex: number, lineId: string) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    const currentSplits = {
      ...(groupMappings[mappingKey]?.qualitySplits || {}),
    };
    delete currentSplits[lineId];
    handleGroupMappingChange(groupIndex, {
      qualitySplits: currentSplits,
      confirmedLineAssignments: {
        ...(groupMappings[mappingKey]?.confirmedLineAssignments || {}),
        [lineId]: false,
      },
    });
  };

  const handleConfirmLineAssignment = (
    groupIndex: number,
    lineId: string,
    confirmed: boolean
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const mappingKey = getGroupKey(group, groupIndex);
    handleGroupMappingChange(groupIndex, {
      confirmedLineAssignments: {
        ...(groupMappings[mappingKey]?.confirmedLineAssignments || {}),
        [lineId]: confirmed,
      },
    });
  };

  const handleReserveSalesLineQuantity = (
    groupIndex: number,
    lineId: string,
    candidateBoxes: ShipmentRecord[]
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const groupKey = getGroupKey(group, groupIndex);
    if (groupAssignmentLocks[groupKey]) {
      toast.warning(t('Libera la asignación del grupo antes de reasignar'));
      return;
    }

    const line = group.salesLines.find((salesLine) => salesLine.id === lineId);
    if (!line) return;
    const requiredQuantity = Number(line.quantity) || 0;
    const mapping = groupMappings[groupKey] || EMPTY_GROUP_MAPPING;
    const availableQuantity = candidateBoxes.reduce(
      (total, box) => total + (Number(box.quantity) || 0),
      0
    );
    if (requiredQuantity <= 0 || availableQuantity <= requiredQuantity) {
      toast.warning(t('No hay excedente disponible para liberar'));
      return;
    }

    const reservedQuantities: Record<string, number> = {};
    let remainingQuantity = requiredQuantity;
    for (const box of candidateBoxes) {
      if (remainingQuantity <= 0) break;
      const quantity = Math.min(remainingQuantity, Number(box.quantity) || 0);
      if (quantity <= 0) continue;
      reservedQuantities[String(box.id)] = quantity;
      remainingQuantity -= quantity;
    }
    if (remainingQuantity > 0) {
      toast.error(t('No se pudo completar la asignación de la fila'));
      return;
    }

    const shipmentBoxQuantitiesByLine = {
      ...mapping.shipmentBoxQuantitiesByLine,
      [lineId]: reservedQuantities,
    };
    handleGroupMappingChange(groupIndex, {
      shipmentBoxQuantitiesByLine,
      confirmedLineAssignments: mapping.confirmedLineAssignments,
    });
    toast.success(
      `${formatNumber(requiredQuantity)} ${t('bultos asignados')}; ${formatNumber(
        availableQuantity - requiredQuantity
      )} ${t('disponibles para otras filas del grupo')}`
    );
  };

  const handleReleaseSalesLineQuantity = (
    groupIndex: number,
    lineId: string
  ) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const groupKey = getGroupKey(group, groupIndex);
    if (groupAssignmentLocks[groupKey]) {
      toast.warning(t('Libera la asignación del grupo antes de reasignar'));
      return;
    }

    const mapping = groupMappings[groupKey] || EMPTY_GROUP_MAPPING;
    const shipmentBoxQuantitiesByLine = {
      ...mapping.shipmentBoxQuantitiesByLine,
    };
    delete shipmentBoxQuantitiesByLine[lineId];
    handleGroupMappingChange(groupIndex, {
      shipmentBoxQuantitiesByLine,
      confirmedLineAssignments: mapping.confirmedLineAssignments,
    });
    toast.info(t('Asignación de la fila liberada'));
  };

  const getGroupAssignmentCandidate = (
    groupIndex: number,
    existingAllocation?: GroupLineAllocation
  ) => {
    const group = groupedData[groupIndex];
    if (!group) {
      return {
        canAssign: false,
        reason: t('Grupo no encontrado'),
        shipmentBoxIdsByLine: {} as Record<string, string[]>,
        shipmentBoxQuantitiesByLine: {} as Record<
          string,
          Record<string, number>
        >,
        unmappedBoxType: '',
      };
    }

    const groupKey = getGroupKey(group, groupIndex);
    const mapping = groupMappings[groupKey] || EMPTY_GROUP_MAPPING;
    const availableBoxes = getAvailableShipmentsForGroup(groupKey);
    const shipmentBoxIdsByLine: Record<string, string[]> = {};
    const shipmentBoxQuantitiesByLine: Record<
      string,
      Record<string, number>
    > = {};

    if (group.salesLines.length === 0) {
      return {
        canAssign: false,
        reason: t('El grupo no tiene filas de venta'),
        shipmentBoxIdsByLine,
        shipmentBoxQuantitiesByLine,
        unmappedBoxType: '',
      };
    }

    const allocation =
      existingAllocation ||
      getGroupLineAllocation(availableBoxes, group, mapping, mappedFormats);
    for (const line of group.salesLines) {
      if (!allocation.matchedLineIds.has(line.id)) {
        return {
          canAssign: false,
          reason: t('Confirma todas las filas de venta'),
          shipmentBoxIdsByLine,
          shipmentBoxQuantitiesByLine,
          unmappedBoxType: '',
        };
      }

      const lineBoxes = allocation.boxesByLine[line.id] || [];
      const quantity = lineBoxes.reduce(
        (total, box) => total + (Number(box.quantity) || 0),
        0
      );

      if (quantity <= 0 || quantity !== Number(line.quantity || 0)) {
        return {
          canAssign: false,
          reason: `${t('Los bultos no cuadran para')} ${line.caliber || line.variety || t('una fila')}`,
          shipmentBoxIdsByLine,
          shipmentBoxQuantitiesByLine,
          unmappedBoxType: '',
        };
      }

      const unmappedBox = lineBoxes.find(
        (box) =>
          normalizeText(box.box_type) &&
          !getMappedShipmentPackageType(box, mappedFormats)
      );
      if (unmappedBox) {
        return {
          canAssign: false,
          reason: `${t('Mapea el tipo de caja')} ${unmappedBox.box_type}`,
          shipmentBoxIdsByLine,
          shipmentBoxQuantitiesByLine,
          unmappedBoxType: normalizeText(unmappedBox.box_type),
        };
      }

      const lineBoxIds = lineBoxes.map((box) => String(box.id));
      shipmentBoxIdsByLine[line.id] = lineBoxIds;
      shipmentBoxQuantitiesByLine[line.id] = Object.fromEntries(
        lineBoxes.map((box) => [String(box.id), Number(box.quantity) || 0])
      );
    }

    return {
      canAssign: true,
      reason: '',
      shipmentBoxIdsByLine,
      shipmentBoxQuantitiesByLine,
      unmappedBoxType: '',
    };
  };

  const handleAssignGroup = (groupIndex: number) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const groupKey = getGroupKey(group, groupIndex);
    const candidate = getGroupAssignmentCandidate(groupIndex);
    if (!candidate.canAssign) {
      toast.error(candidate.reason);
      return;
    }

    setGroupAssignmentLocks((current) => ({
      ...current,
      [groupKey]: {
        shipmentBoxIdsByLine: candidate.shipmentBoxIdsByLine,
        shipmentBoxQuantitiesByLine: candidate.shipmentBoxQuantitiesByLine,
        assignedAt: new Date().toISOString(),
      },
    }));
    setEditingSalesGroupKey((current) => (current === groupKey ? '' : current));
    toast.success(`${group.title}: ${t('cajas asignadas')}`);
  };

  const handleReleaseGroupAssignment = (groupIndex: number) => {
    const group = groupedData[groupIndex];
    if (!group) return;
    const groupKey = getGroupKey(group, groupIndex);
    setGroupAssignmentLocks((current) => {
      const next = { ...current };
      delete next[groupKey];
      return next;
    });
  };

  useEffect(() => {
    if (groupedData.length === 0) return;

    setGroupMappings((currentMappings) => {
      let changed = false;
      const nextMappings = { ...currentMappings };

      groupedData.forEach((group, groupIndex) => {
        const groupKey = getGroupKey(group, groupIndex);
        if (groupAssignmentLocks[groupKey]) return;

        const mapping = currentMappings[groupKey] || EMPTY_GROUP_MAPPING;
        const availableBoxes = getAvailableShipmentsForGroup(groupKey);
        const allocation = getGroupLineAllocation(
          availableBoxes,
          group,
          mapping,
          mappedFormats
        );
        const confirmedLineAssignments = Object.fromEntries(
          group.salesLines.map((line) => [
            line.id,
            allocation.matchedLineIds.has(line.id),
          ])
        );
        const lineChanged = group.salesLines.some(
          (line) =>
            Boolean(mapping.confirmedLineAssignments[line.id]) !==
            Boolean(confirmedLineAssignments[line.id])
        );
        if (!lineChanged) return;

        changed = true;
        nextMappings[groupKey] = {
          ...EMPTY_GROUP_MAPPING,
          ...mapping,
          confirmedLineAssignments,
        };
      });

      return changed ? nextMappings : currentMappings;
    });
  }, [
    effectiveShipments,
    getAvailableShipmentsForGroup,
    groupAssignmentLocks,
    groupedData,
    mappedFormats,
    reservedBoxQuantities,
  ]);

  const showSettlementActionErrors = (errors: string[]) => {
    const uniqueErrors = Array.from(new Set(errors.filter(Boolean)));
    setSettlementActionErrors(uniqueErrors);
    uniqueErrors.forEach((error) => toast.error(error));
  };

  const handleCreateSettlements = async () => {
    setSettlementActionErrors([]);
    if (!selectedFile) {
      showSettlementActionErrors([
        t('Selecciona un archivo antes de crear las liquidaciones'),
      ]);
      return;
    }
    if (!selectedReceiver) {
      showSettlementActionErrors([
        t('Selecciona un cliente antes de crear las liquidaciones'),
      ]);
      return;
    }
    if (!userProfile?.exporter_id) {
      showSettlementActionErrors([t('No se pudo identificar el exportador')]);
      return;
    }
    if (!selectedSeason || !selectedSpecies?.id) {
      showSettlementActionErrors([
        t(
          'Selecciona una temporada y especie antes de crear las liquidaciones'
        ),
      ]);
      return;
    }
    if (!receiverCostMappingsLoaded) {
      showSettlementActionErrors([
        t('Espera a que terminen de cargar los mapeos de costos'),
      ]);
      return;
    }
    const negativeCost = groupedData
      .flatMap((group) => group.costLines)
      .find((line) => Number(line.amount) < 0);
    if (negativeCost) {
      showSettlementActionErrors([
        `${t('V1 no admite costos negativos')}: ${negativeCost.description}`,
      ]);
      return;
    }
    const unnamedCost = groupedData
      .flatMap((group) => group.costLines)
      .find((line) => !line.description.trim());
    if (unnamedCost) {
      showSettlementActionErrors([
        t('Todas las líneas de costo deben tener un concepto'),
      ]);
      return;
    }
    if (derivedSettlementResult.errors.length > 0) {
      showSettlementActionErrors(derivedSettlementResult.errors);
      return;
    }
    if (derivedSettlementResult.settlements.length === 0) {
      showSettlementActionErrors([
        t('No hay liquidaciones derivadas para crear'),
      ]);
      return;
    }
    const incompleteHeader = derivedSettlementResult.settlements.find(
      (settlement) => {
        const header = settlementHeaders[settlement.referenceNumber];
        const hasIncompleteTransport =
          !header?.transportType ||
          (header.transportType === 'M'
            ? !header.shipTripId
            : !header.shipEtd ||
              !header.shipEta ||
              !header.destinationMarketId);
        return (
          hasIncompleteTransport ||
          !header.marketId ||
          !header.dateSold ||
          !header.currency ||
          Number(header.exchangeRate) <= 0
        );
      }
    );
    if (incompleteHeader) {
      showSettlementActionErrors([
        `${t('Completa los datos de liquidación para')}: ${incompleteHeader.referenceNumber}`,
      ]);
      return;
    }
    const invalidCurrencyContext = derivedSettlementResult.settlements
      .map((settlement) => {
        const header = settlementHeaders[settlement.referenceNumber];
        const localCurrency = normalizeCostCurrency(header?.currency);
        const groupIndexes = new Set(
          settlement.details.map((detail) => detail.sourceGroupIndex)
        );
        const invalidLine = Array.from(groupIndexes)
          .flatMap((groupIndex) => groupedData[groupIndex]?.costLines || [])
          .find((line) => {
            const currency = normalizeCostCurrency(line.currency);
            return currency !== 'USD' && currency !== localCurrency;
          });
        return invalidLine
          ? {
              referenceNumber: settlement.referenceNumber,
              line: invalidLine,
              localCurrency,
            }
          : null;
      })
      .find(Boolean);
    if (invalidCurrencyContext) {
      showSettlementActionErrors([
        `${invalidCurrencyContext.line.description}: ${t(
          'la moneda debe ser USD o'
        )} ${invalidCurrencyContext.localCurrency} (${invalidCurrencyContext.referenceNumber})`,
      ]);
      return;
    }

    setIsApplyingSettlements(true);
    let sourceDocumentId: string | null = retrySourceDocumentId || null;
    try {
      // This preflight gives immediate feedback and avoids uploading the source
      // document when the canonical settlement key is already present. The RPC
      // and database unique index remain authoritative for concurrent requests.
      const targetReferences = new Set(
        derivedSettlementResult.settlements.map((settlement) =>
          settlement.referenceNumber.trim()
        )
      );
      // With several divisions and no explicit choice, the RPC resolves each
      // reference's division and checks duplicates there.
      let existingQuery = supabase
        .from('settlements')
        .select('reference_number')
        .eq('exporter_id', userProfile.exporter_id)
        .eq('season', selectedSeason)
        .eq('species_id', selectedSpecies.id);
      if (selectedDivisionId) {
        existingQuery = existingQuery.eq(
          'division_id',
          Number(selectedDivisionId)
        );
      } else if (divisions.length > 1) {
        existingQuery = existingQuery.limit(0);
      }
      const { data: existingSettlements, error: existingSettlementsError } =
        await existingQuery;

      if (existingSettlementsError) throw existingSettlementsError;

      const duplicateReferences = Array.from(
        new Set(
          (existingSettlements || [])
            .map((settlement) => settlement.reference_number?.trim())
            .filter(
              (reference): reference is string =>
                Boolean(reference) && targetReferences.has(reference)
            )
        )
      ).sort();

      if (duplicateReferences.length > 0) {
        showSettlementActionErrors([
          `${t(
            'Ya existe una liquidación para esta temporada y especie'
          )}: ${duplicateReferences.join(', ')}`,
        ]);
        return;
      }

      const resolvedManualTripIds: Record<string, string> = {};
      for (const settlement of derivedSettlementResult.settlements) {
        const header = settlementHeaders[settlement.referenceNumber];
        if (header.transportType !== 'A' && header.transportType !== 'T') {
          continue;
        }

        const trip = await findOrCreateAirShipTrip(
          header.shipEtd,
          header.shipEta,
          header.destinationMarketId,
          header.transportType
        );
        if (!trip) {
          throw new Error(
            `${t('No se pudo crear o asociar el transporte para')}: ${settlement.referenceNumber}`
          );
        }
        resolvedManualTripIds[settlement.referenceNumber] = String(trip.id);
      }

      if (inboxClaim) {
        const prepared = await inboxRequest<{ sourceDocumentId: string }>(
          'prepare',
          {
            id: inboxClaim.id,
            token: inboxClaim.token,
            receiverId: selectedReceiver.id,
          }
        );
        sourceDocumentId = prepared.sourceDocumentId;
        setRetrySourceDocumentId(sourceDocumentId);
      } else if (!sourceDocumentId) {
        const sourceDocument = await uploadOriginalSettlementDocument({
          file: selectedFile,
          exporterId: userProfile.exporter_id,
          exporterReceiverId: selectedReceiver.id,
          userId: userProfile.id,
          sourceFlow: 'multi_shipment',
        });
        sourceDocumentId = sourceDocument.id;
        setRetrySourceDocumentId(sourceDocumentId);
      }

      const groupsPayload = groupedData.map((group, groupIndex) => {
        const groupKey = getGroupKey(group, groupIndex);
        const mapping = groupMappings[groupKey] || EMPTY_GROUP_MAPPING;
        return {
          groupIndex,
          clientGroupName: group.title || group.groupId,
          pages: group.pages,
          extractedHeader: group.header,
          extractedTotals: group.totals,
          scopeFilter: {
            ...mapping.scope,
            manuallyIncludedBoxIds: mapping.manuallyIncludedBoxIds || [],
            manuallyExcludedBoxIds: mapping.manuallyExcludedBoxIds || [],
            manualBoxQuantities: mapping.manualBoxQuantities || {},
            boxTransfers: boxTransfers.filter(
              (transfer) =>
                transfer.fromGroupKey === groupKey ||
                transfer.toGroupKey === groupKey
            ),
          },
          reviewNotes: mapping.notes || null,
          lines: [
            ...group.salesLines.map((line) => ({
              lineType: 'sale',
              sourceLineId: line.id,
              rawText: line.rawText,
              qualitativeData: {
                variety: line.variety,
                caliber: line.caliber,
                color: line.color,
                label: line.label,
                format: line.format,
              },
              quantitativeData: {
                quantity: line.quantity,
                kilos: line.kilos,
                unitPrice: line.unitPrice,
                amount: line.amount,
                currency: line.currency,
              },
              detailScopeFilter: mapping.detailScopes[line.id]?.scope || {},
            })),
            ...group.costLines.map((line) => ({
              lineType: 'cost',
              sourceLineId: line.id,
              rawText: line.description,
              qualitativeData: {
                mappedCategory: line.mappedCategory,
                suggestedCategory:
                  line.suggestedCategory || line.mappedCategory,
                confidence: line.confidence,
                traceabilityLevel: line.traceabilityLevel || 'line_item',
                sourceLocation: {
                  ...(line.sourceLocation || {}),
                  pages: group.pages,
                  sourceLineId: line.id,
                },
              },
              quantitativeData: {
                amount: line.amount,
                currency: line.currency,
              },
              detailScopeFilter: {},
            })),
          ],
        };
      });
      const settlementsPayload = derivedSettlementResult.settlements.map(
        (settlement) => {
          const header = settlementHeaders[settlement.referenceNumber];
          const exchangeRate = Number(header.exchangeRate) || 1;
          const localCurrency = normalizeCostCurrency(header.currency) || 'USD';
          const detailsInLocalCurrency = convertDerivedDetailsToLocalCurrency(
            settlement.details,
            groupedData,
            localCurrency,
            exchangeRate
          );
          const totals = detailsInLocalCurrency.reduce(
            (sum, detail) => ({
              sales: sum.sales + detail.sales,
              tax: sum.tax + detail.tax,
              commission: sum.commission + detail.commission,
              otherCosts: sum.otherCosts + detail.otherCosts,
              repacking: sum.repacking + detail.repacking,
              freight: sum.freight + detail.freight,
            }),
            {
              sales: 0,
              tax: 0,
              commission: 0,
              otherCosts: 0,
              repacking: 0,
              freight: 0,
            }
          );

          return {
            referenceNumber: settlement.referenceNumber,
            containerNumber: settlement.containerNumber,
            header: {
              ...header,
              ...(resolvedManualTripIds[settlement.referenceNumber]
                ? {
                    // A/T is resolved before the transactional import. Omitting
                    // transportType activates the RPC's backwards-compatible
                    // shipTripId path in both old and new deployments.
                    transportType: undefined,
                    shipTripId:
                      resolvedManualTripIds[settlement.referenceNumber],
                  }
                : {}),
              dateSold: toSettlementDate(header.dateSold),
              currency: localCurrency,
              exchangeRate,
            },
            totals,
            details: detailsInLocalCurrency.map((detail) => ({
              sourceGroupIndex: detail.sourceGroupIndex,
              sourceLineId: detail.sourceLineId,
              variety: detail.variety,
              caliber: detail.caliber,
              color: detail.color,
              category: detail.category,
              label: detail.label,
              format: detail.format,
              unitWeight: detail.unitWeight,
              quantity: detail.quantity,
              kilos: detail.kilos,
              sales: detail.sales,
              tax: detail.tax,
              commission: detail.commission,
              freight: detail.freight,
              repacking: detail.repacking,
              otherCosts: detail.otherCosts,
              shipmentBoxes: detail.shipmentBoxes.map((box) => ({
                id: String(box.id),
                quantity: Number(box.quantity) || 0,
                boxWeight: Number(box.box_weight) || 0,
              })),
            })),
          };
        }
      );
      const { error: transactionError } = await supabase.rpc(
        'create_multi_shipment_settlements',
        {
          p_payload: {
            sourceDocumentId,
            ...(inboxClaim
              ? {
                  inboxAttachmentId: inboxClaim.id,
                  inboxClaimToken: inboxClaim.token,
                }
              : {}),
            exporterId: userProfile.exporter_id,
            exporterReceiverId: selectedReceiver.id,
            receiverName: selectedReceiver.name,
            season: selectedSeason || null,
            speciesId: selectedSpecies?.id || null,
            divisionId: selectedDivisionId ? Number(selectedDivisionId) : null,
            groups: groupsPayload,
            settlements: settlementsPayload,
          },
        }
      );
      if (transactionError) throw new Error(transactionError.message);

      toast.success(t('Liquidaciones por referencia creadas correctamente'));
      try {
        await onSuccess?.();
      } catch (refreshError) {
        console.error(
          'Error refreshing settlements after multi-shipment upload:',
          refreshError
        );
      }
      handleClose();
    } catch (error) {
      console.error('Error creating multi-shipment settlements:', error);
      if (sourceDocumentId && !inboxClaim) {
        await supabase
          .from('source_documents')
          .update({ status: 'failed' })
          .eq('id', sourceDocumentId)
          .neq('status', 'applied');
      }
      showSettlementActionErrors([
        getErrorMessage(error, t('No se pudieron crear las liquidaciones')),
      ]);
    } finally {
      setIsApplyingSettlements(false);
    }
  };

  const handleClose = () => {
    setSelectedReceiverId('');
    setSelectedDivisionId('');
    setSelectionMode('ship_group');
    setSelectedShipGroupKeys(new Set());
    setSelectedManualKeys(new Set());
    setPastedShipmentText('');
    setSelectedFile(null);
    setActiveStep('setup');
    setIsProcessing(false);
    setProcessingMessage('');
    setProcessedData([]);
    setGroupedData([]);
    setPdfGroupProcessing({});
    setActiveGroupKey('');
    setGroupMappings({});
    setGroupAssignmentLocks({});
    setBoxTransfers([]);
    setBoxTransferBaselines({});
    setBoxTransferDrafts({});
    setSalesLineTransferDrafts({});
    setProcessWarnings([]);
    setSettlementActionErrors([]);
    setRetrySourceDocumentId('');
    onClose();
  };

  const canSelectShipments = Boolean(selectedReceiverId);
  const selectedFileIsPdf = Boolean(
    selectedFile &&
      (selectedFile.type === 'application/pdf' ||
        selectedFile.name.toLowerCase().endsWith('.pdf'))
  );
  const canLeaveSetup = Boolean(
    selectedReceiverId && selectedFileIsPdf && selectedSeason && selectedSpecies
  );
  const canReadDocument = Boolean(
    selectedReceiverId && selectedFileIsPdf && pdfGroups.length > 0
  );
  const stepTitle =
    activeStep === 'setup'
      ? t('Configuración')
      : activeStep === 'groups'
        ? t('Grupos PDF')
        : activeStep === 'review'
          ? t('Revisión de grupos')
          : t('Liquidaciones a crear');
  const primaryActionLabel =
    activeStep === 'setup'
      ? t('Continuar')
      : activeStep === 'groups'
        ? groupedData.length > 0
          ? t('Volver a leer grupos')
          : t('Leer grupos')
        : t('Volver a leer grupos');
  const isPrimaryActionDisabled =
    isProcessing ||
    (activeStep === 'setup' && !canLeaveSetup) ||
    (activeStep !== 'setup' && !canReadDocument);
  const reviewedGroupCount =
    groupedStatusCounts.reviewed + groupedStatusCounts.ready_to_apply;
  const assignedGroupCount = groupedData.filter((group, groupIndex) =>
    Boolean(groupAssignmentLocks[getGroupKey(group, groupIndex)])
  ).length;
  const selectedReviewGroupKey =
    activeGroupKey ||
    (groupedData.length > 0 ? getGroupKey(groupedData[0], 0) : '');
  const groupTabSummaries = useMemo(() => {
    const summaries = new Map<
      string,
      {
        assignedQuantity: number;
        displayedQuantity: number;
        settlementQuantity: number;
      }
    >();

    groupedData.forEach((group, groupIndex) => {
      const groupKey = getGroupKey(group, groupIndex);
      const assignment = groupAssignmentLocks[groupKey];
      const assignedQuantity = Object.values(
        assignment?.shipmentBoxQuantitiesByLine || {}
      ).reduce(
        (total, lineQuantities) =>
          total +
          Object.values(lineQuantities).reduce(
            (lineTotal, quantity) => lineTotal + (Number(quantity) || 0),
            0
          ),
        0
      );
      const settlementQuantity =
        group.salesLines.reduce(
          (total, line) => total + (Number(line.quantity) || 0),
          0
        ) ||
        Number(group.totals.cases) ||
        0;
      const displayedQuantity = assignment
        ? assignedQuantity
        : getManuallyAdjustedGroupBoxes(
            getAvailableShipmentsForGroup(groupKey),
            deferredGroupMappings[groupKey] || EMPTY_GROUP_MAPPING,
            mappedFormats
          ).reduce((total, box) => total + (Number(box.quantity) || 0), 0);

      summaries.set(groupKey, {
        assignedQuantity,
        displayedQuantity,
        settlementQuantity,
      });
    });

    return summaries;
  }, [
    getAvailableShipmentsForGroup,
    groupAssignmentLocks,
    groupedData,
    deferredGroupMappings,
    mappedFormats,
  ]);
  const selectedReviewGroupIndex = groupedData.findIndex(
    (group, groupIndex) =>
      getGroupKey(group, groupIndex) === selectedReviewGroupKey
  );
  const selectedReviewGroup = groupedData[selectedReviewGroupIndex];
  const selectedReviewGroupMapping = selectedReviewGroup
    ? groupMappings[selectedReviewGroupKey] || EMPTY_GROUP_MAPPING
    : EMPTY_GROUP_MAPPING;
  const selectedReviewGroupAssignment = selectedReviewGroup
    ? groupAssignmentLocks[selectedReviewGroupKey]
    : undefined;
  const selectedReviewGroupScope = selectedReviewGroupMapping.scope;
  const selectedReviewIncludedBoxIds =
    selectedReviewGroupMapping.manuallyIncludedBoxIds;
  const selectedReviewExcludedBoxIds =
    selectedReviewGroupMapping.manuallyExcludedBoxIds;
  const selectedReviewManualBoxQuantities =
    selectedReviewGroupMapping.manualBoxQuantities;
  const selectedReviewDetailScopes = selectedReviewGroupMapping.detailScopes;
  const selectedReviewLineReservations =
    selectedReviewGroupMapping.shipmentBoxQuantitiesByLine;
  const selectedReviewAllocationMapping = useMemo(
    () => ({
      ...EMPTY_GROUP_MAPPING,
      scope: selectedReviewGroupScope,
      detailScopes: selectedReviewDetailScopes,
      shipmentBoxQuantitiesByLine: selectedReviewLineReservations,
      manuallyIncludedBoxIds: selectedReviewIncludedBoxIds,
      manuallyExcludedBoxIds: selectedReviewExcludedBoxIds,
      manualBoxQuantities: selectedReviewManualBoxQuantities,
    }),
    [
      selectedReviewDetailScopes,
      selectedReviewExcludedBoxIds,
      selectedReviewGroupScope,
      selectedReviewIncludedBoxIds,
      selectedReviewLineReservations,
      selectedReviewManualBoxQuantities,
    ]
  );
  const selectedReviewAvailableShipments = useMemo(
    () =>
      selectedReviewGroupKey
        ? getAvailableShipmentsForGroup(selectedReviewGroupKey)
        : [],
    [getAvailableShipmentsForGroup, selectedReviewGroupKey]
  );
  const selectedReviewWorkingShipments = useMemo(() => {
    if (!selectedReviewGroupAssignment) {
      return selectedReviewAvailableShipments;
    }
    const assignedBoxIds = new Set(
      Object.values(
        selectedReviewGroupAssignment.shipmentBoxIdsByLine || {}
      ).flat()
    );
    return selectedReviewAvailableShipments.filter((box) =>
      assignedBoxIds.has(String(box.id))
    );
  }, [selectedReviewAvailableShipments, selectedReviewGroupAssignment]);
  const selectedReviewScopedShipments = useMemo(
    () =>
      getManuallyAdjustedGroupBoxes(
        selectedReviewWorkingShipments,
        {
          ...EMPTY_GROUP_MAPPING,
          scope: selectedReviewGroupScope,
          manuallyIncludedBoxIds: selectedReviewIncludedBoxIds,
          manuallyExcludedBoxIds: selectedReviewExcludedBoxIds,
          manualBoxQuantities: selectedReviewManualBoxQuantities,
        },
        mappedFormats
      ),
    [
      mappedFormats,
      selectedReviewExcludedBoxIds,
      selectedReviewGroupScope,
      selectedReviewIncludedBoxIds,
      selectedReviewManualBoxQuantities,
      selectedReviewWorkingShipments,
    ]
  );
  const selectedReviewLineAllocation = useMemo(
    () =>
      selectedReviewGroup
        ? getGroupLineAllocation(
            selectedReviewWorkingShipments,
            selectedReviewGroup,
            selectedReviewAllocationMapping,
            mappedFormats,
            selectedReviewScopedShipments
          )
        : null,
    [
      mappedFormats,
      selectedReviewGroup,
      selectedReviewAllocationMapping,
      selectedReviewScopedShipments,
      selectedReviewWorkingShipments,
    ]
  );
  const selectedReviewLineScopeOptions = useMemo(() => {
    if (!selectedReviewGroup || !selectedReviewLineAllocation) return {};
    const optionsByBoxes = new Map<ShipmentRecord[], LineScopeOptions>();

    return Object.fromEntries(
      selectedReviewGroup.salesLines.map((line) => {
        const boxes =
          selectedReviewLineAllocation.availableBoxesByLine[line.id] || [];
        let options = optionsByBoxes.get(boxes);
        if (!options) {
          options = buildLineScopeOptions(boxes, mappedFormats);
          optionsByBoxes.set(boxes, options);
        }
        return [line.id, options];
      })
    ) as Record<string, LineScopeOptions>;
  }, [mappedFormats, selectedReviewGroup, selectedReviewLineAllocation]);
  const selectedReviewManualBoxData = useMemo(() => {
    const scopedShipmentBoxIds = new Set(
      selectedReviewScopedShipments.map((box) => String(box.id))
    );
    const manuallyIncludedBoxIds = new Set(selectedReviewIncludedBoxIds || []);
    const manuallyExcludedBoxIds = new Set(selectedReviewExcludedBoxIds || []);
    const resolveBoxes = (boxIds: Set<string>) =>
      Array.from(boxIds).flatMap((boxId) => {
        const box = effectiveShipmentsById.get(boxId);
        return box ? [box] : [];
      });

    return {
      scopedShipmentBoxIds,
      manuallyIncludedBoxIds,
      manuallyExcludedBoxIds,
      manuallyIncludedBoxes: resolveBoxes(manuallyIncludedBoxIds),
      manuallyExcludedBoxes: resolveBoxes(manuallyExcludedBoxIds),
      manualIncludeOptions: sortShipmentBoxesByAdjustmentLabel(
        selectedReviewAvailableShipments.filter(
          (box) =>
            !scopedShipmentBoxIds.has(String(box.id)) &&
            !manuallyIncludedBoxIds.has(String(box.id))
        ),
        mappedFormats
      ),
      manualExcludeOptions: sortShipmentBoxesByAdjustmentLabel(
        selectedReviewScopedShipments.filter(
          (box) => !manuallyExcludedBoxIds.has(String(box.id))
        ),
        mappedFormats
      ),
    };
  }, [
    effectiveShipmentsById,
    mappedFormats,
    selectedReviewAvailableShipments,
    selectedReviewExcludedBoxIds,
    selectedReviewIncludedBoxIds,
    selectedReviewScopedShipments,
  ]);

  const handleBackStep = () => {
    if (activeStep === 'settlements') {
      setIsPdfSidebarOpen(true);
      setActiveStep('review');
      return;
    }

    if (activeStep === 'review') {
      setActiveStep(selectedFileIsPdf ? 'groups' : 'setup');
      return;
    }

    if (activeStep === 'groups') {
      setActiveStep('setup');
    }
  };

  const handleReviewSettlements = () => {
    setSettlementActionErrors([]);
    if (derivedSettlementResult.errors.length > 0) {
      showSettlementActionErrors(derivedSettlementResult.errors);
      return;
    }
    if (derivedSettlementResult.settlements.length === 0) {
      showSettlementActionErrors([
        t('No hay liquidaciones derivadas para revisar'),
      ]);
      return;
    }

    setIsPdfSidebarOpen(false);
    setActiveStep('settlements');
  };

  const handlePrimaryAction = () => {
    if (activeStep === 'setup') {
      if (
        !selectedReceiver ||
        !selectedFileIsPdf ||
        !selectedSeason ||
        !selectedSpecies
      ) {
        toast.error(
          t('Completa cliente, temporada, especie y archivo antes de continuar')
        );
        return;
      }

      setActiveStep('groups');
      return;
    }

    void handleProcessDocument();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isApplyingSettlements) handleClose();
      }}
      isDismissable={!isApplyingSettlements}
      isKeyboardDismissDisabled={isApplyingSettlements}
      hideCloseButton={isApplyingSettlements}
      size='full'
      scrollBehavior='inside'
      classNames={{
        wrapper: 'z-[10000]',
      }}
    >
      <ModalContent className='relative m-0 h-[100dvh] max-h-[100dvh] w-screen max-w-none rounded-none overflow-hidden'>
        <ModalHeader className='flex flex-col gap-1'>
          <div className='flex flex-wrap items-start justify-between gap-3'>
            <div>
              <span>
                {t('Subir archivo multi-embarque')} · {stepTitle}
              </span>
              <span className='block text-sm font-normal text-default-500'>
                {t(
                  'Selecciona el cliente y el PDF para definir grupos de lectura.'
                )}
              </span>
            </div>
            {false && groupedData.length > 0 && (
              <div className='flex flex-wrap items-center gap-2'>
                {isProcessing && (
                  <Chip size='sm' color='warning' variant='flat'>
                    {processingMessage || t('Leyendo grupos')}
                  </Chip>
                )}
                <Chip size='sm' variant='flat'>
                  {groupedStatusCounts.reviewed +
                    groupedStatusCounts.ready_to_apply}
                  /{groupedData.length} {t('grupos revisados')}
                </Chip>
              </div>
            )}
          </div>
        </ModalHeader>
        <ModalBody className='min-h-0 flex-1 gap-4'>
          {activeStep !== 'review' && (
            <>
              <div
                className={`relative z-20 flex flex-col gap-3 rounded-medium border border-default-200 p-4 ${
                  activeStep === 'setup' ? '' : 'hidden'
                }`}
              >
                  <div className='flex items-center gap-2 text-sm font-semibold text-default-700'>
                    <Icon icon='tabler:users' className='text-default-500' />
                    {t('Cliente de la liquidación')}
                  </div>
                  <div className='grid grid-cols-1 gap-3 md:grid-cols-3'>
                    <Autocomplete
                      label={t('Cliente')}
                      placeholder={t(
                        'Selecciona el cliente antes de elegir embarques'
                      )}
                      selectedKey={selectedReceiverId || null}
                      onSelectionChange={(key) =>
                        setSelectedReceiverId(String(key || ''))
                      }
                      isLoading={receiversLoading}
                      defaultItems={[...receivers].sort((a, b) =>
                        a.name.localeCompare(b.name, 'es')
                      )}
                    >
                      {(receiver) => (
                        <AutocompleteItem key={String(receiver.id)}>
                          {receiver.name}
                        </AutocompleteItem>
                      )}
                    </Autocomplete>
                    <Select
                      label={t('Temporada')}
                      selectedKeys={selectedSeason ? [selectedSeason] : []}
                      onSelectionChange={(keys) =>
                        setSelectedSeason(String(Array.from(keys)[0] || ''))
                      }
                    >
                      {Object.values(SEASONS)
                        .map((season) => season.name)
                        .sort((a, b) => a.localeCompare(b, 'es'))
                        .map((season) => (
                          <SelectItem key={season}>{season}</SelectItem>
                        ))}
                    </Select>
                    {divisions.length > 1 && (
                      <Select
                        label={t('División')}
                        selectedKeys={[selectedDivisionId || 'auto']}
                        onChange={(e) =>
                          setSelectedDivisionId(
                            e.target.value === 'auto' ? '' : e.target.value
                          )
                        }
                      >
                        {[
                          <SelectItem key='auto' value='auto'>
                            {t('Automática (según despacho)')}
                          </SelectItem>,
                          ...divisions.map((division) => (
                            <SelectItem key={division.id} value={division.id}>
                              {division.name}
                            </SelectItem>
                          )),
                        ]}
                      </Select>
                    )}
                    <Autocomplete
                      label={t('Especie')}
                      placeholder={t('Selecciona especie')}
                      selectedKey={
                        selectedSpecies ? String(selectedSpecies.id) : null
                      }
                      onSelectionChange={(key) => {
                        const selected = species.find(
                          (item) => String(item.id) === String(key)
                        );
                        if (selected) setSelectedSpecies(selected);
                      }}
                      defaultItems={[...species].sort((a, b) =>
                        a.name.localeCompare(b.name, 'es')
                      )}
                    >
                      {(item) => (
                        <AutocompleteItem key={String(item.id)}>
                          {item.name}
                        </AutocompleteItem>
                      )}
                    </Autocomplete>
                  </div>
                  {false && selectedReceiver && (
                    <div className='flex flex-wrap gap-2'>
                      <Chip size='sm' variant='flat'>
                        {clientShipments.length} {t('líneas de despacho')}
                      </Chip>
                      <Chip size='sm' variant='flat'>
                        {
                          uniqueValues(
                            clientShipments.map((box) => box.reference_number)
                          ).length
                        }{' '}
                        {t('referencias')}
                      </Chip>
                      <Chip size='sm' variant='flat'>
                        {shipGroups.length} {t('naves')}
                      </Chip>
                    </div>
                  )}
              </div>

              <div
                className={`flex flex-col gap-3 rounded-medium border border-default-200 p-4 ${
                  activeStep === 'setup' ? '' : 'hidden'
                }`}
              >
                  <div className='flex flex-wrap items-center justify-between gap-2'>
                    <div>
                      <div className='flex items-center gap-2 text-sm font-semibold text-default-700'>
                        <Icon icon='tabler:ship' className='text-default-500' />
                        {t('Alcance del proceso')}
                        <Chip size='sm' variant='flat'>
                          {t('Opcional')}
                        </Chip>
                      </div>
                      <p className='mt-1 text-xs text-default-500'>
                        {t(
                          'Limita opcionalmente las cajas que estarán disponibles en filtros, asistentes y asignaciones. Sin selección se usarán todos los despachos del cliente.'
                        )}
                      </p>
                    </div>
                    {shipmentsLoading && (
                      <Chip size='sm' variant='flat' color='warning'>
                        {t('Cargando despachos')}
                      </Chip>
                    )}
                  </div>

                  <Tabs
                    selectedKey={selectionMode}
                    onSelectionChange={(key) =>
                      handleSelectionModeChange(key as SelectionMode)
                    }
                    isDisabled={!canSelectShipments}
                    aria-label={t('Modo de selección de embarques')}
                    classNames={{ panel: 'overflow-visible' }}
                  >
                    <Tab key='ship_group' title={t('Por naves')}>
                      <div className='pt-3'>
                        <CustomAutocomplete
                          portal
                          label={t('Naves incluidas')}
                          placeholder={t('Buscar y agregar naves')}
                          options={shipGroups
                            .filter(
                              (group) => !selectedShipGroupKeys.has(group.key)
                            )
                            .map((group) => ({
                              key: group.key,
                              value: group.key,
                              label: `${group.ship_name} · ETD ${
                                formatDate(group.ship_etd) || '-'
                              } · ETA ${
                                formatDate(group.ship_eta) || '-'
                              } · ${group.ship_destination_port} · ${
                                group.references.length
                              } ${t('referencias')}`,
                            }))}
                          onSelectionChange={handleShipGroupAdd}
                          closeOnSelect={false}
                          isDisabled={
                            !canSelectShipments || shipGroups.length === 0
                          }
                          contentInsideInput={Array.from(
                            selectedShipGroupKeys
                          ).map((key) => {
                            const group = shipGroups.find(
                              (item) => item.key === key
                            );
                            if (!group) return null;
                            return (
                              <Chip
                                key={key}
                                size='sm'
                                variant='flat'
                                onClose={() => handleShipGroupRemove(key)}
                              >
                                {group.ship_name} · ETD{' '}
                                {formatDate(group.ship_etd) || '-'} · ETA{' '}
                                {formatDate(group.ship_eta) || '-'}
                              </Chip>
                            );
                          })}
                        />
                      </div>
                    </Tab>
                    <Tab key='manual' title={t('Por embarques')}>
                      <div className='grid grid-cols-1 gap-3 pt-3 lg:grid-cols-2'>
                        <div className='space-y-3'>
                          <CustomAutocomplete
                            portal
                            label={t('Embarques incluidos')}
                            placeholder={t('Buscar y agregar embarques')}
                            options={manualOptions
                              .filter(
                                (option) =>
                                  !selectedManualOptionKeys.has(option.key)
                              )
                              .map((option) => ({
                                key: option.key,
                                value: option.key,
                                label: `${option.label} · ${option.description}`,
                              }))}
                            onSelectionChange={handleManualAdd}
                            closeOnSelect={false}
                            isDisabled={!canSelectShipments}
                            contentInsideInput={manualOptions
                              .filter((option) =>
                                selectedManualOptionKeys.has(option.key)
                              )
                              .map((option) => (
                                <Chip
                                  key={option.key}
                                  size='sm'
                                  variant='flat'
                                  onClose={
                                    selectedManualKeys.has(option.key)
                                      ? () => handleManualRemove(option.key)
                                      : undefined
                                  }
                                >
                                  {option.label}
                                </Chip>
                              ))}
                          />
                          <Textarea
                            label={t('Pegar referencias o contenedores')}
                            placeholder={'MSKU1234567\nREF-2025-001'}
                            value={pastedShipmentText}
                            onValueChange={handlePastedShipmentTextChange}
                            isDisabled={!canSelectShipments}
                            minRows={5}
                          />
                          {pastedMatches.unmatchedTokens.length > 0 && (
                            <div className='rounded-md border border-warning-200 bg-warning-50 p-2 text-xs text-warning-700'>
                              {t('No encontrados para este cliente')}:{' '}
                              {pastedMatches.unmatchedTokens.join(', ')}
                            </div>
                          )}
                        </div>
                        <div className='rounded-md border border-default-200 p-3'>
                          <p className='text-xs font-semibold uppercase text-default-400'>
                            {t('Entrada masiva')}
                          </p>
                          <p className='mt-1 text-xs text-default-500'>
                            {t(
                              'Puedes pegar referencias o contenedores separados por espacios, comas o líneas.'
                            )}
                          </p>
                        </div>
                      </div>
                    </Tab>
                  </Tabs>

                  <div className='flex flex-wrap gap-2 rounded-md bg-default-50 p-3 dark:bg-default-100'>
                    <Chip
                      size='sm'
                      color={hasShipmentPrefilter ? 'primary' : 'default'}
                      variant='flat'
                    >
                      {hasShipmentPrefilter
                        ? t('Alcance limitado')
                        : t('Todos los despachos')}
                    </Chip>
                    <Chip size='sm' variant='flat'>
                      {selectedReferences.length} {t('referencias')}
                    </Chip>
                    <Chip size='sm' variant='flat'>
                      {selectedContainers.length} {t('contenedores')}
                    </Chip>
                    <Chip size='sm' variant='flat'>
                      {formatNumber(
                        effectiveShipments.reduce(
                          (total, box) => total + (Number(box.quantity) || 0),
                          0
                        )
                      )}{' '}
                      {t('bultos')}
                    </Chip>
                    <Chip size='sm' variant='flat'>
                      {formatNumber(selectedKilos)} kg
                    </Chip>
                  </div>
              </div>

              <div
                className={`flex flex-col gap-3 rounded-medium border border-default-200 p-4 ${
                  activeStep === 'setup' ? '' : 'hidden'
                }`}
              >
                  <div className='flex items-center gap-2 text-sm font-semibold text-default-700'>
                    <Icon icon='tabler:upload' className='text-default-500' />
                    {t('Archivo')}
                  </div>
                  <div className='flex flex-col gap-3 rounded-md border border-dashed border-default-300 p-4 sm:flex-row sm:items-center sm:justify-between'>
                    <div>
                      <p className='text-sm font-medium text-default-700'>
                        {selectedFile
                          ? selectedFile.name
                          : t('Selecciona un PDF')}
                      </p>
                      <p className='text-xs text-default-400'>
                        {selectedFile
                          ? `${fileTypeLabel(selectedFile)} · ${(
                              selectedFile.size /
                              1024 /
                              1024
                            ).toFixed(1)} MB`
                          : t(
                              'Este PDF se procesará en el flujo multi-embarque.'
                            )}
                      </p>
                    </div>
                    <div className='flex items-center gap-2'>
                      {selectedFile && !inboxClaim && (
                        <Button
                          size='sm'
                          variant='light'
                          isIconOnly
                          onPress={() => {
                            setSelectedFile(null);
                            setActiveStep('setup');
                            setProcessingMessage('');
                            setProcessedData([]);
                            setGroupedData([]);
                            setPdfGroupProcessing({});
                            setActiveGroupKey('');
                            setGroupMappings({});
                            setGroupAssignmentLocks({});
                            setBoxTransfers([]);
                            setBoxTransferBaselines({});
                            setBoxTransferDrafts({});
                            setSalesLineTransferDrafts({});
                            setProcessWarnings([]);
                            setRetrySourceDocumentId('');
                          }}
                          aria-label={t('Quitar archivo')}
                        >
                          <Icon icon='tabler:x' />
                        </Button>
                      )}
                      <Button
                        as='label'
                        variant='flat'
                        startContent={<Icon icon='tabler:file-type-pdf' />}
                        isDisabled={!!inboxClaim}
                      >
                        {t('Elegir archivo')}
                        <input
                          type='file'
                          accept='.pdf,application/pdf'
                          className='hidden'
                          onChange={handleFileInput}
                          disabled={!!inboxClaim}
                        />
                      </Button>
                    </div>
                  </div>
              </div>

              {activeStep === 'groups' && selectedFileIsPdf && selectedFile && (
                <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-medium border border-default-200 p-4'>
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <div>
                        <p className='text-sm font-semibold text-default-700'>
                          {t('Grupos PDF')}
                        </p>
                        <p className='text-xs text-default-500'>
                          {t(
                            'Selecciona páginas y crea los grupos que el robot leerá por separado.'
                          )}
                        </p>
                      </div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Chip size='sm' variant='flat'>
                          {pdfPageCount || '-'} {t('páginas')}
                        </Chip>
                        <Chip size='sm' color='success' variant='flat'>
                          {assignedPdfPageCount} {t('asignadas')}
                        </Chip>
                        <Chip
                          size='sm'
                          color={
                            unassignedPdfPageCount > 0 ? 'warning' : 'success'
                          }
                          variant='flat'
                        >
                          {unassignedPdfPageCount} {t('sin asignar')}
                        </Chip>
                      </div>
                    </div>

                    <div className='grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_360px]'>
                      <div className='flex min-h-0 flex-col overflow-hidden rounded-md border border-default-200 bg-default-50 p-3'>
                        <div className='mb-3 flex flex-wrap items-center gap-2'>
                          <p className='mr-auto text-xs font-semibold uppercase text-default-400'>
                            {t('Vista previa')}
                          </p>
                          <Button
                            size='sm'
                            variant='flat'
                            onPress={() =>
                              setCurrentPdfPageNumber((page) =>
                                Math.max(1, page - 1)
                              )
                            }
                            isDisabled={currentPdfPageNumber <= 1}
                          >
                            {t('Anterior')}
                          </Button>
                          <div className='whitespace-nowrap text-center text-sm font-semibold'>
                            {t('Página')} {currentPdfPageNumber} {t('de')}{' '}
                            {pdfPageCount || '-'}
                            {currentPdfPageAssigned && (
                              <Chip
                                className='ml-2'
                                size='sm'
                                color='success'
                                variant='flat'
                              >
                                {t('Asignada')}
                              </Chip>
                            )}
                          </div>
                          <Button
                            size='sm'
                            variant='flat'
                            onPress={() =>
                              setCurrentPdfPageNumber((page) =>
                                Math.min(pdfPageCount, page + 1)
                              )
                            }
                            isDisabled={
                              !pdfPageCount ||
                              currentPdfPageNumber >= pdfPageCount
                            }
                          >
                            {t('Siguiente')}
                          </Button>
                          <Button
                            size='sm'
                            variant='flat'
                            onPress={assignCurrentPageToNewGroup}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              event.preventDefault();
                              assignCurrentPageToNewGroup();
                            }}
                            isDisabled={currentPdfPageAssigned}
                          >
                            {t('Nuevo grupo')}
                          </Button>
                          <Select
                            size='sm'
                            aria-label={t('Grupo destino')}
                            placeholder={t('Grupo destino')}
                            className='w-40'
                            classNames={{
                              trigger: 'h-8 min-h-8',
                            }}
                            selectedKeys={
                              selectedPdfGroupId ? [selectedPdfGroupId] : []
                            }
                            onSelectionChange={(keys) =>
                              setSelectedPdfGroupId(
                                String(Array.from(keys)[0] || '')
                              )
                            }
                            isDisabled={pdfGroups.length === 0}
                          >
                            {[...pdfGroups].reverse().map((group) => (
                              <SelectItem key={group.id}>
                                {group.name}
                              </SelectItem>
                            ))}
                          </Select>
                          <Button
                            size='sm'
                            color='primary'
                            onPress={() =>
                              assignCurrentPageToGroup(selectedPdfGroupId)
                            }
                            isDisabled={
                              !selectedPdfGroupId || currentPdfPageAssigned
                            }
                          >
                            {selectedPdfGroupId
                              ? `${t('Asignar a')} ${
                                  pdfGroups.find(
                                    (group) => group.id === selectedPdfGroupId
                                  )?.name || t('grupo')
                                }`
                              : t('Asignar a grupo')}
                          </Button>
                          {pdfPreviewUrl && (
                            <Button
                              size='sm'
                              variant='flat'
                              onPress={() =>
                                window.open(
                                  pdfPreviewUrl,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }
                            >
                              {t('Abrir PDF completo')}
                            </Button>
                          )}
                        </div>
                        {currentPdfThumbnail ? (
                          <div className='flex min-h-0 flex-1 items-center justify-center overflow-auto rounded border border-default-100 bg-content1 p-4'>
                            <img
                              draggable={!currentPdfPageAssigned}
                              onDragStart={(event) =>
                                event.dataTransfer.setData(
                                  'text/plain',
                                  String(currentPdfPageNumber)
                                )
                              }
                              src={currentPdfThumbnail.dataUrl}
                              width={currentPdfThumbnail.width}
                              height={currentPdfThumbnail.height}
                              alt={`${t('Página')} ${currentPdfPageNumber}`}
                              className={`max-h-full max-w-full object-contain ${currentPdfPageAssigned ? 'opacity-60' : 'cursor-grab'}`}
                            />
                          </div>
                        ) : (
                          <div className='flex flex-1 flex-col items-center justify-center gap-3 text-sm text-default-500'>
                            <Spinner size='sm' />
                            <span>
                              {pdfPageCount
                                ? `${t('Cargando página')} ${currentPdfPageNumber}`
                                : t('Cargando PDF')}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className='min-h-0 space-y-3 overflow-auto pr-1'>
                        <div className='flex items-center justify-between gap-2'>
                          <p className='text-xs font-semibold uppercase text-default-400'>
                            {t('Grupos definidos')}
                          </p>
                          <Chip size='sm' variant='flat'>
                            {pdfGroups.length}
                          </Chip>
                        </div>
                        {[...pdfGroups].reverse().map((group) => (
                          <div
                            key={group.id}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={(event) => {
                              event.preventDefault();
                              assignCurrentPageToGroup(group.id);
                            }}
                            onClick={(event) => {
                              if (
                                (event.target as HTMLElement).closest(
                                  '[data-group-remove]'
                                )
                              ) {
                                return;
                              }
                              assignCurrentPageToGroup(group.id);
                            }}
                            className={`grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md border p-3 transition ${currentPdfPageAssigned ? 'border-default-200 bg-default-50 dark:bg-default-100' : 'border-default-200 bg-content1 hover:border-primary hover:bg-primary-50/40 dark:hover:bg-primary-100/30'}`}
                          >
                            <div className='min-w-0'>
                              <p className='truncate text-sm font-semibold text-foreground'>
                                {group.name}
                              </p>
                              <p className='mt-1 text-xs text-default-400'>
                                {formatPageRange(group)}
                              </p>
                            </div>
                            <button
                              type='button'
                              data-group-remove
                              onClick={(event) => {
                                event.stopPropagation();
                                handleRemovePdfGroup(group.id);
                              }}
                              aria-label={t('Quitar grupo')}
                              className='flex h-8 w-8 items-center justify-center rounded-medium text-default-500 transition hover:bg-danger-50 hover:text-danger dark:hover:bg-danger-100/20'
                            >
                              <Icon icon='tabler:x' />
                            </button>
                          </div>
                        ))}
                        {pdfGroups.length === 0 && (
                          <p className='text-sm text-default-400'>
                            {t('Usa Nuevo grupo para comenzar')}
                          </p>
                        )}
                      </div>
                    </div>
                </div>
              )}

              <Card className='hidden'>
                <CardBody className='gap-2'>
                  <p className='text-xs font-semibold uppercase text-default-400'>
                    {t('Resumen')}
                  </p>
                  <div className='grid grid-cols-2 gap-3 text-sm md:grid-cols-4'>
                    <div>
                      <p className='text-default-400'>{t('Cliente')}</p>
                      <p className='font-medium'>
                        {selectedReceiver?.name || '-'}
                      </p>
                    </div>
                    <div>
                      <p className='text-default-400'>{t('Referencias')}</p>
                      <p className='font-medium'>{selectedReferences.length}</p>
                    </div>
                    <div>
                      <p className='text-default-400'>{t('Contenedores')}</p>
                      <p className='font-medium'>{selectedContainers.length}</p>
                    </div>
                    <div>
                      <p className='text-default-400'>{t('Kilos')}</p>
                      <p className='font-medium'>
                        {selectedKilos.toLocaleString('es-CL', {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </>
          )}

          {activeStep === 'review' &&
            hasShipmentPrefilter &&
            outsideScopeShipmentOptions.length > 0 && (
              <Alert variant='warning' showIcon>
                <div className='flex flex-col gap-3'>
                  <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                      <p className='text-sm font-semibold text-warning-800 dark:text-warning-300'>
                        {t('Fuera del alcance inicial')}
                      </p>
                      <p className='text-xs text-warning-700 dark:text-warning-400'>
                        {t(
                          'El PDF menciona despachos conocidos que no estaban incluidos. Puedes ampliar el alcance sin volver a leer el documento.'
                        )}
                      </p>
                    </div>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() => handleExpandShipmentScope()}
                    >
                      {t('Agregar todos')}
                    </Button>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {(selectionMode === 'ship_group'
                      ? outsideScopeShipGroups.map((group) => ({
                          key: group.key,
                          label: `${group.ship_name} · ETD ${
                            formatDate(group.ship_etd) || '-'
                          } · ETA ${formatDate(group.ship_eta) || '-'}`,
                        }))
                      : outsideScopeShipmentOptions.map((option) => ({
                          key: option.key,
                          label: option.label,
                        }))
                    ).map((option) => (
                      <Button
                        key={option.key}
                        size='sm'
                        variant='flat'
                        onPress={() => handleExpandShipmentScope([option.key])}
                      >
                        + {option.label}
                      </Button>
                    ))}
                  </div>
              </div>
              </Alert>
            )}

          {(activeStep === 'review' || activeStep === 'settlements') &&
            groupedData.length > 0 && (
              <div className='flex flex-col gap-4 rounded-medium border border-default-200 p-4'>
                  <div className='hidden'>
                    <div>
                      <p className='text-sm font-semibold text-default-700'>
                        {t('Grupos detectados del documento')}
                      </p>
                      <p className='text-xs text-default-500'>
                        {t(
                          'Revisa cada pestaña antes de convertir los grupos a liquidaciones por embarque.'
                        )}
                      </p>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {REVIEW_STATUS_OPTIONS.map((status) => (
                        <Chip
                          key={status}
                          size='sm'
                          color={getStatusColor(status)}
                          variant='flat'
                        >
                          {groupedStatusCounts[status]}{' '}
                          {t(REVIEW_STATUS_LABELS[status])}
                        </Chip>
                      ))}
                    </div>
                  </div>

                  {processWarnings.length > 0 && (
                    <div className='hidden'>
                      {processWarnings.map((warning) => (
                        <p key={warning}>{warning}</p>
                      ))}
                    </div>
                  )}

                  {derivedSettlementResult.unmappedBoxTypes.length > 0 && (
                    <div className='rounded-lg border border-warning-300 bg-warning-50 p-4 dark:border-warning-700 dark:bg-warning-100/10'>
                      <div className='mb-3'>
                        <p className='text-sm font-semibold text-warning-800 dark:text-warning-300'>
                          {t('Formatos de caja pendientes de mapear')}
                        </p>
                        <p className='text-xs text-warning-700 dark:text-warning-400'>
                          {t(
                            'Estas cajas están asignadas a ventas confirmadas. Indica el formato correspondiente para continuar; el peso se guardará por separado.'
                          )}
                        </p>
                      </div>
                      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3'>
                        {derivedSettlementResult.unmappedBoxTypes.map(
                          (boxType) => (
                            <Select
                              key={boxType}
                              size='sm'
                              label={`${t('Código de caja')}: ${boxType}`}
                              placeholder={t('Seleccionar formato')}
                              isLoading={savingBoxTypeMapping === boxType}
                              isDisabled={
                                Boolean(savingBoxTypeMapping) &&
                                savingBoxTypeMapping !== boxType
                              }
                              onSelectionChange={(keys) => {
                                const format = String(
                                  Array.from(keys)[0] || ''
                                ) as (typeof VALID_FORMATS)[number];
                                if (format) {
                                  void handleSaveBoxTypeMapping(
                                    boxType,
                                    format
                                  );
                                }
                              }}
                            >
                              {VALID_FORMATS.map((format) => (
                                <SelectItem key={format} value={format}>
                                  {format}
                                </SelectItem>
                              ))}
                            </Select>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {activeStep === 'settlements' && (
                    <div className='rounded-lg border border-primary-200 bg-primary-50/40 p-3 dark:bg-primary-100/10'>
                      <div className='flex flex-wrap items-start justify-between gap-2'>
                        <div>
                          <p className='text-sm font-semibold text-primary-700 dark:text-primary-300'>
                            {t('Liquidaciones a crear por referencia')}
                          </p>
                          <p className='text-xs text-default-600'>
                            {derivedSettlementResult.settlements.length}{' '}
                            {t('referencias derivadas')} ·{' '}
                            {formatNumber(
                              derivedSettlementResult.settlements.reduce(
                                (total, settlement) =>
                                  total +
                                  settlement.details.reduce(
                                    (detailTotal, detail) =>
                                      detailTotal + detail.quantity,
                                    0
                                  ),
                                0
                              )
                            )}{' '}
                            {t('bultos')}
                          </p>
                        </div>
                        <Chip
                          size='sm'
                          color={
                            derivedSettlementResult.errors.length ||
                            hasIncompleteSettlementHeaders
                              ? 'warning'
                              : 'success'
                          }
                          variant='flat'
                        >
                          {derivedSettlementResult.errors.length ||
                          hasIncompleteSettlementHeaders
                            ? t('Requiere revisión')
                            : t('Listo para crear')}
                        </Chip>
                      </div>
                      {derivedSettlementResult.errors.length > 0 ? (
                        <p className='mt-2 text-xs text-warning-700 dark:text-warning-300'>
                          {derivedSettlementResult.errors[0]}
                        </p>
                      ) : (
                        <div className='mt-3 space-y-3'>
                          {derivedSettlementResult.settlements.map(
                            (settlement) => {
                              const settlementHeader =
                                settlementHeaders[settlement.referenceNumber];
                              const transportType =
                                settlementHeader?.transportType || '';
                              const availableShipTrips = sortedShipTrips.filter(
                                (trip) =>
                                  !transportType ||
                                  trip.ship?.type === transportType
                              );
                              const currency =
                                normalizeCostCurrency(
                                  settlementHeader?.currency
                                ) ||
                                normalizeCostCurrency(groupedCurrency) ||
                                'USD';
                              const previewDetails =
                                convertDerivedDetailsToLocalCurrency(
                                  settlement.details,
                                  groupedData,
                                  currency,
                                  Number(settlementHeader?.exchangeRate) || 1
                                );
                              const financials =
                                getDerivedSettlementFinancials(previewDetails);

                              return (
                                <div
                                  key={settlement.referenceNumber}
                                  className='rounded-md border border-primary-200/80 bg-background/70 p-3'
                                >
                                  <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                                    <p className='text-sm font-semibold'>
                                      {settlement.referenceNumber} ·{' '}
                                      {settlement.details.length}{' '}
                                      {t('detalles')}
                                    </p>
                                    <Chip size='sm' variant='flat'>
                                      {formatNumber(
                                        settlement.details.reduce(
                                          (total, detail) =>
                                            total + detail.quantity,
                                          0
                                        )
                                      )}{' '}
                                      {t('bultos')}
                                    </Chip>
                                  </div>
                                  <div className='mb-3 grid grid-cols-1 gap-2 sm:grid-cols-3'>
                                    <div className='rounded-md border border-default-200 bg-default-50/70 px-3 py-2'>
                                      <p className='text-[11px] font-semibold uppercase text-default-400'>
                                        {t('Total ingresos')}
                                      </p>
                                      <p className='mt-1 text-sm font-semibold text-success-700 dark:text-success-300'>
                                        {formatMoney(
                                          financials.income,
                                          currency,
                                          2
                                        )}
                                      </p>
                                    </div>
                                    <div className='rounded-md border border-default-200 bg-default-50/70 px-3 py-2'>
                                      <p className='text-[11px] font-semibold uppercase text-default-400'>
                                        {t('Total costos')}
                                      </p>
                                      <p className='mt-1 text-sm font-semibold text-default-700'>
                                        {formatMoney(
                                          financials.costs,
                                          currency,
                                          2
                                        )}
                                      </p>
                                    </div>
                                    <div className='rounded-md border border-default-200 bg-default-50/70 px-3 py-2'>
                                      <p className='text-[11px] font-semibold uppercase text-default-400'>
                                        {t('Neto')}
                                      </p>
                                      <p
                                        className={`mt-1 text-sm font-bold ${
                                          financials.net < 0
                                            ? 'text-danger-600 dark:text-danger-300'
                                            : 'text-primary-700 dark:text-primary-300'
                                        }`}
                                      >
                                        {formatMoney(
                                          financials.net,
                                          currency,
                                          2
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                  <div className='mb-3 rounded-md border border-default-200 bg-default-50/40 p-3'>
                                    <p className='mb-3 text-xs font-semibold uppercase tracking-wide text-default-500'>
                                      {t('Transporte')}
                                    </p>
                                    <div className='grid grid-cols-1 items-end gap-3 md:grid-cols-2 xl:grid-cols-4'>
                                      <div
                                        className={
                                          transportType === 'A' ||
                                          transportType === 'T'
                                            ? ''
                                            : 'xl:col-span-2'
                                        }
                                      >
                                        <TransportTypeSelector
                                          noTooltip
                                          value={transportType}
                                          onChange={(newType) => {
                                            const isManualTrip =
                                              newType === 'A' ||
                                              newType === 'T';
                                            updateSettlementHeader(
                                              settlement.referenceNumber,
                                              {
                                                transportType:
                                                  newType as TransportType,
                                                shipTripId: '',
                                                shipEtd: isManualTrip
                                                  ? settlementHeader?.shipEtd ||
                                                    toDateInputValue(
                                                      settlement.shipEtd
                                                    )
                                                  : settlementHeader?.shipEtd ||
                                                    '',
                                                shipEta: isManualTrip
                                                  ? settlementHeader?.shipEta ||
                                                    toDateInputValue(
                                                      settlement.shipEta
                                                    )
                                                  : settlementHeader?.shipEta ||
                                                    '',
                                                destinationMarketId:
                                                  isManualTrip
                                                    ? settlementHeader?.destinationMarketId ||
                                                      settlementHeader?.marketId ||
                                                      ''
                                                    : settlementHeader?.destinationMarketId ||
                                                      '',
                                              }
                                            );
                                          }}
                                        />
                                      </div>
                                      {transportType === 'M' ? (
                                        <div className='xl:col-span-2'>
                                          <Autocomplete
                                            size='sm'
                                            label={t('Viaje')}
                                            labelPlacement='outside'
                                            placeholder={t('Selecciona viaje')}
                                            selectedKey={
                                              settlementHeader?.shipTripId ||
                                              null
                                            }
                                            onSelectionChange={(key) =>
                                              updateSettlementHeader(
                                                settlement.referenceNumber,
                                                {
                                                  shipTripId: String(key || ''),
                                                }
                                              )
                                            }
                                            defaultItems={availableShipTrips}
                                            isClearable
                                            listboxProps={{
                                              emptyContent: t(
                                                'No hay viajes disponibles para este tipo de transporte'
                                              ),
                                            }}
                                          >
                                            {(trip) => {
                                              const label =
                                                getShipTripLabel(trip);
                                              return (
                                                <AutocompleteItem
                                                  key={String(trip.id)}
                                                  textValue={label}
                                                >
                                                  {label}
                                                </AutocompleteItem>
                                              );
                                            }}
                                          </Autocomplete>
                                        </div>
                                      ) : transportType ? (
                                        <>
                                          <Input
                                            size='sm'
                                            label={t('Fecha de salida (ETD)')}
                                            labelPlacement='outside'
                                            type='date'
                                            value={
                                              settlementHeader?.shipEtd || ''
                                            }
                                            onChange={(event) =>
                                              updateSettlementHeader(
                                                settlement.referenceNumber,
                                                { shipEtd: event.target.value }
                                              )
                                            }
                                          />
                                          <Input
                                            size='sm'
                                            label={t('Fecha de llegada (ETA)')}
                                            labelPlacement='outside'
                                            type='date'
                                            value={
                                              settlementHeader?.shipEta || ''
                                            }
                                            onChange={(event) =>
                                              updateSettlementHeader(
                                                settlement.referenceNumber,
                                                { shipEta: event.target.value }
                                              )
                                            }
                                          />
                                          <Autocomplete
                                            size='sm'
                                            label={t('Lugar de arribo')}
                                            labelPlacement='outside'
                                            placeholder={t(
                                              'Selecciona mercado'
                                            )}
                                            selectedKey={
                                              settlementHeader?.destinationMarketId ||
                                              null
                                            }
                                            onSelectionChange={(key) =>
                                              updateSettlementHeader(
                                                settlement.referenceNumber,
                                                {
                                                  destinationMarketId: String(
                                                    key || ''
                                                  ),
                                                }
                                              )
                                            }
                                            defaultItems={sortedMarkets}
                                            isClearable
                                          >
                                            {(market) => {
                                              const label =
                                                getMarketLabel(market);
                                              return (
                                                <AutocompleteItem
                                                  key={String(market.id)}
                                                  textValue={label}
                                                >
                                                  {label}
                                                </AutocompleteItem>
                                              );
                                            }}
                                          </Autocomplete>
                                        </>
                                      ) : (
                                        <div className='xl:col-span-2'>
                                          <Input
                                            size='sm'
                                            label={t('Viaje')}
                                            labelPlacement='outside'
                                            placeholder={t(
                                              'Selecciona primero el tipo de transporte'
                                            )}
                                            isDisabled
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className='grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3'>
                                    <Autocomplete
                                      size='sm'
                                      label={t('Mercado de venta')}
                                      placeholder={t('Selecciona mercado')}
                                      selectedKey={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.marketId
                                          ? settlementHeaders[
                                              settlement.referenceNumber
                                            ].marketId
                                          : null
                                      }
                                      onSelectionChange={(key) => {
                                        const marketId = String(key || '');
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          {
                                            marketId,
                                            ...(transportType === 'A' ||
                                            transportType === 'T'
                                              ? {
                                                  destinationMarketId: marketId,
                                                }
                                              : {}),
                                          }
                                        );
                                      }}
                                      defaultItems={sortedMarkets}
                                      isClearable
                                    >
                                      {(market) => {
                                        const label = getMarketLabel(market);
                                        return (
                                          <AutocompleteItem
                                            key={String(market.id)}
                                            textValue={label}
                                          >
                                            {label}
                                          </AutocompleteItem>
                                        );
                                      }}
                                    </Autocomplete>
                                    <Autocomplete
                                      size='sm'
                                      label={t('Mercado comparable')}
                                      placeholder={t('Opcional')}
                                      selectedKey={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.comparableMarketId
                                          ? settlementHeaders[
                                              settlement.referenceNumber
                                            ].comparableMarketId
                                          : null
                                      }
                                      onSelectionChange={(key) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          {
                                            comparableMarketId: String(
                                              key || ''
                                            ),
                                          }
                                        )
                                      }
                                      defaultItems={sortedMarkets}
                                      isClearable
                                    >
                                      {(market) => {
                                        const label = getMarketLabel(market);
                                        return (
                                          <AutocompleteItem
                                            key={String(market.id)}
                                            textValue={label}
                                          >
                                            {label}
                                          </AutocompleteItem>
                                        );
                                      }}
                                    </Autocomplete>
                                    <Input
                                      size='sm'
                                      label={t('Fecha de venta')}
                                      type='date'
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.dateSold || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          { dateSold: event.target.value }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Tipo de cambio')}
                                      type='number'
                                      min='0'
                                      step='0.01'
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.exchangeRate || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          { exchangeRate: event.target.value }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Región')}
                                      placeholder={t('Región')}
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.region || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          { region: event.target.value }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Moneda')}
                                      placeholder={t('Moneda')}
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.currency || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          {
                                            currency:
                                              event.target.value.toUpperCase(),
                                          }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Tipo de negocio')}
                                      placeholder={t('Tipo de negocio')}
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.businessType || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          { businessType: event.target.value }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Orden de venta')}
                                      placeholder={t('Orden de venta')}
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.saleOrder || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          { saleOrder: event.target.value }
                                        )
                                      }
                                    />
                                    <Input
                                      size='sm'
                                      label={t('Incoterm original')}
                                      placeholder={t('Incoterm original')}
                                      value={
                                        settlementHeaders[
                                          settlement.referenceNumber
                                        ]?.originalIncoterm || ''
                                      }
                                      onChange={(event) =>
                                        updateSettlementHeader(
                                          settlement.referenceNumber,
                                          {
                                            originalIncoterm:
                                              event.target.value.toUpperCase(),
                                          }
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              );
                            }
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className='hidden'>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>{t('Grupos')}</p>
                      <p className='font-semibold'>
                        {formatNumber(groupedTotals.groups)}
                      </p>
                    </div>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>
                        {t('Venta bruta')}
                      </p>
                      <p className='font-semibold'>
                        {formatMoney(groupedTotals.grossSales, groupedCurrency)}
                      </p>
                    </div>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>{t('Gastos')}</p>
                      <p className='font-semibold'>
                        {formatMoney(
                          groupedTotals.totalExpenses,
                          groupedCurrency,
                          2
                        )}
                      </p>
                    </div>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>
                        {t('Líneas de venta')}
                      </p>
                      <p className='font-semibold'>
                        {formatNumber(groupedTotals.salesLines)}
                      </p>
                    </div>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>
                        {t('Costos detectados')}
                      </p>
                      <p className='font-semibold'>
                        {formatNumber(groupedTotals.costLines)}
                      </p>
                    </div>
                    <div className='rounded-md bg-default-50 p-3'>
                      <p className='text-xs text-default-400'>
                        {t('Neto leído')}
                      </p>
                      <p className='font-semibold'>
                        {formatMoney(
                          groupedTotals.netProceeds,
                          groupedCurrency
                        )}
                      </p>
                    </div>
                  </div>

                  <div className='hidden'>
                    <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
                      <p className='text-xs font-semibold uppercase text-default-400'>
                        {t('Cola de revisión')}
                      </p>
                      <Chip size='sm' variant='flat'>
                        {groupedStatusCounts.reviewed +
                          groupedStatusCounts.ready_to_apply}
                        /{groupedData.length} {t('revisados')}
                      </Chip>
                    </div>
                    <div className='grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3'>
                      {groupedData.map((group, groupIndex) => {
                        const groupKey = getGroupKey(group, groupIndex);
                        const selectedKey =
                          activeGroupKey || getGroupKey(groupedData[0], 0);
                        const isSelected = selectedKey === groupKey;
                        const processState = pdfGroupProcessing[groupKey];
                        const processColor = getProcessStatusColor(
                          processState?.status,
                          group.status
                        );
                        const groupCostTotal = group.costLines.reduce(
                          (total, line) => total + line.amount,
                          0
                        );
                        const stateClass =
                          group.status === 'reviewed'
                            ? 'border-primary bg-primary-50'
                            : processState?.status === 'error'
                              ? 'border-danger-200 bg-danger-50'
                              : processState?.status === 'success'
                                ? 'border-success-200 bg-success-50'
                                : processState?.status === 'processing'
                                  ? 'border-warning-200 bg-warning-50'
                                  : 'border-default-200 bg-content1';

                        return (
                          <button
                            key={groupKey}
                            type='button'
                            onClick={() => setActiveGroupKey(groupKey)}
                            className={`rounded-md border p-3 text-left transition ${
                              isSelected
                                ? `${stateClass} ring-2 ring-primary-100`
                                : `${stateClass} hover:border-primary-200`
                            }`}
                          >
                            <div className='flex items-start justify-between gap-2'>
                              <div className='min-w-0'>
                                <p className='truncate text-sm font-semibold text-default-800'>
                                  {group.title || group.groupId}
                                </p>
                                <p className='truncate text-xs text-default-500'>
                                  {group.header.species ||
                                    group.header.variety ||
                                    '-'}
                                </p>
                              </div>
                              <Chip
                                size='sm'
                                color={processColor}
                                variant='flat'
                              >
                                {processState?.status === 'processing' && (
                                  <Spinner size='sm' className='mr-1' />
                                )}
                                {t(
                                  getProcessStatusLabel(
                                    processState?.status,
                                    group.status
                                  )
                                )}
                              </Chip>
                            </div>
                            <div className='mt-3 grid grid-cols-3 gap-2 text-xs'>
                              <div>
                                <p className='text-default-400'>
                                  {t('Páginas')}
                                </p>
                                <p className='font-medium'>
                                  {group.pages.join(', ') || '-'}
                                </p>
                              </div>
                              <div>
                                <p className='text-default-400'>
                                  {t('Líneas')}
                                </p>
                                <p className='font-medium'>
                                  {group.salesLines.length}
                                </p>
                              </div>
                              <div>
                                <p className='text-default-400'>
                                  {t('Costos')}
                                </p>
                                <p className='font-medium'>
                                  {group.costLines.length}
                                </p>
                              </div>
                            </div>
                            <div className='mt-3 flex flex-wrap gap-2'>
                              <Chip size='sm' variant='flat'>
                                {formatMoney(
                                  group.totals.netProceeds,
                                  group.header.currency,
                                  2
                                )}
                              </Chip>
                              {groupCostTotal > 0 && (
                                <Chip size='sm' variant='flat' color='warning'>
                                  {formatMoney(
                                    groupCostTotal,
                                    group.header.currency,
                                    2
                                  )}{' '}
                                  {t('costos')}
                                </Chip>
                              )}
                              {group.warnings.length > 0 && (
                                <Chip size='sm' variant='flat' color='warning'>
                                  {group.warnings.length} {t('alertas')}
                                </Chip>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {activeStep === 'review' && (
                    <Tabs
                      selectedKey={selectedReviewGroupKey}
                      onSelectionChange={(key) => {
                        const nextKey = String(key);
                        const nextGroupIndex = groupedData.findIndex(
                          (candidate, candidateIndex) =>
                            getGroupKey(candidate, candidateIndex) === nextKey
                        );
                        setActiveGroupKey(nextKey);
                        const firstPage =
                          groupedData[nextGroupIndex]?.pages[0] || 1;
                        setPdfSidebarPage(firstPage);
                      }}
                      aria-label={t('Grupos del documento')}
                      variant='underlined'
                      classNames={{
                        tabList: 'gap-2',
                        tab: 'h-10 px-3 py-0',
                        cursor: 'bottom-0 h-0.5',
                        panel: 'p-0',
                      }}
                    >
                      {groupedData.map((group, groupIndex) => {
                        const groupKey = getGroupKey(group, groupIndex);
                        const processState = pdfGroupProcessing[groupKey];
                        const isGroupBusy =
                          processState?.status === 'pending' ||
                          processState?.status === 'processing';
                        const isGroupError = processState?.status === 'error';
                        const isGroupReviewed =
                          group.status === 'reviewed' ||
                          group.status === 'ready_to_apply';
                        const isGroupAssigned = Boolean(
                          groupAssignmentLocks[groupKey]
                        );
                        const tabSummary = groupTabSummaries.get(groupKey) || {
                          assignedQuantity: 0,
                          displayedQuantity: 0,
                          settlementQuantity: 0,
                        };
                        const tabQuantitiesMatch =
                          tabSummary.displayedQuantity ===
                          tabSummary.settlementQuantity;
                        const tabAssignmentCandidate =
                          !isGroupAssigned && !isGroupBusy && !isGroupError
                            ? getGroupAssignmentCandidate(groupIndex)
                            : null;
                        const canAssignFromTab = Boolean(
                          tabAssignmentCandidate?.canAssign
                        );
                        const tabTitle = (
                          <span className='inline-flex items-center gap-2 whitespace-nowrap'>
                            {isGroupBusy ? <Spinner size='sm' /> : null}
                            <span className='font-semibold'>
                              {`${t('Grupo')} ${groupIndex + 1}`}
                            </span>
                            {!isGroupBusy && !isGroupError && (
                              <span
                                title={t('Asignados / liquidados')}
                                className={`text-xs font-semibold ${
                                  tabQuantitiesMatch
                                    ? 'text-success-700 dark:text-success-400'
                                    : 'text-warning-700 dark:text-warning-400'
                                }`}
                              >
                                {formatNumber(tabSummary.displayedQuantity)}/
                                {formatNumber(tabSummary.settlementQuantity)}{' '}
                                {t('bultos')}
                              </span>
                            )}
                            <span
                              role='button'
                              tabIndex={isGroupBusy || isGroupError ? -1 : 0}
                              aria-pressed={isGroupReviewed}
                              aria-disabled={isGroupBusy || isGroupError}
                              aria-label={
                                isGroupReviewed
                                  ? t('Marcar grupo por revisar')
                                  : t('Marcar grupo revisado')
                              }
                              title={
                                isGroupReviewed
                                  ? t('Revisado: marcar por revisar')
                                  : t('Por revisar: marcar como revisado')
                              }
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs transition ${
                                isGroupReviewed
                                  ? 'bg-success-100 text-success-700 dark:bg-success-100/20 dark:text-success-400'
                                  : 'bg-warning-100 text-warning-700 dark:bg-warning-100/20 dark:text-warning-400'
                              } ${
                                isGroupBusy || isGroupError
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer hover:opacity-80'
                              }`}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (isGroupBusy || isGroupError) return;
                                handleGroupStatusChange(
                                  groupIndex,
                                  isGroupReviewed ? 'mapped' : 'reviewed'
                                );
                              }}
                              onKeyDown={(event) => {
                                if (
                                  (event.key !== 'Enter' &&
                                    event.key !== ' ') ||
                                  isGroupBusy ||
                                  isGroupError
                                ) {
                                  return;
                                }
                                event.preventDefault();
                                event.stopPropagation();
                                handleGroupStatusChange(
                                  groupIndex,
                                  isGroupReviewed ? 'mapped' : 'reviewed'
                                );
                              }}
                            >
                              <Icon icon='tabler:eye' />
                            </span>
                            <span
                              role='button'
                              tabIndex={canAssignFromTab ? 0 : -1}
                              aria-disabled={!canAssignFromTab}
                              aria-label={
                                isGroupAssigned
                                  ? t('Grupo asignado')
                                  : canAssignFromTab
                                    ? t('Marcar como asignado')
                                    : t('Grupo sin asignar')
                              }
                              title={
                                isGroupAssigned
                                  ? t('Asignado')
                                  : canAssignFromTab
                                    ? t('Marcar como asignado')
                                    : tabAssignmentCandidate?.reason ||
                                      t('Sin asignar')
                              }
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs transition ${
                                isGroupAssigned
                                  ? 'bg-success-100 text-success-700 dark:bg-success-100/20 dark:text-success-400'
                                  : 'bg-warning-100 text-warning-700 dark:bg-warning-100/20 dark:text-warning-400'
                              } ${
                                isGroupAssigned
                                  ? 'cursor-default'
                                  : canAssignFromTab
                                    ? 'cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-success-400'
                                    : 'cursor-not-allowed opacity-60'
                              }`}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                if (!canAssignFromTab) return;
                                handleAssignGroup(groupIndex);
                              }}
                              onKeyDown={(event) => {
                                if (
                                  (event.key !== 'Enter' &&
                                    event.key !== ' ') ||
                                  !canAssignFromTab
                                ) {
                                  return;
                                }
                                event.preventDefault();
                                event.stopPropagation();
                                handleAssignGroup(groupIndex);
                              }}
                            >
                              <Icon icon='tabler:check' />
                            </span>
                          </span>
                        );

                        if (groupKey !== selectedReviewGroupKey) {
                          return (
                            <Tab key={groupKey} title={tabTitle}>
                              <span />
                            </Tab>
                          );
                        }

                        const groupMapping =
                          groupMappings[groupKey] || EMPTY_GROUP_MAPPING;
                        const groupMatches = getGroupShipmentMatches(group);
                        const varietyOptions = uniqueValues([
                          ...group.salesLines.map((line) => line.variety),
                          group.header.variety,
                        ]);
                        const caliberOptions = uniqueValues(
                          group.salesLines.map((line) => line.caliber)
                        );
                        const colorOptions = uniqueValues(
                          group.salesLines.map((line) => line.color)
                        );
                        const costTotals = group.costLines.reduce(
                          (totals, line) => ({
                            ...totals,
                            [line.mappedCategory]:
                              totals[line.mappedCategory] + line.amount,
                          }),
                          {
                            commission: 0,
                            tax: 0,
                            freight: 0,
                            repacking: 0,
                            other_costs: 0,
                          } as Record<SettlementCostCategory, number>
                        );
                        const salesLineTotal = group.salesLines.reduce(
                          (total, line) => total + (Number(line.amount) || 0),
                          0
                        );
                        const salesLineQuantity = group.salesLines.reduce(
                          (total, line) => total + (Number(line.quantity) || 0),
                          0
                        );
                        const costLineTotal = group.costLines.reduce(
                          (total, line) => total + (Number(line.amount) || 0),
                          0
                        );
                        const groupCurrency =
                          normalizeCostCurrency(group.header.currency) || 'USD';
                        const costLineTotalsByCurrency = group.costLines.reduce(
                          (totals, line) => {
                            const currency =
                              normalizeCostCurrency(line.currency) ||
                              groupCurrency;
                            totals[currency] =
                              (totals[currency] || 0) +
                              (Number(line.amount) || 0);
                            return totals;
                          },
                          {} as Record<string, number>
                        );
                        const costLineCurrencyTotals = Object.entries(
                          costLineTotalsByCurrency
                        );
                        const canCalculateNet =
                          costLineCurrencyTotals.length === 0 ||
                          costLineCurrencyTotals.every(
                            ([currency]) => currency === groupCurrency
                          );
                        const calculatedNet = salesLineTotal - costLineTotal;
                        const isEditingSalesLines =
                          editingSalesGroupKey === groupKey;
                        const isEditingCostLines =
                          editingCostGroupKey === groupKey;
                        const assignedQuantity = tabSummary.assignedQuantity;
                        const groupWorkingShipments =
                          selectedReviewWorkingShipments;
                        const groupLineAllocation =
                          selectedReviewLineAllocation!;
                        const assignmentCandidate = getGroupAssignmentCandidate(
                          groupIndex,
                          groupLineAllocation
                        );
                        const scopedShipmentBoxes =
                          selectedReviewScopedShipments;
                        const {
                          manuallyIncludedBoxes,
                          manuallyExcludedBoxes,
                          manualIncludeOptions,
                          manualExcludeOptions,
                        } = selectedReviewManualBoxData;
                        const manualQuantityDelta = Object.entries(
                          groupMapping.manualBoxQuantities || {}
                        ).reduce(
                          (totals, [boxId, manualQuantity]) => {
                            const box = effectiveShipmentsById.get(boxId);
                            if (!box) return totals;
                            const filteredQuantity = matchesShipmentBoxScope(
                              box,
                              groupMapping.scope,
                              mappedFormats
                            )
                              ? Number(box.quantity) || 0
                              : 0;
                            const difference =
                              (Number(manualQuantity) || 0) - filteredQuantity;
                            return {
                              added:
                                totals.added +
                                (difference > 0 ? difference : 0),
                              removed:
                                totals.removed +
                                (difference < 0 ? Math.abs(difference) : 0),
                            };
                          },
                          { added: 0, removed: 0 }
                        );
                        const manuallyAddedQuantity =
                          manualQuantityDelta.added +
                          manuallyIncludedBoxes.reduce(
                            (total, box) => total + (Number(box.quantity) || 0),
                            0
                          );
                        const manuallyRemovedQuantity =
                          manualQuantityDelta.removed +
                          manuallyExcludedBoxes.reduce(
                            (total, box) => total + (Number(box.quantity) || 0),
                            0
                          );
                        const boxTransferDraft = boxTransferDrafts[
                          groupKey
                        ] || {
                          shipmentBoxId: '',
                          toGroupKey: '',
                          quantity: '',
                        };
                        const groupBoxTransfers = boxTransfers.filter(
                          (transfer) =>
                            transfer.fromGroupKey === groupKey ||
                            transfer.toGroupKey === groupKey
                        );
                        const scopedReferences = uniqueValues(
                          scopedShipmentBoxes.map((box) => box.reference_number)
                        );
                        const scopedBoxCount = scopedShipmentBoxes.reduce(
                          (total, box) => total + (Number(box.quantity) || 0),
                          0
                        );
                        const settlementBoxCount =
                          salesLineQuantity || Number(group.totals.cases) || 0;
                        const boxDifference =
                          scopedBoxCount - settlementBoxCount;
                        const boxesMatch = boxDifference === 0;
                        const primaryScope = emptyShipmentBoxScope();
                        PRIMARY_SCOPE_FIELDS.forEach((field) => {
                          primaryScope[field] = groupMapping.scope[field];
                        });
                        const primaryScopedShipmentBoxes =
                          groupWorkingShipments.filter((box) =>
                            matchesShipmentBoxScope(
                              box,
                              primaryScope,
                              mappedFormats
                            )
                          );
                        const scopeOptions = {
                          shipNames: getCascadingScopeFieldValues(
                            groupWorkingShipments,
                            groupMapping.scope,
                            'shipNames',
                            mappedFormats
                          ),
                          referenceNumbers: getCascadingScopeFieldValues(
                            groupWorkingShipments,
                            groupMapping.scope,
                            'referenceNumbers',
                            mappedFormats
                          ),
                          containerNumbers: getCascadingScopeFieldValues(
                            groupWorkingShipments,
                            groupMapping.scope,
                            'containerNumbers',
                            mappedFormats
                          ),
                          varieties: uniqueValues(
                            primaryScopedShipmentBoxes.map((box) => box.variety)
                          ),
                          labeledVarieties: uniqueValues(
                            primaryScopedShipmentBoxes.map(
                              (box) => box.labeled_variety
                            )
                          ),
                          calibers: uniqueValues(
                            primaryScopedShipmentBoxes.map((box) => box.caliber)
                          ),
                          colors: uniqueValues(
                            primaryScopedShipmentBoxes.map((box) => box.color)
                          ),
                          categories: uniqueValues(
                            primaryScopedShipmentBoxes.map(
                              (box) => box.category
                            )
                          ),
                          labels: uniqueValues(
                            primaryScopedShipmentBoxes.map(
                              (box) => box.box_label
                            )
                          ),
                          formats: uniqueValues(
                            primaryScopedShipmentBoxes.map((box) =>
                              getShipmentFormat(box, mappedFormats)
                            )
                          ),
                        };
                        const reconciliationFields: Array<{
                          field: keyof ShipmentBoxScope;
                          label: string;
                        }> = [
                          {
                            field: 'referenceNumbers',
                            label: t('Referencias'),
                          },
                          {
                            field: 'containerNumbers',
                            label: t('Contenedores'),
                          },
                          {
                            field: 'varieties',
                            label: t('Variedad Real'),
                          },
                          {
                            field: 'labeledVarieties',
                            label: t('Variedad Etiquetada'),
                          },
                          { field: 'calibers', label: t('Calibre') },
                          { field: 'colors', label: t('Color') },
                          { field: 'categories', label: t('Categoría') },
                          { field: 'labels', label: t('Etiqueta') },
                          { field: 'formats', label: t('Formato') },
                        ];
                        const scopeSuggestions =
                          reconciliationAssistantGroupKey === groupKey &&
                          !boxesMatch
                            ? getScopeSuggestions(
                                groupWorkingShipments,
                                groupMapping.scope,
                                settlementBoxCount,
                                mappedFormats,
                                reconciliationFields
                              )
                            : [];

                        return (
                          <Tab key={groupKey} title={tabTitle}>
                            <div
                              className={`grid gap-4 pt-3 ${
                                isPdfSidebarOpen && pdfPreviewUrl
                                  ? 'xl:grid-cols-[minmax(0,1fr)_minmax(420px,42%)]'
                                  : 'grid-cols-1'
                              }`}
                            >
                              <div className='min-w-0 space-y-4'>
                                {!isPdfSidebarOpen && pdfPreviewUrl && (
                                  <div className='flex justify-end'>
                                    <Button
                                      size='sm'
                                      variant='flat'
                                      startContent={<Icon icon='tabler:file-type-pdf' />}
                                      onPress={() => setIsPdfSidebarOpen(true)}
                                    >
                                      {t('Mostrar PDF original')}
                                    </Button>
                                  </div>
                                )}
                                {!isGroupBusy && !isGroupError && (
                                  <div
                                    className={`flex flex-wrap items-center justify-between gap-3 rounded-md border p-3 ${
                                      isGroupAssigned
                                        ? 'border-primary-300 bg-primary-50/60 dark:bg-primary-100/10'
                                        : assignmentCandidate.canAssign
                                          ? 'border-success-300 bg-success-50/60 dark:bg-success-100/10'
                                          : 'border-warning-200 bg-warning-50/60 dark:bg-warning-100/10'
                                    }`}
                                  >
                                    <div>
                                      <p className='text-sm font-semibold'>
                                        {isGroupAssigned
                                          ? t(
                                              'Cajas reservadas para este grupo'
                                            )
                                          : assignmentCandidate.canAssign
                                            ? t('Grupo listo para asignar')
                                            : t('Grupo aún sin asignar')}
                                      </p>
                                      <p className='text-xs text-default-500'>
                                        {isGroupAssigned
                                          ? `${formatNumber(assignedQuantity)} ${t('bultos no estarán disponibles en otros grupos')}`
                                          : assignmentCandidate.canAssign
                                            ? t(
                                                'Todas las filas cuadran. Confirma para descontar estas cajas de los demás grupos.'
                                              )
                                            : assignmentCandidate.reason}
                                      </p>
                                      {!isGroupAssigned &&
                                        assignmentCandidate.unmappedBoxType && (
                                          <Select
                                            className='mt-2 min-w-[260px] max-w-sm'
                                            size='sm'
                                            label={`${t('Código de caja')}: ${assignmentCandidate.unmappedBoxType}`}
                                            placeholder={t(
                                              'Seleccionar formato'
                                            )}
                                            isLoading={
                                              savingBoxTypeMapping ===
                                              assignmentCandidate.unmappedBoxType
                                            }
                                            isDisabled={Boolean(
                                              savingBoxTypeMapping
                                            )}
                                            onSelectionChange={(keys) => {
                                              const format = String(
                                                Array.from(keys)[0] || ''
                                              ) as (typeof VALID_FORMATS)[number];
                                              if (format) {
                                                void handleSaveBoxTypeMapping(
                                                  assignmentCandidate.unmappedBoxType,
                                                  format
                                                );
                                              }
                                            }}
                                          >
                                            {VALID_FORMATS.map((format) => (
                                              <SelectItem
                                                key={format}
                                                value={format}
                                              >
                                                {format}
                                              </SelectItem>
                                            ))}
                                          </Select>
                                        )}
                                    </div>
                                    <Button
                                      size='sm'
                                      color={
                                        isGroupAssigned ? 'default' : 'success'
                                      }
                                      variant={
                                        isGroupAssigned ? 'bordered' : 'flat'
                                      }
                                      isDisabled={
                                        !isGroupAssigned &&
                                        !assignmentCandidate.canAssign
                                      }
                                      onPress={() =>
                                        isGroupAssigned
                                          ? handleReleaseGroupAssignment(
                                              groupIndex
                                            )
                                          : handleAssignGroup(groupIndex)
                                      }
                                    >
                                      {isGroupAssigned
                                        ? t('Liberar asignación')
                                        : t('Marcar como asignado')}
                                    </Button>
                                  </div>
                                )}
                                {!isGroupBusy && !isGroupError && (
                                  <details
                                    open={
                                      reviewSectionVisibility[
                                        `${groupKey}:filters`
                                      ] !== false
                                    }
                                    onToggle={(event) =>
                                      handleReviewSectionToggle(
                                        `${groupKey}:filters`,
                                        event.currentTarget.open
                                      )
                                    }
                                    className='group rounded-md border border-primary-200 bg-primary-50/50 p-3 dark:bg-primary-100/10'
                                  >
                                    <summary className='mb-3 cursor-pointer list-none'>
                                      <div className='flex flex-wrap items-center justify-between gap-2'>
                                        <div className='flex min-w-0 items-start gap-2'>
                                          <Icon icon='tabler:chevron-right' className='mt-1 shrink-0 text-xs text-default-400 transition-transform group-open:rotate-90' />
                                          <div>
                                            <p className='text-sm font-semibold text-foreground'>
                                              {t('Filtros del grupo')}
                                            </p>
                                          </div>
                                        </div>
                                        <div
                                          className={`min-w-[290px] rounded-lg border px-4 py-3 ${
                                            boxesMatch
                                              ? 'border-success-300 bg-success-50/80 dark:bg-success-100/10'
                                              : 'border-warning-300 bg-warning-50/80 dark:bg-warning-100/10'
                                          }`}
                                        >
                                          <p className='flex flex-wrap items-baseline gap-x-2 text-sm font-semibold'>
                                            <span>
                                              {t('Seleccionados')}{' '}
                                              <span className='text-sm'>
                                                {formatNumber(scopedBoxCount)}
                                              </span>
                                            </span>
                                            <span className='text-default-400'>
                                              /
                                            </span>
                                            <span>
                                              {t('Liquidados')}{' '}
                                              <span className='text-sm'>
                                                {formatNumber(
                                                  settlementBoxCount
                                                )}
                                              </span>
                                            </span>
                                          </p>
                                          <p
                                            className={`mt-1 text-sm font-semibold ${
                                              boxesMatch
                                                ? 'text-success-700 dark:text-success-400'
                                                : 'text-warning-700 dark:text-warning-400'
                                            }`}
                                          >
                                            {boxesMatch
                                              ? t(
                                                  'Los bultos seleccionados coinciden con los liquidados.'
                                                )
                                              : boxDifference > 0
                                                ? `${t('Te sobran')} ${formatNumber(boxDifference)} ${t('bultos')}.`
                                                : `${t('Te faltan')} ${formatNumber(Math.abs(boxDifference))} ${t('bultos')}.`}
                                          </p>
                                        </div>
                                      </div>
                                    </summary>
                                    {!isGroupAssigned && !boxesMatch && (
                                      <div className='order-5 mt-3 rounded-md border border-secondary-200 bg-secondary-50/40 p-2 dark:bg-secondary-100/10'>
                                        <div className='flex flex-wrap items-center justify-between gap-2'>
                                          <div>
                                            <p className='text-xs font-semibold text-secondary-700 dark:text-secondary-300'>
                                              {t('Asistente de conciliación')}
                                            </p>
                                            <p className='text-xs text-default-500'>
                                              {boxDifference > 0
                                                ? `${t('Busca filtros que reduzcan')} ${formatNumber(
                                                    boxDifference
                                                  )} ${t('bultos')}.`
                                                : `${t('Busca filtros alternativos para recuperar')} ${formatNumber(
                                                    Math.abs(boxDifference)
                                                  )} ${t('bultos')}.`}
                                            </p>
                                          </div>
                                          <Button
                                            size='sm'
                                            variant='flat'
                                            onPress={() =>
                                              setReconciliationAssistantGroupKey(
                                                reconciliationAssistantGroupKey ===
                                                  groupKey
                                                  ? ''
                                                  : groupKey
                                              )
                                            }
                                          >
                                            {reconciliationAssistantGroupKey ===
                                            groupKey
                                              ? t('Ocultar sugerencias')
                                              : t('Buscar filtros faltantes')}
                                          </Button>
                                        </div>
                                        {reconciliationAssistantGroupKey ===
                                          groupKey && (
                                          <div className='mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2'>
                                            {scopeSuggestions.map(
                                              (suggestion) => (
                                                <button
                                                  key={`${suggestion.field}-${suggestion.values.join('|')}`}
                                                  type='button'
                                                  className={`rounded-md border p-2 text-left transition hover:border-secondary-400 ${
                                                    suggestion.difference === 0
                                                      ? 'border-success-300 bg-success-50/60 dark:bg-success-100/10'
                                                      : 'border-default-200 bg-content1'
                                                  }`}
                                                  onClick={() => {
                                                    handleGroupScopeChange(
                                                      groupIndex,
                                                      {
                                                        [suggestion.field]:
                                                          suggestion.values,
                                                      }
                                                    );
                                                    if (
                                                      suggestion.difference ===
                                                      0
                                                    ) {
                                                      setReconciliationAssistantGroupKey(
                                                        ''
                                                      );
                                                    }
                                                  }}
                                                >
                                                  <div className='flex items-center justify-between gap-2'>
                                                    <span className='text-xs font-semibold text-foreground'>
                                                      {suggestion.label}
                                                    </span>
                                                    <Chip
                                                      size='sm'
                                                      color={
                                                        suggestion.difference ===
                                                        0
                                                          ? 'success'
                                                          : 'warning'
                                                      }
                                                      variant='flat'
                                                    >
                                                      {formatNumber(
                                                        suggestion.boxes
                                                      )}{' '}
                                                      {t('bultos')}
                                                    </Chip>
                                                  </div>
                                                  <p className='mt-1 line-clamp-2 text-xs text-default-600'>
                                                    {suggestion.values.join(
                                                      ', '
                                                    )}
                                                  </p>
                                                  <p
                                                    className={`mt-1 text-xs ${
                                                      suggestion.difference ===
                                                      0
                                                        ? 'text-success'
                                                        : 'text-warning-700 dark:text-warning-400'
                                                    }`}
                                                  >
                                                    {suggestion.difference === 0
                                                      ? t('Match exacto')
                                                      : `${t('Diferencia')}: ${
                                                          suggestion.difference >
                                                          0
                                                            ? '+'
                                                            : ''
                                                        }${formatNumber(
                                                          suggestion.difference
                                                        )}`}
                                                  </p>
                                                </button>
                                              )
                                            )}
                                            {scopeSuggestions.length === 0 && (
                                              <p className='col-span-full rounded-md bg-content1 p-3 text-xs text-default-500'>
                                                {t(
                                                  'No se encontró una combinación simple. Prueba ajustando nave, referencias o calibres antes de volver a buscar.'
                                                )}
                                              </p>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    <div className='grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4'>
                                      {(
                                        [
                                          [
                                            'shipNames',
                                            t('Nave'),
                                            scopeOptions.shipNames,
                                          ],
                                          [
                                            'referenceNumbers',
                                            t('Referencias'),
                                            scopeOptions.referenceNumbers,
                                          ],
                                          [
                                            'containerNumbers',
                                            t('Contenedores'),
                                            scopeOptions.containerNumbers,
                                          ],
                                          [
                                            'varieties',
                                            t('Variedad Real'),
                                            scopeOptions.varieties,
                                          ],
                                          [
                                            'labeledVarieties',
                                            t('Variedad Etiquetada'),
                                            scopeOptions.labeledVarieties,
                                          ],
                                          [
                                            'calibers',
                                            t('Calibre'),
                                            scopeOptions.calibers,
                                          ],
                                          [
                                            'colors',
                                            t('Color'),
                                            scopeOptions.colors,
                                          ],
                                          [
                                            'categories',
                                            t('Categoría'),
                                            scopeOptions.categories,
                                          ],
                                          [
                                            'labels',
                                            t('Etiqueta'),
                                            scopeOptions.labels,
                                          ],
                                          [
                                            'formats',
                                            t('Formato'),
                                            scopeOptions.formats,
                                          ],
                                        ] as Array<
                                          [
                                            keyof ShipmentBoxScope,
                                            string,
                                            string[],
                                          ]
                                        >
                                      ).map(([field, label, options]) => (
                                        <div key={field} className='space-y-1'>
                                          <CustomAutocomplete
                                            label={label}
                                            placeholder={t('Buscar y agregar')}
                                            closeOnSelect={false}
                                            isDisabled={isGroupAssigned}
                                            contentInsideInput={
                                              groupMapping.scope[field]
                                                .length ? (
                                                <div className='flex flex-wrap gap-1'>
                                                  {groupMapping.scope[field]
                                                    .slice()
                                                    .sort((a, b) =>
                                                      a.localeCompare(b, 'es')
                                                    )
                                                    .map((value) => (
                                                      <Chip
                                                        key={value}
                                                        size='sm'
                                                        variant='flat'
                                                        onClose={
                                                          isGroupAssigned
                                                            ? undefined
                                                            : () =>
                                                                handleGroupScopeChange(
                                                                  groupIndex,
                                                                  {
                                                                    [field]:
                                                                      groupMapping.scope[
                                                                        field
                                                                      ].filter(
                                                                        (
                                                                          item
                                                                        ) =>
                                                                          item !==
                                                                          value
                                                                      ),
                                                                  }
                                                                )
                                                        }
                                                      >
                                                        {value}
                                                      </Chip>
                                                    ))}
                                                </div>
                                              ) : null
                                            }
                                            options={[...options]
                                              .sort((a, b) =>
                                                a.localeCompare(b, 'es')
                                              )
                                              .map((option) => ({
                                                key: option,
                                                value: option,
                                                label: option,
                                              }))}
                                            onSelectionChange={(key) => {
                                              if (!key) return;
                                              const values =
                                                groupMapping.scope[field];
                                              const value = String(key);
                                              handleGroupScopeChange(
                                                groupIndex,
                                                {
                                                  [field]: values.includes(
                                                    value
                                                  )
                                                    ? values
                                                    : [...values, value],
                                                }
                                              );
                                            }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                    <details
                                      open={
                                        reviewSectionVisibility[
                                          `${groupKey}:manual-boxes`
                                        ] ??
                                        (manuallyIncludedBoxes.length > 0 ||
                                          manuallyExcludedBoxes.length > 0 ||
                                          groupBoxTransfers.length > 0)
                                      }
                                      onToggle={(event) =>
                                        handleReviewSectionToggle(
                                          `${groupKey}:manual-boxes`,
                                          event.currentTarget.open
                                        )
                                      }
                                      className='group mt-3 rounded-md border border-default-200 bg-content1'
                                    >
                                      <summary className='cursor-pointer list-none px-3 py-2'>
                                        <div className='flex flex-wrap items-center justify-between gap-2'>
                                          <div className='flex items-center gap-2'>
                                            <Icon icon='tabler:chevron-right' className='shrink-0 text-xs text-default-400 transition-transform group-open:rotate-90' />
                                            <div>
                                              <p className='text-sm font-semibold text-foreground'>
                                                {t('Ajustes manuales de cajas')}
                                              </p>
                                              <p className='text-xs text-default-500'>
                                                {t(
                                                  'Incluye o excluye filas específicas de shipment_boxes para rebalancear este grupo.'
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                          <div className='flex gap-2'>
                                            <Chip
                                              size='sm'
                                              color='success'
                                              variant='flat'
                                            >
                                              +
                                              {formatNumber(
                                                manuallyAddedQuantity
                                              )}{' '}
                                              {t('bultos')}
                                            </Chip>
                                            <Chip
                                              size='sm'
                                              color='danger'
                                              variant='flat'
                                            >
                                              −
                                              {formatNumber(
                                                manuallyRemovedQuantity
                                              )}{' '}
                                              {t('bultos')}
                                            </Chip>
                                          </div>
                                        </div>
                                      </summary>
                                      <div className='border-t border-default-200 p-3'>
                                        {isGroupAssigned ? (
                                          <div className='flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning-200 bg-warning-50/60 p-3 dark:bg-warning-100/10'>
                                            <p className='text-sm text-warning-800 dark:text-warning-300'>
                                              {t(
                                                'Libera la asignación para modificar las cajas de este grupo.'
                                              )}
                                            </p>
                                            <Button
                                              size='sm'
                                              variant='flat'
                                              onPress={() =>
                                                handleReleaseGroupAssignment(
                                                  groupIndex
                                                )
                                              }
                                            >
                                              {t('Liberar y ajustar')}
                                            </Button>
                                          </div>
                                        ) : (
                                          <div className='space-y-4'>
                                            <div className='rounded-md border border-secondary-200 bg-secondary-50/40 p-3 dark:bg-secondary-100/10'>
                                              <div className='mb-3'>
                                                <p className='text-sm font-semibold text-secondary-700 dark:text-secondary-300'>
                                                  {t(
                                                    'Transferir parcialmente a otro grupo'
                                                  )}
                                                </p>
                                                <p className='text-xs text-default-500'>
                                                  {t(
                                                    'Selecciona una fila, indica cuántos bultos mover y el grupo que los recibirá.'
                                                  )}
                                                </p>
                                              </div>
                                              <div className='grid grid-cols-1 items-end gap-2 lg:grid-cols-[minmax(0,2fr)_140px_minmax(180px,1fr)_auto]'>
                                                <CustomAutocomplete
                                                  label={t(
                                                    'Fila de shipment_boxes'
                                                  )}
                                                  placeholder={t(
                                                    'Buscar fila para transferir'
                                                  )}
                                                  portal
                                                  selectedKey={
                                                    boxTransferDraft.shipmentBoxId ||
                                                    undefined
                                                  }
                                                  options={scopedShipmentBoxes
                                                    .slice()
                                                    .sort((a, b) =>
                                                      getShipmentBoxAdjustmentLabel(
                                                        a,
                                                        mappedFormats
                                                      ).localeCompare(
                                                        getShipmentBoxAdjustmentLabel(
                                                          b,
                                                          mappedFormats
                                                        ),
                                                        'es'
                                                      )
                                                    )
                                                    .map((box) => ({
                                                      key: String(box.id),
                                                      value: String(box.id),
                                                      label:
                                                        getShipmentBoxAdjustmentLabel(
                                                          box,
                                                          mappedFormats
                                                        ),
                                                    }))}
                                                  onSelectionChange={(key) =>
                                                    setBoxTransferDrafts(
                                                      (current) => ({
                                                        ...current,
                                                        [groupKey]: {
                                                          ...boxTransferDraft,
                                                          shipmentBoxId: String(
                                                            key || ''
                                                          ),
                                                        },
                                                      })
                                                    )
                                                  }
                                                />
                                                <Input
                                                  type='number'
                                                  min={1}
                                                  step={1}
                                                  label={t('Bultos')}
                                                  placeholder='0'
                                                  value={
                                                    boxTransferDraft.quantity
                                                  }
                                                  onValueChange={(quantity) =>
                                                    setBoxTransferDrafts(
                                                      (current) => ({
                                                        ...current,
                                                        [groupKey]: {
                                                          ...boxTransferDraft,
                                                          quantity,
                                                        },
                                                      })
                                                    )
                                                  }
                                                />
                                                <Select
                                                  label={t('Grupo destino')}
                                                  placeholder={t(
                                                    'Seleccionar grupo'
                                                  )}
                                                  selectedKeys={
                                                    boxTransferDraft.toGroupKey
                                                      ? [
                                                          boxTransferDraft.toGroupKey,
                                                        ]
                                                      : []
                                                  }
                                                  onSelectionChange={(keys) =>
                                                    setBoxTransferDrafts(
                                                      (current) => ({
                                                        ...current,
                                                        [groupKey]: {
                                                          ...boxTransferDraft,
                                                          toGroupKey: String(
                                                            Array.from(
                                                              keys
                                                            )[0] || ''
                                                          ),
                                                        },
                                                      })
                                                    )
                                                  }
                                                >
                                                  {groupedData.flatMap(
                                                    (
                                                      targetGroup,
                                                      targetGroupIndex
                                                    ) => {
                                                      const targetGroupKey =
                                                        getGroupKey(
                                                          targetGroup,
                                                          targetGroupIndex
                                                        );
                                                      return targetGroupKey ===
                                                        groupKey
                                                        ? []
                                                        : [
                                                            <SelectItem
                                                              key={
                                                                targetGroupKey
                                                              }
                                                              value={
                                                                targetGroupKey
                                                              }
                                                            >
                                                              {`${t('Grupo')} ${targetGroupIndex + 1}${groupAssignmentLocks[targetGroupKey] ? ` · ${t('Asignado')}` : ''}`}
                                                            </SelectItem>,
                                                          ];
                                                    }
                                                  )}
                                                </Select>
                                                <Button
                                                  color='primary'
                                                  onPress={() =>
                                                    handleTransferBoxes(
                                                      groupIndex
                                                    )
                                                  }
                                                >
                                                  {t('Enviar bultos')}
                                                </Button>
                                              </div>
                                              {groupBoxTransfers.length > 0 && (
                                                <div className='mt-3 space-y-2'>
                                                  {groupBoxTransfers.map(
                                                    (transfer) => {
                                                      const isOutgoing =
                                                        transfer.fromGroupKey ===
                                                        groupKey;
                                                      const relatedGroupKey =
                                                        isOutgoing
                                                          ? transfer.toGroupKey
                                                          : transfer.fromGroupKey;
                                                      const relatedGroupIndex =
                                                        groupedData.findIndex(
                                                          (
                                                            relatedGroup,
                                                            relatedIndex
                                                          ) =>
                                                            getGroupKey(
                                                              relatedGroup,
                                                              relatedIndex
                                                            ) ===
                                                            relatedGroupKey
                                                        );
                                                      const transferBox =
                                                        effectiveShipments.find(
                                                          (box) =>
                                                            String(box.id) ===
                                                            transfer.shipmentBoxId
                                                        );
                                                      return (
                                                        <div
                                                          key={transfer.id}
                                                          className='flex items-center gap-2 rounded-md border border-default-200 bg-content1 p-2'
                                                        >
                                                          <Chip
                                                            size='sm'
                                                            color={
                                                              isOutgoing
                                                                ? 'warning'
                                                                : 'success'
                                                            }
                                                            variant='flat'
                                                          >
                                                            {isOutgoing
                                                              ? t('Enviados')
                                                              : t('Recibidos')}
                                                          </Chip>
                                                          <p className='min-w-0 flex-1 text-xs text-foreground'>
                                                            {formatNumber(
                                                              transfer.quantity
                                                            )}{' '}
                                                            {t('bultos')} ·{' '}
                                                            {isOutgoing
                                                              ? t('hacia')
                                                              : t('desde')}{' '}
                                                            {t('Grupo')}{' '}
                                                            {relatedGroupIndex +
                                                              1}
                                                            {transferBox
                                                              ? ` · ${getShipmentBoxAdjustmentLabel(
                                                                  {
                                                                    ...transferBox,
                                                                    quantity:
                                                                      transfer.quantity,
                                                                  },
                                                                  mappedFormats
                                                                )}`
                                                              : ''}
                                                          </p>
                                                          <button
                                                            type='button'
                                                            aria-label={t(
                                                              'Deshacer transferencia'
                                                            )}
                                                            className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-danger transition hover:bg-danger-100'
                                                            onClick={() =>
                                                              handleRemoveBoxTransfer(
                                                                transfer.id
                                                              )
                                                            }
                                                          >
                                                            <Icon icon='tabler:x' />
                                                          </button>
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                            <div>
                                              <p className='mb-2 text-xs font-semibold uppercase text-default-400'>
                                                {t('Ajustar filas completas')}
                                              </p>
                                              <div className='grid grid-cols-1 gap-4 xl:grid-cols-2'>
                                                <div className='space-y-2'>
                                                  <CustomAutocomplete
                                                    label={t(
                                                      'Excluir de este grupo'
                                                    )}
                                                    placeholder={t(
                                                      'Buscar fila seleccionada'
                                                    )}
                                                    closeOnSelect={false}
                                                    portal
                                                    options={manualExcludeOptions.map(
                                                      (box) => ({
                                                        key: String(box.id),
                                                        value: String(box.id),
                                                        label:
                                                          getShipmentBoxAdjustmentLabel(
                                                            box,
                                                            mappedFormats
                                                          ),
                                                      })
                                                    )}
                                                    onSelectionChange={(
                                                      key
                                                    ) => {
                                                      if (!key) return;
                                                      handleManualBoxAdjustment(
                                                        groupIndex,
                                                        String(key),
                                                        'exclude'
                                                      );
                                                    }}
                                                  />
                                                  <p className='text-xs text-default-500'>
                                                    {t(
                                                      'La fila dejará de contar aunque cumpla los filtros del grupo.'
                                                    )}
                                                  </p>
                                                  {manuallyExcludedBoxes.map(
                                                    (box) => (
                                                      <div
                                                        key={String(box.id)}
                                                        className='flex items-center gap-2 rounded-md border border-danger-200 bg-danger-50/50 p-2 dark:bg-danger-100/10'
                                                      >
                                                        <p className='min-w-0 flex-1 text-xs text-foreground'>
                                                          {getShipmentBoxAdjustmentLabel(
                                                            box,
                                                            mappedFormats
                                                          )}
                                                        </p>
                                                        <button
                                                          type='button'
                                                          aria-label={t(
                                                            'Quitar exclusión'
                                                          )}
                                                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-danger transition hover:bg-danger-100'
                                                          onClick={() =>
                                                            handleManualBoxAdjustment(
                                                              groupIndex,
                                                              String(box.id),
                                                              'clear'
                                                            )
                                                          }
                                                        >
                                                          <Icon icon='tabler:x' />
                                                        </button>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                                <div className='space-y-2'>
                                                  <CustomAutocomplete
                                                    label={t(
                                                      'Incluir en este grupo'
                                                    )}
                                                    placeholder={t(
                                                      'Buscar fila disponible'
                                                    )}
                                                    closeOnSelect={false}
                                                    portal
                                                    options={manualIncludeOptions.map(
                                                      (box) => ({
                                                        key: String(box.id),
                                                        value: String(box.id),
                                                        label:
                                                          getShipmentBoxAdjustmentLabel(
                                                            box,
                                                            mappedFormats
                                                          ),
                                                      })
                                                    )}
                                                    onSelectionChange={(
                                                      key
                                                    ) => {
                                                      if (!key) return;
                                                      handleManualBoxAdjustment(
                                                        groupIndex,
                                                        String(key),
                                                        'include'
                                                      );
                                                    }}
                                                  />
                                                  <p className='text-xs text-default-500'>
                                                    {t(
                                                      'La fila contará aunque no cumpla los filtros del grupo.'
                                                    )}
                                                  </p>
                                                  {manuallyIncludedBoxes.map(
                                                    (box) => (
                                                      <div
                                                        key={String(box.id)}
                                                        className='flex items-center gap-2 rounded-md border border-success-200 bg-success-50/50 p-2 dark:bg-success-100/10'
                                                      >
                                                        <p className='min-w-0 flex-1 text-xs text-foreground'>
                                                          {getShipmentBoxAdjustmentLabel(
                                                            box,
                                                            mappedFormats
                                                          )}
                                                        </p>
                                                        <button
                                                          type='button'
                                                          aria-label={t(
                                                            'Quitar inclusión'
                                                          )}
                                                          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-success transition hover:bg-success-100'
                                                          onClick={() =>
                                                            handleManualBoxAdjustment(
                                                              groupIndex,
                                                              String(box.id),
                                                              'clear'
                                                            )
                                                          }
                                                        >
                                                          <Icon icon='tabler:x' />
                                                        </button>
                                                      </div>
                                                    )
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </details>
                                    {scopeHasValues(groupMapping.scope) &&
                                      scopedReferences.length > 0 && (
                                        <div className='mt-3 flex flex-wrap gap-2'>
                                          {scopedReferences.map((reference) => (
                                            <Chip
                                              key={reference}
                                              size='sm'
                                              variant='flat'
                                            >
                                              {reference}
                                            </Chip>
                                          ))}
                                        </div>
                                      )}
                                  </details>
                                )}

                                {isGroupBusy && (
                                  <div className='flex min-h-[220px] items-center justify-center rounded-md border border-warning-200 bg-warning-50 p-6 text-center'>
                                    <div className='flex flex-col items-center gap-3'>
                                      <Spinner color='warning' />
                                      <div>
                                        <p className='text-sm font-semibold text-warning-800'>
                                          {t('Leyendo grupo')}
                                        </p>
                                        <p className='text-xs text-warning-700'>
                                          {group.title || group.groupId}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {isGroupError && (
                                  <div className='rounded-md border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700'>
                                    <p className='font-semibold'>
                                      {t('Error leyendo grupo')}
                                    </p>
                                    <p className='mt-1'>
                                      {processState?.error ||
                                        t('No se pudo leer el documento')}
                                    </p>
                                  </div>
                                )}

                                <div className='hidden'>
                                  <p className='mb-3 text-xs font-semibold uppercase text-default-400'>
                                    {t('Asignación del grupo')}
                                  </p>
                                  <div className='grid grid-cols-1 gap-3 lg:grid-cols-4'>
                                    <Select
                                      label={t('Embarques del grupo')}
                                      placeholder={t('Selecciona embarques')}
                                      selectionMode='multiple'
                                      selectedKeys={
                                        new Set(groupMapping.shipmentKeys)
                                      }
                                      onSelectionChange={(keys) =>
                                        handleGroupMappingChange(groupIndex, {
                                          shipmentKeys:
                                            keys === 'all'
                                              ? manualOptions.map(
                                                  (option) => option.key
                                                )
                                              : Array.from(keys).map(String),
                                        })
                                      }
                                    >
                                      {manualOptions.map((option) => (
                                        <SelectItem
                                          key={option.key}
                                          value={option.key}
                                          description={option.description}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </Select>
                                    <Select
                                      label={t('Filtro variedad')}
                                      placeholder={t('Todas')}
                                      selectedKeys={
                                        groupMapping.varietyFilter
                                          ? [groupMapping.varietyFilter]
                                          : []
                                      }
                                      onSelectionChange={(keys) =>
                                        handleGroupMappingChange(groupIndex, {
                                          varietyFilter: String(
                                            Array.from(keys)[0] || ''
                                          ),
                                        })
                                      }
                                    >
                                      {varietyOptions.map((variety) => (
                                        <SelectItem
                                          key={variety}
                                          value={variety}
                                        >
                                          {variety}
                                        </SelectItem>
                                      ))}
                                    </Select>
                                    <Select
                                      label={t('Filtro calibre')}
                                      placeholder={t('Todos')}
                                      selectedKeys={
                                        groupMapping.caliberFilter
                                          ? [groupMapping.caliberFilter]
                                          : []
                                      }
                                      onSelectionChange={(keys) =>
                                        handleGroupMappingChange(groupIndex, {
                                          caliberFilter: String(
                                            Array.from(keys)[0] || ''
                                          ),
                                        })
                                      }
                                    >
                                      {caliberOptions.map((caliber) => (
                                        <SelectItem
                                          key={caliber}
                                          value={caliber}
                                        >
                                          {caliber}
                                        </SelectItem>
                                      ))}
                                    </Select>
                                    <Select
                                      label={t('Filtro color')}
                                      placeholder={t('Todos')}
                                      selectedKeys={
                                        groupMapping.colorFilter
                                          ? [groupMapping.colorFilter]
                                          : []
                                      }
                                      onSelectionChange={(keys) =>
                                        handleGroupMappingChange(groupIndex, {
                                          colorFilter: String(
                                            Array.from(keys)[0] || ''
                                          ),
                                        })
                                      }
                                    >
                                      {colorOptions.map((color) => (
                                        <SelectItem key={color} value={color}>
                                          {color}
                                        </SelectItem>
                                      ))}
                                    </Select>
                                  </div>
                                  <Textarea
                                    className='mt-3'
                                    label={t('Notas de mapeo')}
                                    value={groupMapping.notes}
                                    onValueChange={(notes) =>
                                      handleGroupMappingChange(groupIndex, {
                                        notes,
                                      })
                                    }
                                    minRows={2}
                                  />
                                </div>

                                <div
                                  className={`grid grid-cols-1 gap-3 ${
                                    isGroupBusy || isGroupError ? 'hidden' : ''
                                  }`}
                                >
                                  <div className='hidden'>
                                    <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                                      <p className='text-xs font-semibold uppercase text-default-400'>
                                        {t('Match sugerido con despachos')}
                                      </p>
                                      <div className='flex gap-2'>
                                        <Chip
                                          size='sm'
                                          color='success'
                                          variant='flat'
                                        >
                                          {groupMatches.matched.length}{' '}
                                          {t('matches')}
                                        </Chip>
                                        {groupMatches.unmatched.length > 0 && (
                                          <Chip
                                            size='sm'
                                            color='warning'
                                            variant='flat'
                                          >
                                            {groupMatches.unmatched.length}{' '}
                                            {t('sin match')}
                                          </Chip>
                                        )}
                                      </div>
                                    </div>
                                    <div className='flex flex-wrap gap-2'>
                                      {groupMatches.matched
                                        .slice(0, 8)
                                        .map((match) => (
                                          <Chip
                                            key={`${match.reference}-${match.container}`}
                                            size='sm'
                                            color='success'
                                            variant='flat'
                                          >
                                            {match.label}
                                          </Chip>
                                        ))}
                                      {groupMatches.unmatched
                                        .slice(0, 8)
                                        .map((item) => (
                                          <Chip
                                            key={item}
                                            size='sm'
                                            color='warning'
                                            variant='flat'
                                          >
                                            {item}
                                          </Chip>
                                        ))}
                                      {groupMatches.matched.length === 0 &&
                                        groupMatches.unmatched.length === 0 && (
                                          <span className='text-sm text-default-400'>
                                            {t(
                                              'Sin referencias o contenedores detectados'
                                            )}
                                          </span>
                                        )}
                                    </div>
                                  </div>

                                  <div className='hidden'>
                                    <p className='mb-2 text-xs font-semibold uppercase text-default-400'>
                                      {t('Resumen por categoría')}
                                    </p>
                                    <div className='grid grid-cols-2 gap-2 text-sm sm:grid-cols-5'>
                                      {COST_CATEGORY_OPTIONS.map((category) => (
                                        <div
                                          key={category}
                                          className='rounded-md bg-default-50 p-2'
                                        >
                                          <p className='text-[11px] text-default-400'>
                                            {t(COST_CATEGORY_LABELS[category])}
                                          </p>
                                          <p className='font-semibold'>
                                            {formatMoney(
                                              costTotals[category],
                                              group.header.currency,
                                              2
                                            )}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  className={`flex flex-wrap items-center justify-between gap-3 rounded-md border border-default-200 bg-default-50 px-4 py-3 dark:bg-default-100 ${
                                    isGroupBusy || isGroupError ? 'hidden' : ''
                                  }`}
                                >
                                  <div>
                                    <p className='text-xs font-semibold uppercase text-default-500'>
                                      {t('Neto calculado')}
                                    </p>
                                    {canCalculateNet ? (
                                      <p className='text-xs text-default-400'>
                                        {formatMoney(
                                          salesLineTotal,
                                          groupCurrency,
                                          2
                                        )}{' '}
                                        {t('ventas')} −{' '}
                                        {formatMoney(
                                          costLineTotal,
                                          groupCurrency,
                                          2
                                        )}{' '}
                                        {t('costos')}
                                      </p>
                                    ) : (
                                      <p className='text-xs text-default-400'>
                                        {t(
                                          'Se calculará al aplicar el tipo de cambio de cada liquidación'
                                        )}
                                      </p>
                                    )}
                                  </div>
                                  {canCalculateNet ? (
                                    <p
                                      className={`text-lg font-bold ${
                                        calculatedNet < 0
                                          ? 'text-danger-600 dark:text-danger-300'
                                          : 'text-success-700 dark:text-success-300'
                                      }`}
                                    >
                                      {formatMoney(
                                        calculatedNet,
                                        groupCurrency,
                                        2
                                      )}
                                    </p>
                                  ) : (
                                    <div className='text-right text-sm font-semibold'>
                                      {costLineCurrencyTotals.map(
                                        ([currency, total]) => (
                                          <p key={currency}>
                                            {formatMoney(total, currency, 2)}{' '}
                                            {t('costos')}
                                          </p>
                                        )
                                      )}
                                    </div>
                                  )}
                                </div>

                                <details
                                  open={
                                    reviewSectionVisibility[
                                      `${groupKey}:sales`
                                    ] !== false
                                  }
                                  onToggle={(event) =>
                                    handleReviewSectionToggle(
                                      `${groupKey}:sales`,
                                      event.currentTarget.open
                                    )
                                  }
                                  className={`group rounded-md border border-default-200 ${
                                    isGroupBusy || isGroupError ? 'hidden' : ''
                                  }`}
                                >
                                  <summary className='cursor-pointer list-none border-b border-default-200 px-3 py-2'>
                                    <div className='flex flex-wrap items-center justify-between gap-2'>
                                      <div className='flex items-center gap-2'>
                                        <Icon icon='tabler:chevron-right' className='shrink-0 text-xs text-default-400 transition-transform group-open:rotate-90' />
                                        <p className='text-xs font-semibold uppercase text-default-400'>
                                          {t('Ventas del grupo')}
                                        </p>
                                      </div>
                                      <div
                                        className='flex gap-1'
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                        }}
                                      >
                                        {isEditingSalesLines && (
                                          <Button
                                            size='sm'
                                            variant='flat'
                                            onPress={() =>
                                              handleAddSalesLine(groupIndex)
                                            }
                                          >
                                            {t('Agregar fila')}
                                          </Button>
                                        )}
                                        <Button
                                          size='sm'
                                          variant='flat'
                                          isDisabled={isGroupAssigned}
                                          onPress={() =>
                                            setEditingSalesGroupKey(
                                              isEditingSalesLines
                                                ? ''
                                                : groupKey
                                            )
                                          }
                                        >
                                          {isEditingSalesLines
                                            ? t('Terminar edición')
                                            : t('Editar filas')}
                                        </Button>
                                      </div>
                                    </div>
                                  </summary>
                                  <div className='overflow-x-auto'>
                                    <table className='w-full min-w-[860px] text-sm'>
                                      <thead className='sticky top-0 bg-default-100 text-xs uppercase text-default-500'>
                                        <tr>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Variedad')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Calibre')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Color')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Etiqueta')}
                                          </th>
                                          <th className='px-3 py-2 text-right'>
                                            {t('Bultos')}
                                          </th>
                                          <th className='px-3 py-2 text-right'>
                                            {t('Precio')}
                                          </th>
                                          <th className='px-3 py-2 text-right'>
                                            {t('Venta')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Asignación')}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className='border-b border-default-200 bg-default-50 font-semibold dark:bg-default-100'>
                                          <td className='px-3 py-2' colSpan={4}>
                                            {t('Total ventas leídas')}
                                          </td>
                                          <td className='px-3 py-2 text-right'>
                                            {formatNumber(salesLineQuantity)}
                                          </td>
                                          <td />
                                          <td className='px-3 py-2 text-right'>
                                            {formatMoney(
                                              salesLineTotal,
                                              group.header.currency,
                                              2
                                            )}
                                          </td>
                                          <td />
                                        </tr>
                                        {group.salesLines.map((line) => {
                                          const detailScope = groupMapping
                                            .detailScopes[line.id] || {
                                            scope: emptyShipmentBoxScope(),
                                            activeFields: [],
                                          };
                                          const lineBoxes =
                                            groupLineAllocation.boxesByLine[
                                              line.id
                                            ] || [];
                                          const lineAvailableBoxes =
                                            groupLineAllocation
                                              .availableBoxesByLine[line.id] ||
                                            [];
                                          const lineReferences = uniqueValues(
                                            lineBoxes.map(
                                              (box) => box.reference_number
                                            )
                                          );
                                          const filteredLineQuantity =
                                            lineBoxes.reduce(
                                              (total, box) =>
                                                total +
                                                (Number(box.quantity) || 0),
                                              0
                                            );
                                          const lineQuantityDifference =
                                            filteredLineQuantity -
                                            (Number(line.quantity) || 0);
                                          const lineQuantityMatches =
                                            lineQuantityDifference === 0;
                                          const lineTransferDraftKey = `${groupKey}:${line.id}`;
                                          const lineTransferDraft =
                                            salesLineTransferDrafts[
                                              lineTransferDraftKey
                                            ] || {
                                              toGroupKey: '',
                                              quantity: '',
                                            };
                                          const transferredFromLineQuantity =
                                            boxTransfers
                                              .filter(
                                                (transfer) =>
                                                  transfer.fromGroupKey ===
                                                    groupKey &&
                                                  transfer.sourceSalesLineId ===
                                                    line.id
                                              )
                                              .reduce(
                                                (total, transfer) =>
                                                  total + transfer.quantity,
                                                0
                                              );
                                          const isLineAssignmentConfirmed =
                                            groupLineAllocation.matchedLineIds.has(
                                              line.id
                                            );
                                          const hasReservedLineQuantity =
                                            Object.keys(
                                              groupMapping
                                                .shipmentBoxQuantitiesByLine?.[
                                                line.id
                                              ] || {}
                                            ).length > 0;
                                          const qualitySplits =
                                            groupMapping.qualitySplits[
                                              line.id
                                            ] || [];
                                          const lineScopeOptions =
                                            selectedReviewLineScopeOptions[
                                              line.id
                                            ] || EMPTY_LINE_SCOPE_OPTIONS;
                                          const detailFilterDefinitions = [
                                            [
                                              'referenceNumbers',
                                              t('Referencias'),
                                              lineScopeOptions.referenceNumbers,
                                            ],
                                            [
                                              'varieties',
                                              t('Variedad Real'),
                                              lineScopeOptions.varieties,
                                            ],
                                            [
                                              'labeledVarieties',
                                              t('Variedad Etiquetada'),
                                              lineScopeOptions.labeledVarieties,
                                            ],
                                            [
                                              'calibers',
                                              t('Calibre'),
                                              lineScopeOptions.calibers,
                                            ],
                                            [
                                              'colors',
                                              t('Color'),
                                              lineScopeOptions.colors,
                                            ],
                                            [
                                              'categories',
                                              t('Categoría'),
                                              lineScopeOptions.categories,
                                            ],
                                            [
                                              'labels',
                                              t('Etiqueta'),
                                              lineScopeOptions.labels,
                                            ],
                                            [
                                              'formats',
                                              t('Formato'),
                                              lineScopeOptions.formats,
                                            ],
                                          ] as Array<
                                            [
                                              keyof ShipmentBoxScope,
                                              string,
                                              string[],
                                            ]
                                          >;
                                          const lineAssistantKey = `${groupKey}:${line.id}`;
                                          const isLineAssistantOpen =
                                            activeLineAssistantKey ===
                                            lineAssistantKey;
                                          const detailScopeSuggestions =
                                            isLineAssistantOpen &&
                                            boxesMatch &&
                                            !lineQuantityMatches
                                              ? getScopeSuggestions(
                                                  lineAvailableBoxes,
                                                  detailScope.scope,
                                                  Number(line.quantity) || 0,
                                                  mappedFormats,
                                                  detailFilterDefinitions.map(
                                                    ([field, label]) => ({
                                                      field,
                                                      label,
                                                    })
                                                  )
                                                )
                                              : [];

                                          return (
                                            <tr
                                              key={line.id}
                                              className={`border-t ${
                                                lineQuantityMatches
                                                  ? 'border-success-200 bg-success-50/50 dark:bg-success-100/10'
                                                  : 'border-warning-200 bg-warning-50/50 dark:bg-warning-100/10'
                                              }`}
                                            >
                                              <td className='px-3 py-2'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    aria-label={t('Variedad')}
                                                    value={line.variety}
                                                    onValueChange={(variety) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { variety }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  line.variety ||
                                                  group.header.variety ||
                                                  '-'
                                                )}
                                              </td>
                                              <td className='px-3 py-2'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    aria-label={t('Calibre')}
                                                    value={line.caliber}
                                                    onValueChange={(caliber) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { caliber }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  line.caliber || '-'
                                                )}
                                              </td>
                                              <td className='px-3 py-2'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    aria-label={t('Color')}
                                                    value={line.color}
                                                    onValueChange={(color) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { color }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  line.color || '-'
                                                )}
                                              </td>
                                              <td className='px-3 py-2'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    aria-label={t('Etiqueta')}
                                                    value={line.label}
                                                    onValueChange={(label) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { label }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  line.label || '-'
                                                )}
                                              </td>
                                              <td className='px-3 py-2 text-right'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    type='number'
                                                    min='0'
                                                    aria-label={t('Bultos')}
                                                    value={String(
                                                      line.quantity
                                                    )}
                                                    onValueChange={(quantity) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        {
                                                          quantity:
                                                            Number(quantity) ||
                                                            0,
                                                        }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  formatNumber(line.quantity)
                                                )}
                                              </td>
                                              <td className='px-3 py-2 text-right'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    type='number'
                                                    min='0'
                                                    step='0.01'
                                                    aria-label={t('Precio')}
                                                    value={String(
                                                      line.unitPrice
                                                    )}
                                                    onValueChange={(
                                                      unitPrice
                                                    ) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        {
                                                          unitPrice:
                                                            Number(unitPrice) ||
                                                            0,
                                                        }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  formatMoney(
                                                    line.unitPrice,
                                                    line.currency
                                                  )
                                                )}
                                              </td>
                                              <td className='px-3 py-2 text-right'>
                                                {isEditingSalesLines ? (
                                                  <Input
                                                    size='sm'
                                                    type='number'
                                                    min='0'
                                                    step='0.01'
                                                    aria-label={t('Venta')}
                                                    value={String(line.amount)}
                                                    onValueChange={(amount) =>
                                                      handleSalesLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        {
                                                          amount:
                                                            Number(amount) || 0,
                                                        }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  formatMoney(
                                                    line.amount,
                                                    line.currency,
                                                    2
                                                  )
                                                )}
                                              </td>
                                              <td className='min-w-[320px] px-3 py-2'>
                                                {isEditingSalesLines && (
                                                  <Button
                                                    size='sm'
                                                    color='danger'
                                                    variant='light'
                                                    className='mb-1'
                                                    onPress={() =>
                                                      handleDeleteSalesLine(
                                                        groupIndex,
                                                        line.id
                                                      )
                                                    }
                                                  >
                                                    {t('Eliminar fila')}
                                                  </Button>
                                                )}
                                                <p className='mt-1 text-xs text-default-500'>
                                                  {lineReferences.length
                                                    ? lineReferences.join(', ')
                                                    : t('Sin match')}
                                                </p>
                                                <div className='mt-2 flex flex-wrap items-center gap-1'>
                                                  <Chip
                                                    size='sm'
                                                    color={
                                                      lineQuantityMatches
                                                        ? 'success'
                                                        : 'warning'
                                                    }
                                                    variant='flat'
                                                  >
                                                    {formatNumber(
                                                      filteredLineQuantity
                                                    )}{' '}
                                                    /{' '}
                                                    {formatNumber(
                                                      line.quantity
                                                    )}{' '}
                                                    {t('bultos')}
                                                  </Chip>
                                                  <Button
                                                    size='sm'
                                                    color={
                                                      isLineAssignmentConfirmed
                                                        ? 'success'
                                                        : 'primary'
                                                    }
                                                    variant='flat'
                                                    isDisabled={
                                                      isGroupAssigned ||
                                                      lineBoxes.length === 0
                                                    }
                                                    onPress={() =>
                                                      handleConfirmLineAssignment(
                                                        groupIndex,
                                                        line.id,
                                                        !isLineAssignmentConfirmed
                                                      )
                                                    }
                                                  >
                                                    {isLineAssignmentConfirmed
                                                      ? t(
                                                          'Asignación confirmada'
                                                        )
                                                      : t(
                                                          'Confirmar asignación'
                                                        )}
                                                  </Button>
                                                  {hasReservedLineQuantity &&
                                                    !isGroupAssigned && (
                                                      <Button
                                                        size='sm'
                                                        variant='flat'
                                                        onPress={() =>
                                                          handleReleaseSalesLineQuantity(
                                                            groupIndex,
                                                            line.id
                                                          )
                                                        }
                                                      >
                                                        {t('Liberar')}
                                                      </Button>
                                                    )}
                                                </div>
                                                {!lineQuantityMatches && (
                                                  <p className='mt-1 text-xs text-warning-700 dark:text-warning-400'>
                                                    {t('Diferencia de bultos')}:{' '}
                                                    {lineQuantityDifference > 0
                                                      ? '+'
                                                      : ''}
                                                    {formatNumber(
                                                      lineQuantityDifference
                                                    )}
                                                  </p>
                                                )}
                                                {lineQuantityDifference > 0 &&
                                                  Number(line.quantity) > 0 &&
                                                  !isGroupAssigned && (
                                                    <div className='mt-2 rounded-md border border-primary-200 bg-primary-50/60 p-2 dark:bg-primary-100/10'>
                                                      <p className='text-xs font-semibold text-primary-700 dark:text-primary-300'>
                                                        {t(
                                                          'Asignar dentro del grupo'
                                                        )}
                                                      </p>
                                                      <p className='mt-1 text-[11px] text-default-500'>
                                                        {`${formatNumber(filteredLineQuantity)} ${t('disponibles')} / ${formatNumber(line.quantity)} ${t('requeridos')}. ${formatNumber(lineQuantityDifference)} ${t('quedarán disponibles para otras filas del grupo')}.`}
                                                      </p>
                                                      <Button
                                                        className='mt-2'
                                                        size='sm'
                                                        variant='flat'
                                                        onPress={() =>
                                                          handleReserveSalesLineQuantity(
                                                            groupIndex,
                                                            line.id,
                                                            lineBoxes
                                                          )
                                                        }
                                                      >
                                                        {`${t('Asignar')} ${formatNumber(line.quantity)} ${t('bultos')}`}
                                                      </Button>
                                                    </div>
                                                  )}
                                                {lineQuantityDifference > 0 &&
                                                  !isGroupAssigned && (
                                                    <div className='mt-2 rounded-md border border-secondary-200 bg-secondary-50/60 p-2 dark:bg-secondary-100/10'>
                                                      <div className='mb-2 flex flex-wrap items-center justify-between gap-1'>
                                                        <div>
                                                          <p className='text-xs font-semibold text-secondary-700 dark:text-secondary-300'>
                                                            {t(
                                                              'Enviar excedente a otro grupo'
                                                            )}
                                                          </p>
                                                          <p className='text-[11px] text-default-500'>
                                                            {`${t('Excedente disponible')}: ${formatNumber(lineQuantityDifference)} ${t('bultos')}`}
                                                          </p>
                                                        </div>
                                                        {transferredFromLineQuantity >
                                                          0 && (
                                                          <Chip
                                                            size='sm'
                                                            color='secondary'
                                                            variant='flat'
                                                          >
                                                            {formatNumber(
                                                              transferredFromLineQuantity
                                                            )}{' '}
                                                            {t('enviados')}
                                                          </Chip>
                                                        )}
                                                      </div>
                                                      <div className='grid grid-cols-[100px_minmax(130px,1fr)] gap-1'>
                                                        <Input
                                                          size='sm'
                                                          type='number'
                                                          min={1}
                                                          max={
                                                            lineQuantityDifference
                                                          }
                                                          step={1}
                                                          label={t('Bultos')}
                                                          placeholder={String(
                                                            lineQuantityDifference
                                                          )}
                                                          value={
                                                            lineTransferDraft.quantity
                                                          }
                                                          onValueChange={(
                                                            quantity
                                                          ) =>
                                                            setSalesLineTransferDrafts(
                                                              (current) => ({
                                                                ...current,
                                                                [lineTransferDraftKey]:
                                                                  {
                                                                    ...lineTransferDraft,
                                                                    quantity,
                                                                  },
                                                              })
                                                            )
                                                          }
                                                        />
                                                        <Select
                                                          size='sm'
                                                          label={t(
                                                            'Grupo destino'
                                                          )}
                                                          placeholder={t(
                                                            'Seleccionar'
                                                          )}
                                                          selectedKeys={
                                                            lineTransferDraft.toGroupKey
                                                              ? [
                                                                  lineTransferDraft.toGroupKey,
                                                                ]
                                                              : []
                                                          }
                                                          onSelectionChange={(
                                                            keys
                                                          ) =>
                                                            setSalesLineTransferDrafts(
                                                              (current) => ({
                                                                ...current,
                                                                [lineTransferDraftKey]:
                                                                  {
                                                                    ...lineTransferDraft,
                                                                    toGroupKey:
                                                                      String(
                                                                        Array.from(
                                                                          keys
                                                                        )[0] ||
                                                                          ''
                                                                      ),
                                                                  },
                                                              })
                                                            )
                                                          }
                                                        >
                                                          {groupedData
                                                            .map(
                                                              (
                                                                targetGroup,
                                                                targetGroupIndex
                                                              ) => ({
                                                                group:
                                                                  targetGroup,
                                                                index:
                                                                  targetGroupIndex,
                                                                key: getGroupKey(
                                                                  targetGroup,
                                                                  targetGroupIndex
                                                                ),
                                                              })
                                                            )
                                                            .filter(
                                                              (target) =>
                                                                target.key !==
                                                                groupKey
                                                            )
                                                            .map((target) => (
                                                              <SelectItem
                                                                key={target.key}
                                                                value={
                                                                  target.key
                                                                }
                                                              >
                                                                {`${t('Grupo')} ${target.index + 1}${groupAssignmentLocks[target.key] ? ` · ${t('Asignado')}` : ''}`}
                                                              </SelectItem>
                                                            ))}
                                                        </Select>
                                                      </div>
                                                      <Button
                                                        className='mt-2 w-full'
                                                        size='sm'
                                                        variant='flat'
                                                        onPress={() =>
                                                          handleTransferSalesLineSurplus(
                                                            groupIndex,
                                                            line.id
                                                          )
                                                        }
                                                      >
                                                        {t(
                                                          'Enviar desde esta fila'
                                                        )}
                                                      </Button>
                                                    </div>
                                                  )}
                                                <div className='mt-2 flex flex-wrap gap-1'>
                                                  <Button
                                                    size='sm'
                                                    variant='flat'
                                                    isDisabled={
                                                      isGroupAssigned ||
                                                      lineBoxes.length === 0
                                                    }
                                                    onPress={() =>
                                                      handleQualitySplit(
                                                        groupIndex,
                                                        line,
                                                        lineBoxes
                                                      )
                                                    }
                                                  >
                                                    {qualitySplits.length > 0
                                                      ? t('Actualizar desglose')
                                                      : t('Desglosar calidad')}
                                                  </Button>
                                                  {qualitySplits.length > 0 && (
                                                    <Button
                                                      size='sm'
                                                      variant='light'
                                                      isDisabled={
                                                        isGroupAssigned
                                                      }
                                                      onPress={() =>
                                                        handleClearQualitySplit(
                                                          groupIndex,
                                                          line.id
                                                        )
                                                      }
                                                    >
                                                      {t('Usar una fila')}
                                                    </Button>
                                                  )}
                                                </div>
                                                {qualitySplits.length > 0 && (
                                                  <div className='mt-2 space-y-1 rounded-md border border-primary-200 bg-primary-50/50 p-2 text-xs dark:bg-primary-100/10'>
                                                    <p className='font-semibold text-primary'>
                                                      1 {t('fila leída')} →{' '}
                                                      {qualitySplits.length}{' '}
                                                      {t(
                                                        'detalles de liquidación'
                                                      )}
                                                    </p>
                                                    {qualitySplits.map(
                                                      (split) => (
                                                        <div
                                                          key={split.id}
                                                          className='border-t border-primary-100 pt-1 dark:border-primary-200/30'
                                                        >
                                                          <p className='font-medium text-foreground'>
                                                            {[
                                                              split.variety,
                                                              split.caliber,
                                                              split.color,
                                                              split.category,
                                                              split.format,
                                                              split.label,
                                                            ]
                                                              .filter(Boolean)
                                                              .join(' · ') ||
                                                              '-'}
                                                          </p>
                                                          <p className='text-default-500'>
                                                            {formatNumber(
                                                              split.quantity
                                                            )}{' '}
                                                            {t('cajas')} ·{' '}
                                                            {formatNumber(
                                                              split.kilos
                                                            )}{' '}
                                                            kg ·{' '}
                                                            {formatMoney(
                                                              split.amount,
                                                              line.currency,
                                                              2
                                                            )}
                                                          </p>
                                                        </div>
                                                      )
                                                    )}
                                                  </div>
                                                )}
                                                <div className='mt-2 space-y-2'>
                                                  <Select
                                                    size='sm'
                                                    label={t('Agregar filtro')}
                                                    placeholder={t(
                                                      'Selecciona variable'
                                                    )}
                                                    selectedKeys={[]}
                                                    isDisabled={isGroupAssigned}
                                                    onSelectionChange={(
                                                      keys
                                                    ) => {
                                                      const field = String(
                                                        Array.from(keys)[0] ||
                                                          ''
                                                      ) as keyof ShipmentBoxScope;
                                                      if (field) {
                                                        handleAddDetailFilter(
                                                          groupIndex,
                                                          field
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {detailFilterDefinitions
                                                      .filter(
                                                        ([field]) =>
                                                          !detailScope.activeFields.includes(
                                                            field
                                                          )
                                                      )
                                                      .sort(
                                                        (
                                                          [, labelA],
                                                          [, labelB]
                                                        ) =>
                                                          labelA.localeCompare(
                                                            labelB,
                                                            'es'
                                                          )
                                                      )
                                                      .map(([field, label]) => (
                                                        <SelectItem key={field}>
                                                          {label}
                                                        </SelectItem>
                                                      ))}
                                                  </Select>
                                                  {detailFilterDefinitions
                                                    .filter(([field]) =>
                                                      detailScope.activeFields.includes(
                                                        field
                                                      )
                                                    )
                                                    .map(
                                                      ([
                                                        field,
                                                        label,
                                                        options,
                                                      ]) => (
                                                        <div
                                                          key={field}
                                                          className='space-y-1'
                                                        >
                                                          <div className='flex items-end gap-1'>
                                                            <CustomAutocomplete
                                                              className='min-w-0 flex-1'
                                                              label={label}
                                                              placeholder={t(
                                                                'Buscar y agregar'
                                                              )}
                                                              closeOnSelect={
                                                                false
                                                              }
                                                              isDisabled={
                                                                isGroupAssigned
                                                              }
                                                              contentInsideInput={
                                                                detailScope
                                                                  .scope[field]
                                                                  .length ? (
                                                                  <div className='flex flex-wrap gap-1'>
                                                                    {detailScope.scope[
                                                                      field
                                                                    ]
                                                                      .slice()
                                                                      .sort(
                                                                        (
                                                                          a,
                                                                          b
                                                                        ) =>
                                                                          a.localeCompare(
                                                                            b,
                                                                            'es'
                                                                          )
                                                                      )
                                                                      .map(
                                                                        (
                                                                          value
                                                                        ) => (
                                                                          <Chip
                                                                            key={
                                                                              value
                                                                            }
                                                                            size='sm'
                                                                            variant='flat'
                                                                            onClose={
                                                                              isGroupAssigned
                                                                                ? undefined
                                                                                : () =>
                                                                                    handleDetailScopeChange(
                                                                                      groupIndex,
                                                                                      line.id,
                                                                                      {
                                                                                        scope:
                                                                                          {
                                                                                            ...detailScope.scope,
                                                                                            [field]:
                                                                                              detailScope.scope[
                                                                                                field
                                                                                              ].filter(
                                                                                                (
                                                                                                  item
                                                                                                ) =>
                                                                                                  item !==
                                                                                                  value
                                                                                              ),
                                                                                          },
                                                                                      }
                                                                                    )
                                                                            }
                                                                          >
                                                                            {
                                                                              value
                                                                            }
                                                                          </Chip>
                                                                        )
                                                                      )}
                                                                  </div>
                                                                ) : null
                                                              }
                                                              options={options.map(
                                                                (option) => ({
                                                                  key: option,
                                                                  value: option,
                                                                  label: option,
                                                                })
                                                              )}
                                                              onSelectionChange={(
                                                                key
                                                              ) => {
                                                                if (!key)
                                                                  return;
                                                                const values =
                                                                  detailScope
                                                                    .scope[
                                                                    field
                                                                  ];
                                                                handleDetailScopeChange(
                                                                  groupIndex,
                                                                  line.id,
                                                                  {
                                                                    scope: {
                                                                      ...detailScope.scope,
                                                                      [field]:
                                                                        values.includes(
                                                                          String(
                                                                            key
                                                                          )
                                                                        )
                                                                          ? values
                                                                          : [
                                                                              ...values,
                                                                              String(
                                                                                key
                                                                              ),
                                                                            ],
                                                                    },
                                                                  }
                                                                );
                                                              }}
                                                            />
                                                            <Button
                                                              size='sm'
                                                              variant='light'
                                                              isIconOnly
                                                              isDisabled={
                                                                isGroupAssigned
                                                              }
                                                              onPress={() =>
                                                                handleRemoveDetailFilter(
                                                                  groupIndex,
                                                                  line.id,
                                                                  field
                                                                )
                                                              }
                                                              aria-label={t(
                                                                'Quitar filtro'
                                                              )}
                                                            >
                                                              <Icon icon='tabler:x' />
                                                            </Button>
                                                          </div>
                                                        </div>
                                                      )
                                                    )}
                                                  {boxesMatch &&
                                                    !lineQuantityMatches && (
                                                      <div className='rounded-md border border-secondary-200 bg-secondary-50/50 p-2 dark:bg-secondary-100/10'>
                                                        <div className='flex flex-wrap items-center justify-between gap-1'>
                                                          <div>
                                                            <p className='text-xs font-semibold text-secondary-700 dark:text-secondary-300'>
                                                              {t(
                                                                'Asistente de esta fila'
                                                              )}
                                                            </p>
                                                            <p className='text-[11px] text-default-500'>
                                                              {`${formatNumber(
                                                                filteredLineQuantity
                                                              )}/${formatNumber(
                                                                line.quantity
                                                              )} ${t('bultos')}`}
                                                            </p>
                                                          </div>
                                                          <Button
                                                            size='sm'
                                                            variant='flat'
                                                            onPress={() =>
                                                              setActiveLineAssistantKey(
                                                                isLineAssistantOpen
                                                                  ? ''
                                                                  : lineAssistantKey
                                                              )
                                                            }
                                                          >
                                                            {isLineAssistantOpen
                                                              ? t(
                                                                  'Ocultar sugerencias'
                                                                )
                                                              : t(
                                                                  'Buscar sugerencias'
                                                                )}
                                                          </Button>
                                                        </div>
                                                        {isLineAssistantOpen && (
                                                          <div className='mt-2 space-y-1'>
                                                            {detailScopeSuggestions.map(
                                                              (suggestion) => (
                                                                <button
                                                                  key={`${suggestion.field}-${suggestion.values.join('|')}`}
                                                                  type='button'
                                                                  className={`w-full rounded-md border p-2 text-left transition hover:border-secondary-400 ${
                                                                    suggestion.difference ===
                                                                    0
                                                                      ? 'border-success-300 bg-success-50/60 dark:bg-success-100/10'
                                                                      : 'border-default-200 bg-content1'
                                                                  }`}
                                                                  onClick={() => {
                                                                    handleDetailScopeChange(
                                                                      groupIndex,
                                                                      line.id,
                                                                      {
                                                                        activeFields:
                                                                          detailScope.activeFields.includes(
                                                                            suggestion.field
                                                                          )
                                                                            ? detailScope.activeFields
                                                                            : [
                                                                                ...detailScope.activeFields,
                                                                                suggestion.field,
                                                                              ],
                                                                        scope: {
                                                                          ...detailScope.scope,
                                                                          [suggestion.field]:
                                                                            suggestion.values,
                                                                        },
                                                                      }
                                                                    );
                                                                  }}
                                                                >
                                                                  <div className='flex items-center justify-between gap-2'>
                                                                    <span className='text-xs font-semibold'>
                                                                      {
                                                                        suggestion.label
                                                                      }
                                                                    </span>
                                                                    <Chip
                                                                      size='sm'
                                                                      color={
                                                                        suggestion.difference ===
                                                                        0
                                                                          ? 'success'
                                                                          : 'warning'
                                                                      }
                                                                      variant='flat'
                                                                    >
                                                                      {formatNumber(
                                                                        suggestion.boxes
                                                                      )}
                                                                    </Chip>
                                                                  </div>
                                                                  <p className='mt-1 line-clamp-2 text-[11px] text-default-600'>
                                                                    {suggestion.values.join(
                                                                      ', '
                                                                    )}
                                                                  </p>
                                                                  <p className='mt-1 text-[11px] text-default-500'>
                                                                    {suggestion.difference ===
                                                                    0
                                                                      ? t(
                                                                          'Match exacto'
                                                                        )
                                                                      : `${t(
                                                                          'Diferencia'
                                                                        )}: ${
                                                                          suggestion.difference >
                                                                          0
                                                                            ? '+'
                                                                            : ''
                                                                        }${formatNumber(
                                                                          suggestion.difference
                                                                        )}`}
                                                                  </p>
                                                                </button>
                                                              )
                                                            )}
                                                            {detailScopeSuggestions.length ===
                                                              0 && (
                                                              <p className='rounded-md bg-content1 p-2 text-[11px] text-default-500'>
                                                                {t(
                                                                  'No se encontró una combinación simple para esta fila.'
                                                                )}
                                                              </p>
                                                            )}
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                        {group.salesLines.length === 0 && (
                                          <tr>
                                            <td
                                              colSpan={8}
                                              className='px-3 py-6 text-center text-default-400'
                                            >
                                              {t(
                                                'No se detectaron líneas de venta en este grupo'
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </details>

                                <details
                                  open={
                                    reviewSectionVisibility[
                                      `${groupKey}:costs`
                                    ] !== false
                                  }
                                  onToggle={(event) =>
                                    handleReviewSectionToggle(
                                      `${groupKey}:costs`,
                                      event.currentTarget.open
                                    )
                                  }
                                  className={`group rounded-md border border-default-200 ${
                                    isGroupBusy || isGroupError ? 'hidden' : ''
                                  }`}
                                >
                                  <summary className='cursor-pointer list-none border-b border-default-200 px-3 py-2'>
                                    <div className='flex flex-wrap items-center justify-between gap-2'>
                                      <div className='flex items-center gap-2'>
                                        <Icon icon='tabler:chevron-right' className='shrink-0 text-xs text-default-400 transition-transform group-open:rotate-90' />
                                        <p className='text-xs font-semibold uppercase text-default-400'>
                                          {t('Costos del grupo')}
                                        </p>
                                      </div>
                                      <div
                                        className='flex gap-1'
                                        onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                        }}
                                      >
                                        {isEditingCostLines && (
                                          <Button
                                            size='sm'
                                            variant='flat'
                                            onPress={() =>
                                              handleAddCostLine(groupIndex)
                                            }
                                          >
                                            {t('Agregar costo')}
                                          </Button>
                                        )}
                                        <Button
                                          size='sm'
                                          variant='flat'
                                          onPress={() =>
                                            setEditingCostGroupKey(
                                              isEditingCostLines ? '' : groupKey
                                            )
                                          }
                                        >
                                          {isEditingCostLines
                                            ? t('Terminar edición')
                                            : t('Editar costos')}
                                        </Button>
                                      </div>
                                    </div>
                                  </summary>
                                  <div className='overflow-x-auto'>
                                    <table className='w-full min-w-[760px] text-sm'>
                                      <thead className='sticky top-0 bg-default-100 text-xs uppercase text-default-500'>
                                        <tr>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Descripción')}
                                          </th>
                                          <th className='px-3 py-2 text-right'>
                                            {t('Monto')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Moneda origen')}
                                          </th>
                                          <th className='px-3 py-2 text-left'>
                                            {t('Categoría')}
                                          </th>
                                          {showExporterCostCategories && (
                                            <th className='px-3 py-2 text-left'>
                                              {t('Categoría exportadora')}
                                            </th>
                                          )}
                                          <th className='px-3 py-2 text-right'>
                                            {t('Estado')}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className='border-b border-default-200 bg-default-50 font-semibold dark:bg-default-100'>
                                          <td className='px-3 py-2'>
                                            {t('Total costos leídos')}
                                          </td>
                                          <td className='px-3 py-2 text-right'>
                                            <div className='flex flex-col items-end'>
                                              {(costLineCurrencyTotals.length
                                                ? costLineCurrencyTotals
                                                : [[groupCurrency, 0]]
                                              ).map(([currency, total]) => (
                                                <span key={currency}>
                                                  {formatMoney(
                                                    Number(total),
                                                    String(currency),
                                                    2
                                                  )}
                                                </span>
                                              ))}
                                            </div>
                                          </td>
                                          <td
                                            colSpan={
                                              showExporterCostCategories
                                                ? 4
                                                : 3
                                            }
                                          />
                                        </tr>
                                        {group.costLines.map(
                                          (line, costIndex) => (
                                            <tr
                                              key={line.id}
                                              className='border-t border-default-100'
                                            >
                                              <td className='px-3 py-2'>
                                                {isEditingCostLines ? (
                                                  <Input
                                                    size='sm'
                                                    aria-label={t(
                                                      'Descripción'
                                                    )}
                                                    value={line.description}
                                                    onValueChange={(
                                                      description
                                                    ) =>
                                                      handleCostLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { description }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  <div className='flex flex-col gap-1'>
                                                    <span>
                                                      {line.description || '-'}
                                                    </span>
                                                    {line.sourceLocation
                                                      ?.originalNameEdited ===
                                                      true && (
                                                      <span className='text-xs text-warning-600'>
                                                        {t(
                                                          'Concepto corregido manualmente'
                                                        )}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </td>
                                              <td className='px-3 py-2 text-right'>
                                                {isEditingCostLines ? (
                                                  <Input
                                                    size='sm'
                                                    type='number'
                                                    min='0'
                                                    step='0.01'
                                                    aria-label={t('Monto')}
                                                    value={String(line.amount)}
                                                    onValueChange={(amount) =>
                                                      handleCostLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        {
                                                          amount:
                                                            Number(amount) || 0,
                                                        }
                                                      )
                                                    }
                                                  />
                                                ) : (
                                                  formatMoney(
                                                    line.amount,
                                                    line.currency,
                                                    2
                                                  )
                                                )}
                                              </td>
                                              <td className='w-40 px-3 py-2'>
                                                {isEditingCostLines ? (
                                                  <Select
                                                    size='sm'
                                                    aria-label={t(
                                                      'Moneda origen'
                                                    )}
                                                    selectedKeys={[
                                                      normalizeCostCurrency(
                                                        line.currency
                                                      ),
                                                    ]}
                                                    onSelectionChange={(
                                                      keys
                                                    ) => {
                                                      const currency = String(
                                                        Array.from(keys)[0] ||
                                                          line.currency
                                                      );
                                                      handleCostLineChange(
                                                        groupIndex,
                                                        line.id,
                                                        { currency }
                                                      );
                                                    }}
                                                  >
                                                    {Array.from(
                                                      new Set(
                                                        [
                                                          'USD',
                                                          normalizeCostCurrency(
                                                            group.header
                                                              .currency
                                                          ),
                                                          normalizeCostCurrency(
                                                            line.currency
                                                          ),
                                                        ].filter(Boolean)
                                                      )
                                                    ).map((currency) => (
                                                      <SelectItem
                                                        key={currency}
                                                        value={currency}
                                                      >
                                                        {currency}
                                                      </SelectItem>
                                                    ))}
                                                  </Select>
                                                ) : (
                                                  <div className='flex flex-col gap-1'>
                                                    <span>
                                                      {normalizeCostCurrency(
                                                        line.currency
                                                      )}
                                                    </span>
                                                    {line.sourceLocation
                                                      ?.currencyEdited ===
                                                      true && (
                                                      <span className='text-xs text-warning-600'>
                                                        {t(
                                                          'Moneda corregida manualmente'
                                                        )}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </td>
                                              <td className='px-3 py-2'>
                                                <Select
                                                  size='sm'
                                                  aria-label={t('Categoría')}
                                                  isDisabled={
                                                    savingReceiverCostMappingId ===
                                                    line.mappingId
                                                  }
                                                  selectedKeys={[
                                                    line.mappedCategory,
                                                  ]}
                                                  onSelectionChange={(keys) => {
                                                    const category = String(
                                                      Array.from(keys)[0] ||
                                                        line.mappedCategory
                                                    ) as SettlementCostCategory;
                                                    handleCostCategoryChange(
                                                      groupIndex,
                                                      costIndex,
                                                      category
                                                    );
                                                  }}
                                                >
                                                  {COST_CATEGORY_OPTIONS.map(
                                                    (category) => (
                                                      <SelectItem
                                                        key={category}
                                                        value={category}
                                                      >
                                                        {t(
                                                          COST_CATEGORY_LABELS[
                                                            category
                                                          ]
                                                        )}
                                                      </SelectItem>
                                                    )
                                                  )}
                                                </Select>
                                              </td>
                                              {showExporterCostCategories && (
                                                <td className='px-3 py-2'>
                                                  <Select
                                                    size='sm'
                                                    aria-label={t(
                                                      'Categoría exportadora'
                                                    )}
                                                    isDisabled={
                                                      savingExporterCostConcept ===
                                                      normalizeReceiverCostConcept(
                                                        line.description
                                                      )
                                                    }
                                                    selectedKeys={[
                                                      String(
                                                        exporterCostMappings[
                                                          normalizeReceiverCostConcept(
                                                            line.description
                                                          )
                                                        ] ?? 'unclassified'
                                                      ),
                                                    ]}
                                                    onSelectionChange={(
                                                      keys
                                                    ) => {
                                                      const key =
                                                        Array.from(keys)[0];
                                                      if (
                                                        typeof key !== 'string'
                                                      )
                                                        return;
                                                      handleExporterCostCategoryChange(
                                                        line.description,
                                                        key === 'unclassified'
                                                          ? null
                                                          : Number(key)
                                                      );
                                                    }}
                                                  >
                                                    {[
                                                      <SelectItem
                                                        key='unclassified'
                                                        value='unclassified'
                                                      >
                                                        {getDefaultExporterCostCategory(
                                                          exporterCostCategories,
                                                          line.mappedCategory
                                                        )
                                                          ? `${t('Automática')}: ${
                                                              getDefaultExporterCostCategory(
                                                                exporterCostCategories,
                                                                line.mappedCategory
                                                              )!.name
                                                            }`
                                                          : t(
                                                              UNCLASSIFIED_EXPORTER_COST_LABEL
                                                            )}
                                                      </SelectItem>,
                                                      ...exporterCostCategories
                                                        .filter(
                                                          (category) =>
                                                            !category.archivedAt ||
                                                            exporterCostMappings[
                                                              normalizeReceiverCostConcept(
                                                                line.description
                                                              )
                                                            ] === category.id
                                                        )
                                                        .map((category) => (
                                                          <SelectItem
                                                            key={String(
                                                              category.id
                                                            )}
                                                            value={String(
                                                              category.id
                                                            )}
                                                          >
                                                            {category.name}
                                                          </SelectItem>
                                                        )),
                                                    ]}
                                                  </Select>
                                                </td>
                                              )}
                                              <td className='px-3 py-2 text-right'>
                                                {isEditingCostLines ? (
                                                  <Button
                                                    size='sm'
                                                    color='danger'
                                                    variant='light'
                                                    onPress={() =>
                                                      handleDeleteCostLine(
                                                        groupIndex,
                                                        line.id
                                                      )
                                                    }
                                                  >
                                                    {t('Eliminar')}
                                                  </Button>
                                                ) : (
                                                  <div className='flex flex-col items-end gap-1'>
                                                    <Chip
                                                      size='sm'
                                                      variant='flat'
                                                      color={
                                                        line.mappingStatus ===
                                                        'saved'
                                                          ? 'success'
                                                          : 'warning'
                                                      }
                                                    >
                                                      {line.mappingStatus ===
                                                      'saved'
                                                        ? t('Mapeo guardado')
                                                        : t('Nuevo mapeo')}
                                                    </Chip>
                                                    {line.confidence ? (
                                                      <span className='text-xs text-default-400'>
                                                        {formatNumber(
                                                          line.confidence * 100
                                                        )}
                                                        % {t('confianza')}
                                                      </span>
                                                    ) : null}
                                                  </div>
                                                )}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                        {group.costLines.length === 0 && (
                                          <tr>
                                            <td
                                              colSpan={
                                                showExporterCostCategories
                                                  ? 6
                                                  : 5
                                              }
                                              className='px-3 py-6 text-center text-default-400'
                                            >
                                              {t(
                                                'No se detectaron costos en este grupo'
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </details>

                                {group.warnings.length > 0 && (
                                  <Alert variant='warning'>
                                    {group.warnings.map((warning) => (
                                      <p key={warning}>{warning}</p>
                                    ))}
                                  </Alert>
                                )}
                              </div>
                              {isPdfSidebarOpen && pdfPreviewUrl && (
                                <aside className='sticky top-0 flex h-[calc(100dvh-250px)] min-h-[560px] flex-col overflow-hidden rounded-md border border-default-200 bg-content1'>
                                  <div className='flex flex-wrap items-center justify-between gap-2 border-b border-default-200 px-3 py-2'>
                                    <div className='flex flex-wrap items-center gap-2'>
                                      <Icon icon='tabler:file-type-pdf' className='text-danger' />
                                      <span className='text-sm font-semibold'>
                                        {t('PDF original')}
                                      </span>
                                      {group.pages.map((pageNumber) => (
                                        <Button
                                          key={pageNumber}
                                          size='sm'
                                          color={
                                            pdfSidebarPage === pageNumber
                                              ? 'primary'
                                              : 'default'
                                          }
                                          variant='flat'
                                          onPress={() =>
                                            setPdfSidebarPage(pageNumber)
                                          }
                                        >
                                          P{pageNumber}
                                        </Button>
                                      ))}
                                    </div>
                                    <Button
                                      size='sm'
                                      variant='light'
                                      isIconOnly
                                      onPress={() => setIsPdfSidebarOpen(false)}
                                      aria-label={t('Ocultar PDF')}
                                    >
                                      <Icon icon='tabler:x' />
                                    </Button>
                                  </div>
                                  {pdfSidebarThumbnail ? (
                                    <div className='flex min-h-0 flex-1 items-start justify-center overflow-auto bg-default-50 p-3'>
                                      <img
                                        src={pdfSidebarThumbnail.dataUrl}
                                        width={pdfSidebarThumbnail.width}
                                        height={pdfSidebarThumbnail.height}
                                        alt={`${t('PDF original')} · ${t('Grupo')} ${groupIndex + 1} · ${t('Página')} ${pdfSidebarPage}`}
                                        className='h-auto max-w-full bg-content1 object-contain shadow-sm'
                                      />
                                    </div>
                                  ) : (
                                    <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-3 bg-default-50 text-sm text-default-500'>
                                      <Spinner size='sm' />
                                      <span>
                                        {t('Cargando página')} {pdfSidebarPage}
                                      </span>
                                    </div>
                                  )}
                                </aside>
                              )}
                            </div>
                          </Tab>
                        );
                      })}
                    </Tabs>
                  )}
              </div>
            )}

          {activeStep === 'review' && processedData.length > 0 && (
            <div className='flex flex-col gap-4 rounded-medium border border-default-200 p-4'>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                  <div>
                    <p className='text-sm font-semibold text-default-700'>
                      {t('Vista previa del documento leído')}
                    </p>
                    <p className='text-xs text-default-500'>
                      {t(
                        'Esta información aún no crea ni actualiza liquidaciones.'
                      )}
                    </p>
                  </div>
                  <Chip color='success' variant='flat'>
                    {processedData.length} {t('líneas leídas')}
                  </Chip>
                </div>

                {processWarnings.length > 0 && (
                  <Alert variant='warning'>
                    {processWarnings.map((warning) => (
                      <p key={warning}>{warning}</p>
                    ))}
                  </Alert>
                )}

                <div className='grid grid-cols-2 gap-3 text-sm md:grid-cols-4'>
                  <div className='rounded-md bg-default-50 p-3'>
                    <p className='text-xs text-default-400'>
                      {t('Kilos leídos')}
                    </p>
                    <p className='font-semibold'>
                      {formatNumber(previewTotals.kilos)}
                    </p>
                  </div>
                  <div className='rounded-md bg-default-50 p-3'>
                    <p className='text-xs text-default-400'>
                      {t('Venta leída')}
                    </p>
                    <p className='font-semibold'>
                      {formatMoney(previewTotals.revenue, previewCurrencies[0])}
                    </p>
                  </div>
                  <div className='rounded-md bg-default-50 p-3'>
                    <p className='text-xs text-default-400'>
                      {t('Repacking leído')}
                    </p>
                    <p className='font-semibold'>
                      {formatMoney(
                        previewTotals.repacking,
                        previewCurrencies[0],
                        2
                      )}
                    </p>
                  </div>
                  <div className='rounded-md bg-default-50 p-3'>
                    <p className='text-xs text-default-400'>
                      {t('Flete leído USD')}
                    </p>
                    <p className='font-semibold'>
                      USD {formatNumber(previewTotals.freight)}
                    </p>
                  </div>
                </div>

                <div className='grid grid-cols-1 gap-3 text-sm md:grid-cols-2'>
                  <div className='rounded-md border border-default-200 p-3'>
                    <p className='mb-2 text-xs font-semibold uppercase text-default-400'>
                      {t('Referencias leídas')}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {previewReferences.slice(0, 12).map((reference) => (
                        <Chip key={reference} size='sm' variant='flat'>
                          {reference}
                        </Chip>
                      ))}
                      {previewReferences.length === 0 && (
                        <span className='text-default-400'>-</span>
                      )}
                    </div>
                  </div>
                  <div className='rounded-md border border-default-200 p-3'>
                    <p className='mb-2 text-xs font-semibold uppercase text-default-400'>
                      {t('Contenedores leídos')}
                    </p>
                    <div className='flex flex-wrap gap-2'>
                      {previewContainers.slice(0, 12).map((container) => (
                        <Chip key={container} size='sm' variant='flat'>
                          {container}
                        </Chip>
                      ))}
                      {previewContainers.length === 0 && (
                        <span className='text-default-400'>-</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className='rounded-md border border-default-200 p-3 text-sm'>
                  <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                    <p className='text-xs font-semibold uppercase text-default-400'>
                      {t('Match con despachos del cliente')}
                    </p>
                    <div className='flex gap-2'>
                      <Chip size='sm' color='success' variant='flat'>
                        {previewShipmentMatches.matched.length} {t('matches')}
                      </Chip>
                      {previewShipmentMatches.unmatched.length > 0 && (
                        <Chip size='sm' color='warning' variant='flat'>
                          {previewShipmentMatches.unmatched.length}{' '}
                          {t('sin match')}
                        </Chip>
                      )}
                    </div>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {previewShipmentMatches.matched
                      .slice(0, 12)
                      .map((match) => (
                        <Chip
                          key={`${match.reference}-${match.container}`}
                          size='sm'
                          color='success'
                          variant='flat'
                        >
                          {match.label}
                        </Chip>
                      ))}
                    {previewShipmentMatches.unmatched
                      .slice(0, 12)
                      .map((item) => (
                        <Chip
                          key={item}
                          size='sm'
                          color='warning'
                          variant='flat'
                        >
                          {item}
                        </Chip>
                      ))}
                    {previewShipmentMatches.matched.length === 0 &&
                      previewShipmentMatches.unmatched.length === 0 && (
                        <span className='text-default-400'>
                          {t(
                            'No se leyeron referencias o contenedores para comparar'
                          )}
                        </span>
                      )}
                  </div>
                </div>

                <div className='max-h-[360px] overflow-auto rounded-md border border-default-200'>
                  <table className='w-full min-w-[980px] text-sm'>
                    <thead className='sticky top-0 bg-default-100 text-xs uppercase text-default-500'>
                      <tr>
                        <th className='px-3 py-2 text-left'>
                          {t('Referencia')}
                        </th>
                        <th className='px-3 py-2 text-left'>
                          {t('Contenedor')}
                        </th>
                        <th className='px-3 py-2 text-left'>{t('Variedad')}</th>
                        <th className='px-3 py-2 text-left'>{t('Calibre')}</th>
                        <th className='px-3 py-2 text-left'>{t('Color')}</th>
                        <th className='px-3 py-2 text-left'>{t('Etiqueta')}</th>
                        <th className='px-3 py-2 text-right'>{t('Bultos')}</th>
                        <th className='px-3 py-2 text-right'>{t('Kilos')}</th>
                        <th className='px-3 py-2 text-right'>{t('Precio')}</th>
                        <th className='px-3 py-2 text-right'>{t('Venta')}</th>
                        <th className='px-3 py-2 text-right'>Repacking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processedData.slice(0, 80).map((detail, index) => (
                        <tr
                          key={`${detail.referencia}-${detail.variedad}-${detail.calibre_color}-${index}`}
                          className='border-t border-default-100'
                        >
                          <td className='px-3 py-2'>
                            {detail.referencia || '-'}
                          </td>
                          <td className='px-3 py-2'>
                            {detail.nro_contenedor || '-'}
                          </td>
                          <td className='px-3 py-2'>
                            {detail.variedad || '-'}
                          </td>
                          <td className='px-3 py-2'>
                            {detail.calibre_color || '-'}
                          </td>
                          <td className='px-3 py-2'>{detail.color || '-'}</td>
                          <td className='px-3 py-2'>
                            {detail.embalaje_etiqueta || '-'}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {formatNumber(Number(detail.bultos_cargados) || 0)}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {formatNumber(Number(detail.kilos) || 0)}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {formatMoney(
                              Number(detail.precio_por_caja) || 0,
                              detail.moneda
                            )}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {formatMoney(
                              Number(detail.ingreso_moneda_origen) || 0,
                              detail.moneda
                            )}
                          </td>
                          <td className='px-3 py-2 text-right'>
                            {formatMoney(
                              Number(detail.repacking_costs_moneda_origen) || 0,
                              detail.moneda,
                              2
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {processedData.length > 80 && (
                  <p className='text-xs text-default-400'>
                    {t('Mostrando primeras 80 líneas de la lectura.')}
                  </p>
                )}
            </div>
          )}
        </ModalBody>
        {settlementActionErrors.length > 0 && (
          <div
            role='alert'
            aria-live='assertive'
            className='mx-6 max-h-48 overflow-auto rounded-lg border border-danger-300 bg-danger-50 p-4 shadow-lg dark:border-danger-700 dark:bg-danger-100'
          >
            <div className='flex items-start justify-between gap-3'>
              <div>
                <p className='text-sm font-semibold text-danger-700 dark:text-danger-300'>
                  {t('No se puede continuar')}
                </p>
                <ul className='mt-1 list-disc space-y-1 pl-5 text-xs text-danger-700 dark:text-danger-300'>
                  {settlementActionErrors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
              <button
                type='button'
                aria-label={t('Cerrar alertas')}
                className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-danger-600 transition hover:bg-danger-100'
                onClick={() => setSettlementActionErrors([])}
              >
                <Icon icon='tabler:x' />
              </button>
            </div>
          </div>
        )}
        <ModalFooter>
          {activeStep === 'review' && groupedData.length > 0 && (
            <div className='mr-auto flex min-w-[280px] max-w-xl flex-1 items-center gap-3'>
              <div className='min-w-[150px] text-xs font-medium text-default-500'>
                <p>
                  {assignedGroupCount}/{groupedData.length}{' '}
                  {t('grupos asignados')}
                </p>
                <p>
                  {reviewedGroupCount}/{groupedData.length}{' '}
                  {t('grupos revisados')}
                </p>
              </div>
              <div className='h-2 flex-1 overflow-hidden rounded-full bg-default-200'>
                <div
                  className='h-full rounded-full bg-primary transition-all'
                  style={{
                    width: `${
                      groupedData.length
                        ? Math.round(
                            (assignedGroupCount / groupedData.length) * 100
                          )
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
          <Button
            variant='light'
            onPress={handleClose}
            isDisabled={isApplyingSettlements}
          >
            {t('Cancelar')}
          </Button>
          {activeStep !== 'setup' && (
            <Button
              variant='flat'
              onPress={handleBackStep}
              isDisabled={isProcessing}
            >
              {activeStep === 'settlements'
                ? t('Volver a revisión')
                : activeStep === 'review' && selectedFileIsPdf
                  ? t('Volver a grupos')
                  : t('Volver')}
            </Button>
          )}
          {activeStep === 'review' && groupedData.length > 0 && (
            <Button
              color='primary'
              onPress={handleReviewSettlements}
              isDisabled={isProcessing || isApplyingSettlements}
            >
              {t('Revisar')} {derivedSettlementResult.settlements.length}{' '}
              {t('liquidaciones a crear')}
            </Button>
          )}
          {activeStep === 'settlements' && (
            <Button
              color='primary'
              onPress={() => void handleCreateSettlements()}
              isDisabled={isProcessing || isApplyingSettlements}
              isLoading={isApplyingSettlements}
            >
              {t('Crear')} {derivedSettlementResult.settlements.length}{' '}
              {t('liquidaciones')}
            </Button>
          )}
          {(activeStep === 'setup' || activeStep === 'groups') && (
            <Button
              color='primary'
              onPress={handlePrimaryAction}
              isDisabled={isPrimaryActionDisabled}
              isLoading={isProcessing}
            >
              {primaryActionLabel}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MultiShipmentUploadModal;
